import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import io from 'socket.io-client';
import { useDropzone } from 'react-dropzone';
import { useDispatch, useSelector } from 'react-redux';

// MUI Components
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Input from '@mui/material/Input';
import InputLabel from '@mui/material/InputLabel';
import Typography from '@mui/material/Typography';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import DoneIcon from '@mui/icons-material/Done';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';

// Styles
import './styles.scss';

import request from '~/utils/request';
import ConfirmModal from '~/components/General/ConfirmModal';
import { getUserData } from '~/redux/actions/auth-actions';
import { getUserToken } from '~/utils/auth';
import {
  fetchContactLabels,
  fetchContactLabelsWithSearch,
  fetchContacts,
} from '~/redux/actions/contacts-actions';
import { failure, success } from '~/redux/actions/snackbar-actions';
import { DEFAULT_PAGINATION } from '~/utils/constants';
import { debounce } from '~/utils/helperFunctions';
import {
  SaveFilterModal,
  SAVED_FILTER_MODULES,
  createSavedFilter,
} from '~/modules/saved-filters';
import { ConfirmPurchaseCIcon } from '~/assets/images';
import { MESSAGES } from '~/utils/message';

import AddressEntryPanel from './AddressEntryPanel';
import FiltersPanel from './FiltersPanel';
import MapPreviewPanel from './MapPreviewPanel';
import MapTargetPanel from './MapTargetPanel';
import SummaryPanel from './SummaryPanel';
import {
  buildContactAddress,
  buildContactOptionTitle,
  downloadSampleAddressUploadCsv,
  getAddressKey,
  getAddressMatchKey,
  getAutocompleteDisplayText,
  isMatchingStartingAddress,
  getContactDataAxlePlaceIds,
  getDisplayAddress,
  getUniqueAddresses,
  normalizeAddress,
  parseUploadedAddressText,
} from './utils/addresses';
import {
  createEmptyFilterState,
  createEmptyFilterValue,
  getDataAxleFilterByKey,
  getDataAxleFilterDefinitions,
  getActiveFilterItems,
  getActiveFilterLabels,
  getEstimateFiltersPayload,
  getInvalidRangeFilterKeys,
  hasInvalidRangeFilters,
  normalizeFilterMultiValues,
  normalizeFilterOptionValue,
  normalizeFindContactFilters,
} from './utils/filters';

const addressPlaceholder = 'Enter a starting address';
const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
const googleMapsMapId = import.meta.env.VITE_GOOGLE_MAPS_MAP_ID;
const useCleanNavigationMapStyle = true;
const shouldUseGoogleMapsMapId = Boolean(googleMapsMapId);
const googleMapsScriptId = 'google-maps-js-api';
const googleMapsCallbackName = '__olcGoogleMapsLoaded';
const googleMapsAuthFailureEvent = 'olc-google-maps-auth-failure';
const googleMapsAuthErrorMessage =
  'Google Maps rejected the API key. Check VITE_GOOGLE_MAPS_API_KEY, enabled APIs, billing, and localhost referrer restrictions.';
// Google's region collection covers cities, counties, states, and postal codes; validation below rejects countries.
const areaAutocompleteTypes = ['(regions)'];
const browserGeolocationOptions = { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 };
const targetCountOptions = [10, 25, 50, 100];
const nearbyRadiusOptions = [0.25, 0.5, 0.75, 1];
const defaultHomesSearchRadiusMiles = 1;
const defaultTargetCount = '';
const defaultNearbyTargetMode = '';
const minimumTargetCount = 10;
const maximumTargetCount = 10000;
const minimumRadiusMiles = 0.001;
const maximumRadiusMiles = 10;
const maximumFreeformRadiusMiles = 500;
const freeformPointMinDistanceMiles = 0.005;
const freeformSimplificationToleranceMiles = 0.02;
const maxFreeformShapePoints = 500;
const defaultMapCenter = { lat: 37.0902, lng: -95.7129 };
const defaultMapZoom = 5;
const localMapZoom = 12;
const usaMapBounds = {
  east: -65,
  north: 72,
  south: 18,
  west: -179,
};
const usaSearchBounds = {
  east: -66.885444,
  north: 49.384358,
  south: 24.396308,
  west: -124.848974,
};
const addressSearchBiasRadiusMiles = 75;
const estimateRequestTimeoutMs = 60000;
const estimateRequestDebounceMs = 1000;
const customRadiusCommitDebounceMs = 1000;
const bulkEstimateResultChunkSize = 5;
const estimateFailureNotificationCooldownMs = 5000;
const bulkAddressEstimateEventPrefix = 'find-new-contacts-neighborhood-estimates';
const customTargetDebounceMs = 900;
const lookupSearchDebounceMs = 300;
const settingsDraftStorageKey = 'olc:find-new-contacts:draft:v1';
const settingsDraftModeStorageKey = 'olc:find-new-contacts:draft-mode:v1';
const addressSettingsDraftStorageKey = 'olc:find-new-contacts:draft:v2:addresses';
const addressSettingsDraftModeStorageKey = 'olc:find-new-contacts:draft-mode:v2:addresses';
const legacyAddressSettingsDraftStorageKey = 'olc:find-new-contacts:draft:v1:addresses';
const legacyAddressSettingsDraftModeStorageKey = 'olc:find-new-contacts:draft-mode:v1:addresses';
const maxSettingsDraftCharacters = 750000;
const contactTypeKeys = ['consumers', 'businesses'];
const maxSelectedAddresses = 10;
const maxSelectedMapLocations = 10;
const addressVerificationBatchSize = 5;
const maxSelectedItemsPlaceholder =
  'Limit reached. Remove addresses or areas from the list on the right side.';
const maxSelectedAddressEntryLimitMessage =
  `Maximum of ${maxSelectedAddresses} addresses reached. Remove an address before adding another.`;
const maxSelectedAddressImportMessage =
  `Maximum of ${maxSelectedAddresses} addresses reached. Remove an address before adding another.`;
const endUserAgreementUrl = 'https://openlettermarketing.com/end-user-license-agreement';
const createPurchaseIdempotencyKey = () => {
  if (typeof window !== 'undefined' && typeof window.crypto?.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }

  const bytes = Array.from(window.crypto.getRandomValues(new Uint8Array(16)));
  bytes[6] = (bytes[6] % 16) + 64;
  bytes[8] = (bytes[8] % 64) + 128;
  const hex = bytes.map((byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};
const invalidAddressUploadFileMessage = MESSAGES.CONTACTS.IMPORT.UPLOAD_FILE.INVALID_FILE;
const isMaxSelectedAddressMessage = (message) =>
  [maxSelectedItemsPlaceholder, maxSelectedAddressEntryLimitMessage, maxSelectedAddressImportMessage].includes(message);
const normalizeContactType = (contactType) =>
  (contactTypeKeys.includes(contactType) ? contactType : 'consumers');
const defaultListCleanupOptions = {
  excludeTargetAddressesFromMailingList: true,
  removeDuplicateHomesAcrossAreas: true,
};
const manualAreaModes = ['draw-area', 'draw-radius', 'custom'];
const drawAreaStrokeColor = '#ed5c2f';
const boundaryFeatureStyles = {
  fillColor: '#ed5c2f',
  fillOpacity: 0.16,
  strokeColor: '#ed5c2f',
  strokeOpacity: 0.95,
  strokeWeight: 3,
};
const boundaryFeatureTypeByPlaceType = [
  ['country', 'COUNTRY'],
  ['administrative_area_level_1', 'ADMINISTRATIVE_AREA_LEVEL_1'],
  ['postal_code', 'POSTAL_CODE'],
  ['postal_code_prefix', 'POSTAL_CODE'],
  ['administrative_area_level_2', 'ADMINISTRATIVE_AREA_LEVEL_2'],
  ['locality', 'LOCALITY'],
  ['school_district', 'SCHOOL_DISTRICT'],
];
const cleanMapStyles = [
  {
    elementType: 'labels.icon',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'administrative',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#475569' }],
  },
  {
    featureType: 'administrative.land_parcel',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'poi',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'poi.business',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'poi.medical',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'poi.place_of_worship',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'poi.school',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'poi.sports_complex',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'transit',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ color: '#d7e1eb' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'labels.icon',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'road',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#4f5f73' }],
  },
  {
    featureType: 'road',
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#ffffff' }, { weight: 3 }],
  },
  {
    featureType: 'landscape',
    elementType: 'geometry',
    stylers: [{ color: '#f5f7f9' }],
  },
  {
    featureType: 'landscape.natural',
    elementType: 'geometry',
    stylers: [{ color: '#d8f1df' }],
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#b8d9ee' }],
  },
];

let googleMapsLoadPromise;
let googleMapsLibrariesPromise;

const getGoogleMapsErrorMessage = (error) => error?.message || 'Google Maps failed to load.';

const getNeighborhoodEstimateValidationMessage = (error) => {
  const errors = error?.response?.data?.data?.errors;
  if (Array.isArray(errors) && errors.length) {
    const first = errors.find((entry) => entry?.msg) || errors[0];
    if (first?.msg) return first.msg;
  }

  const fallback = error?.response?.data?.message;
  if (fallback && fallback !== 'Validation Errors') return fallback;

  return null;
};

const logNeighborhoodEstimateError = (message, meta = {}) => {
  // Keep this error-only so successful multi-address batches do not flood DevTools.
  console.error('[Find New Contacts] Neighborhood estimate failed', {
    message,
    ...meta,
  });
};

const normalizeLookupOptions = (options = []) => {
  const seenValues = new Set();

  return (Array.isArray(options) ? options : []).reduce((normalizedOptions, option) => {
    const value = String(option?.value || option?.id || '').trim();
    const label = String(option?.label || option?.title || value).trim();
    if (!value || !label || seenValues.has(value)) return normalizedOptions;

    seenValues.add(value);
    const title = label.includes(value) ? label : `${label} (${value})`;
    return [...normalizedOptions, { id: value, title }];
  }, []);
};

const mergeLookupOptionLabels = (currentLabels = {}, key, options = []) => {
  if (!key || !options.length) return currentLabels;

  const nextLabels = { ...(currentLabels[key] || {}) };
  options.forEach((option) => {
    if (option?.id && option?.title) {
      nextLabels[option.id] = option.title;
    }
  });

  return {
    ...currentLabels,
    [key]: nextLabels,
  };
};

const normalizeCoordinates = (value) => {
  const latitude = Number(value?.latitude);
  const longitude = Number(value?.longitude);

  return Number.isFinite(latitude) && Number.isFinite(longitude)
    ? { latitude, longitude }
    : null;
};

const areCoordinatesInsideBounds = (coordinates, bounds) => (
  coordinates.latitude >= bounds.south &&
  coordinates.latitude <= bounds.north &&
  coordinates.longitude >= bounds.west &&
  coordinates.longitude <= bounds.east
);

const getAddressSearchBoundsFromLocation = (location) => {
  if (location?.countryCode && String(location.countryCode).toUpperCase() !== 'US') return null;

  const coordinates = normalizeCoordinates(location);
  if (!coordinates || !areCoordinatesInsideBounds(coordinates, usaSearchBounds)) return null;

  const latitudeRadius = addressSearchBiasRadiusMiles / 69;
  const longitudeRadius = addressSearchBiasRadiusMiles / (
    69 * Math.max(Math.cos((coordinates.latitude * Math.PI) / 180), 0.1)
  );
  const bounds = {
    east: Math.min(usaSearchBounds.east, coordinates.longitude + longitudeRadius),
    north: Math.min(usaSearchBounds.north, coordinates.latitude + latitudeRadius),
    south: Math.max(usaSearchBounds.south, coordinates.latitude - latitudeRadius),
    west: Math.max(usaSearchBounds.west, coordinates.longitude - longitudeRadius),
  };

  return bounds.north > bounds.south && bounds.east > bounds.west ? bounds : null;
};

const getLocalMapCenterFromLocation = (location) => {
  if (location?.countryCode && String(location.countryCode).toUpperCase() !== 'US') return null;

  const coordinates = normalizeCoordinates(location);
  if (!coordinates || !areCoordinatesInsideBounds(coordinates, usaSearchBounds)) return null;

  return { lat: coordinates.latitude, lng: coordinates.longitude };
};

const areMapCentersEqual = (left, right) => (
  Number(left?.lat) === Number(right?.lat) &&
  Number(left?.lng) === Number(right?.lng)
);
const getAddressAutocompleteTypes = (onlyLocations) =>
  onlyLocations ? areaAutocompleteTypes : undefined;

const getAddressAutocompleteOptions = ({ bounds, fields, onlyLocations }) => {
  const types = getAddressAutocompleteTypes(onlyLocations);

  return {
    bounds,
    componentRestrictions: { country: 'us' },
    ...(fields ? { fields } : {}),
    strictBounds: false,
    ...(types ? { types } : {}),
  };
};

const normalizePlaceTypes = (types) =>
  (Array.isArray(types) ? types : [])
    .map((type) => String(type || '').trim().toLowerCase())
    .filter(Boolean);

const getPlaceCountryCode = (addressComponents = []) => {
  const countryComponent = (Array.isArray(addressComponents) ? addressComponents : [])
    .find((component) => normalizePlaceTypes(component?.types).includes('country'));

  return String(countryComponent?.short_name || '').trim().toUpperCase();
};

const getPlaceComponent = (addressComponents = [], type) =>
  (Array.isArray(addressComponents) ? addressComponents : [])
    .find((component) => normalizePlaceTypes(component?.types).includes(type));

const normalizeCountyName = (value = '') =>
  String(value || '').trim().replace(/\s+County$/i, '');

const hasStreetAddressComponent = (addressComponents = []) =>
  Boolean(getPlaceComponent(addressComponents, 'street_number') || getPlaceComponent(addressComponents, 'route'));

const getPlacePostalCode = (addressComponents = []) =>
  String(getPlaceComponent(addressComponents, 'postal_code')?.short_name || '').trim();

const shouldUsePlacePostalCode = ({ addressComponents = [], types = [] } = {}) => {
  const normalizedTypes = normalizePlaceTypes(types);

  return Boolean(
    getPlacePostalCode(addressComponents) &&
      !hasStreetAddressComponent(addressComponents) &&
      (
        normalizedTypes.includes('postal_code') ||
        normalizedTypes.includes('postal_code_prefix') ||
        normalizedTypes.includes('locality') ||
        normalizedTypes.includes('administrative_area_level_2')
      )
  );
};

const getPlaceLocationFilter = ({ addressComponents = [], types = [] } = {}) => {
  const normalizedTypes = normalizePlaceTypes(types);
  const state = String(getPlaceComponent(addressComponents, 'administrative_area_level_1')?.short_name || '')
    .trim()
    .toUpperCase();
  const postalCode = getPlacePostalCode(addressComponents);

  if (
    normalizedTypes.includes('postal_code') ||
    normalizedTypes.includes('postal_code_prefix') ||
    shouldUsePlacePostalCode({ addressComponents, types })
  ) {
    return postalCode ? { postalCode, type: 'postal_code' } : null;
  }

  if (normalizedTypes.includes('locality')) {
    const city = String(getPlaceComponent(addressComponents, 'locality')?.long_name || '').trim();

    return city && state ? { city, state, type: 'city' } : null;
  }

  if (normalizedTypes.includes('administrative_area_level_2')) {
    const county = normalizeCountyName(getPlaceComponent(addressComponents, 'administrative_area_level_2')?.long_name);

    return county && state ? { county, state, type: 'county' } : null;
  }

  if (normalizedTypes.includes('administrative_area_level_1')) {
    return state ? { state, type: 'state' } : null;
  }

  return null;
};

const isRestrictedSearchLevel = (types) => {
  const normalizedTypes = normalizePlaceTypes(types);

  return normalizedTypes.includes('country');
};

const isAllowedLocationSearchLevel = (types, addressComponents = []) => {
  const normalizedTypes = normalizePlaceTypes(types);

  return (
    normalizedTypes.includes('locality') ||
    normalizedTypes.includes('postal_code') ||
    normalizedTypes.includes('postal_code_prefix') ||
    normalizedTypes.includes('administrative_area_level_2') ||
    normalizedTypes.includes('administrative_area_level_1') ||
    shouldUsePlacePostalCode({ addressComponents, types })
  );
};

const getPlaceSearchRestrictionMessage = ({ addressComponents, onlyLocations = false, types } = {}) => {
  const countryCode = getPlaceCountryCode(addressComponents);
  if (countryCode && countryCode !== 'US') {
    return 'Search is limited to United States locations.';
  }

  if (!onlyLocations && normalizePlaceTypes(types).includes('administrative_area_level_1')) {
    return 'Choose a county, city, or ZIP code instead of a whole state.';
  }

  if (isRestrictedSearchLevel(types)) {
    return 'Choose a state, county, city, or ZIP code instead of a whole country.';
  }

  if (onlyLocations && !isAllowedLocationSearchLevel(types, addressComponents)) {
    return 'Choose a state, county, city, or ZIP code in the United States.';
  }

  return '';
};

const getBoundaryFeatureType = (types) => {
  const normalizedTypes = normalizePlaceTypes(types);
  const matchedType = boundaryFeatureTypeByPlaceType.find(([placeType]) => normalizedTypes.includes(placeType));

  return matchedType?.[1] || '';
};

const normalizeAddressBounds = (value) => {
  if (!value) return null;

  const northEast = value?.getNorthEast?.();
  const southWest = value?.getSouthWest?.();
  const north = Number(northEast ? getLatLngValue(northEast, 'lat') : value.north);
  const east = Number(northEast ? getLatLngValue(northEast, 'lng') : value.east);
  const south = Number(southWest ? getLatLngValue(southWest, 'lat') : value.south);
  const west = Number(southWest ? getLatLngValue(southWest, 'lng') : value.west);

  return [north, east, south, west].every(Number.isFinite)
    ? { east, north, south, west }
    : null;
};

const areCoordinatesEqual = (first, second) => {
  const firstCoordinates = normalizeCoordinates(first);
  const secondCoordinates = normalizeCoordinates(second);

  if (!firstCoordinates || !secondCoordinates) return firstCoordinates === secondCoordinates;

  return (
    Number(firstCoordinates.latitude.toFixed(7)) === Number(secondCoordinates.latitude.toFixed(7)) &&
    Number(firstCoordinates.longitude.toFixed(7)) === Number(secondCoordinates.longitude.toFixed(7))
  );
};

const createDefaultAddressConfig = (contactType = 'consumers') => ({
  addressBounds: null,
  addressViewportBounds: null,
  areaMode: 'radius',
  contactType: normalizeContactType(contactType),
  customRadiusMiles: '',
  customShapePoints: [],
  customShapes: [],
  customTargetCount: '',
  coordinates: null,
  dataAxlePlaceIds: [],
  drawRadiusAreas: [],
  drawRadiusPoints: [],
  estimatedFilterKey: '',
  estimatedHomes: null,
  estimateStatus: 'idle',
  estimatedRadiusMiles: null,
  filters: {},
  isDataAxleConfigured: true,
  locationFilter: null,
  manualAreaOrder: [],
  nearbyTargetMode: defaultNearbyTargetMode,
  placeId: '',
  placeTypes: [],
  radiusMiles: '',
  showFilters: false,
  targetCount: defaultTargetCount,
});

const parseNullableNumber = (value) => {
  if (value === '' || value === null || value === undefined) return null;

  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

const normalizeDataAxlePlaceIds = (values) => (
  Array.isArray(values)
    ? [...new Set(values.map((value) => String(value || '').trim()).filter(Boolean))]
    : []
);

const getAddressMetadataLookupKeys = (addressValue = '') => {
  const normalizedAddress = normalizeAddress(addressValue);
  if (!normalizedAddress) return [];

  const withoutCareOfPrefix = normalizedAddress
    .replace(/^(?:c\/o|c o|care of|attn|attention)\b[^0-9]*/i, '')
    .trim();
  const variants = [normalizedAddress, withoutCareOfPrefix].filter(Boolean);

  return [...new Set(variants.flatMap((variant) => [
    getAddressKey(variant),
    getAddressMatchKey(variant),
  ]).filter(Boolean))];
};

const mergeAddressMetadata = (metadata = {}, addressValue = '', dataAxlePlaceIds = []) => {
  const normalizedDataAxlePlaceIds = normalizeDataAxlePlaceIds(dataAxlePlaceIds);
  if (!normalizedDataAxlePlaceIds.length) return metadata;

  return getAddressMetadataLookupKeys(addressValue).reduce((nextMetadata, metadataKey) => ({
    ...nextMetadata,
    [metadataKey]: {
      dataAxlePlaceIds: [
        ...new Set([
          ...(nextMetadata[metadataKey]?.dataAxlePlaceIds || []),
          ...normalizedDataAxlePlaceIds,
        ]),
      ],
    },
  }), metadata);
};

const getDataAxlePlaceIdsForAddress = (metadata = {}, addressValue = '') => (
  [...new Set(getAddressMetadataLookupKeys(addressValue)
    .flatMap((metadataKey) => metadata[metadataKey]?.dataAxlePlaceIds || [])
    .map((value) => String(value || '').trim())
    .filter(Boolean))]
);

const parsePositiveInteger = (value) => {
  if (value === '' || value === null || value === undefined) return null;

  const numberValue = Number(value);
  return Number.isFinite(numberValue) && numberValue > 0 ? Math.floor(numberValue) : null;
};

const normalizeStartingAddressMode = (mode) => (mode === 'multiple' ? 'multiple' : 'single');

const normalizeSettingsDraftScope = (scope) =>
  (scope === 'addresses' || scope === 'locations' ? scope : 'default');

const getSettingsDraftModeStorageKey = (scope = 'default') => {
  const normalizedScope = normalizeSettingsDraftScope(scope);

  if (normalizedScope === 'addresses') return addressSettingsDraftModeStorageKey;

  return normalizedScope === 'default'
    ? settingsDraftModeStorageKey
    : `${settingsDraftModeStorageKey}:${normalizedScope}`;
};

const getSettingsDraftStorageKey = (mode, scope = 'default') => {
  const normalizedScope = normalizeSettingsDraftScope(scope);
  const normalizedMode = normalizeStartingAddressMode(mode);

  if (normalizedScope === 'addresses') return `${addressSettingsDraftStorageKey}:${normalizedMode}`;

  return normalizedScope === 'default'
    ? `${settingsDraftStorageKey}:${normalizedMode}`
    : `${settingsDraftStorageKey}:${normalizedScope}:${normalizedMode}`;
};

const normalizeListCleanupOptions = (options = {}) => ({
  excludeTargetAddressesFromMailingList: options.excludeTargetAddressesFromMailingList !== false,
  removeDuplicateHomesAcrossAreas: options.removeDuplicateHomesAcrossAreas !== false,
});

const isManualAreaModeValue = (mode) => manualAreaModes.includes(mode);

const normalizeStoredRadiusMiles = (value) => {
  const radius = Number(value);

  return Number.isFinite(radius) && radius > 0
    ? Math.min(Math.max(Number(radius.toFixed(3)), minimumRadiusMiles), maximumRadiusMiles)
    : '';
};

const normalizeNearbyModeSettings = (settings = {}) => {
  const radiusSettings = settings?.radius || {};
  const homesSettings = settings?.homes || {};
  const homesTargetCount = Number(homesSettings.targetCount);

  return {
    homes: {
      customTargetCount: homesSettings.customTargetCount || '',
      targetCount: Number.isFinite(homesTargetCount) && homesTargetCount > 0
        ? Math.min(Math.max(Math.floor(homesTargetCount), minimumTargetCount), maximumTargetCount)
        : '',
    },
    radius: {
      customRadiusMiles: radiusSettings.customRadiusMiles === ''
        ? ''
        : normalizeStoredRadiusMiles(radiusSettings.customRadiusMiles),
      radiusMiles: normalizeStoredRadiusMiles(radiusSettings.radiusMiles),
    },
  };
};

const updateNearbyModeSettings = (settings = {}, config = {}) => {
  const normalizedSettings = normalizeNearbyModeSettings(settings);

  if (config.nearbyTargetMode === 'radius') {
    return {
      ...normalizedSettings,
      radius: {
        customRadiusMiles: config.customRadiusMiles === ''
          ? ''
          : normalizeStoredRadiusMiles(config.customRadiusMiles),
        radiusMiles: normalizeStoredRadiusMiles(config.radiusMiles),
      },
    };
  }

  if (config.nearbyTargetMode === 'homes') {
    const target = Number(config.targetCount);

    return {
      ...normalizedSettings,
      homes: {
        customTargetCount: config.customTargetCount || '',
        targetCount: Number.isFinite(target) && target > 0
          ? Math.min(Math.max(Math.floor(target), minimumTargetCount), maximumTargetCount)
          : '',
      },
    };
  }

  return normalizedSettings;
};

const sanitizeAddressConfig = (config = {}, contactType = config.contactType) => {
  const normalizedContactType = normalizeContactType(contactType);
  const radius = Number(config.radiusMiles);
  const customRadius = config.customRadiusMiles === '' ? '' : Number(config.customRadiusMiles);
  const target = Number(config.targetCount);
  const estimatedRadius = parseNullableNumber(config.estimatedRadiusMiles);
  const estimated = parseNullableNumber(config.estimatedHomes);
  const estimateStatus = ['idle', 'loading', 'ready', 'error'].includes(config.estimateStatus)
    ? config.estimateStatus
    : estimated !== null
      ? 'ready'
      : 'idle';
  const coordinates = normalizeCoordinates(config.coordinates);
  const addressBounds = normalizeAddressBounds(config.addressBounds);
  const addressViewportBounds = normalizeAddressBounds(config.addressViewportBounds);
  const estimatedFilterKey = typeof config.estimatedFilterKey === 'string' ? config.estimatedFilterKey : '';
  const placeTypes = normalizePlaceTypes(config.placeTypes);
  const areaMode = ['radius', ...manualAreaModes].includes(config.areaMode)
    ? config.areaMode
    : 'radius';
  const requestedNearbyTargetMode = ['homes', 'radius'].includes(config.nearbyTargetMode)
    ? config.nearbyTargetMode
    : defaultNearbyTargetMode;
  const hasValidRadiusSelection = Number.isFinite(radius) && radius > 0;
  const sanitizedNearbyTargetMode = requestedNearbyTargetMode === 'radius' && !hasValidRadiusSelection
    ? defaultNearbyTargetMode
    : requestedNearbyTargetMode;
  const canStoreNearbyRadius =
    sanitizedNearbyTargetMode === 'radius' &&
    Number.isFinite(radius) &&
    radius > 0;

  return {
    ...createDefaultAddressConfig(normalizedContactType),
    addressBounds,
    addressViewportBounds,
    areaMode,
    customRadiusMiles: !canStoreNearbyRadius || config.customRadiusMiles === ''
      ? ''
      : Number.isFinite(customRadius) && customRadius > 0
        ? customRadius
        : '',
    customShapePoints: Array.isArray(config.customShapePoints) ? config.customShapePoints : [],
    customShapes: Array.isArray(config.customShapes) ? config.customShapes : [],
    customTargetCount: config.customTargetCount || '',
    coordinates,
    dataAxlePlaceIds: normalizeDataAxlePlaceIds(config.dataAxlePlaceIds),
    drawRadiusAreas: Array.isArray(config.drawRadiusAreas) ? config.drawRadiusAreas : [],
    drawRadiusPoints: Array.isArray(config.drawRadiusPoints) ? config.drawRadiusPoints : [],
    estimatedFilterKey,
    estimatedHomes: estimated,
    estimateStatus,
    estimatedRadiusMiles: estimatedRadius,
    filters: normalizeFindContactFilters(config.filters, normalizedContactType),
    isDataAxleConfigured: config.isDataAxleConfigured !== false,
    locationFilter: config.locationFilter && typeof config.locationFilter === 'object'
      ? config.locationFilter
      : null,
    manualAreaOrder: normalizeManualAreaOrder(config.manualAreaOrder),
    nearbyTargetMode: sanitizedNearbyTargetMode,
    nearbyModeSettings: normalizeNearbyModeSettings(config.nearbyModeSettings),
    placeId: typeof config.placeId === 'string' ? config.placeId : '',
    placeTypes,
    radiusMiles: canStoreNearbyRadius
      ? radius
      : '',
    showFilters: Boolean(config.showFilters),
    targetCount: sanitizedNearbyTargetMode === 'homes' && Number.isFinite(target) && target > 0
      ? Math.min(Math.max(Math.floor(target), minimumTargetCount), maximumTargetCount)
      : defaultTargetCount,
  };
};

const getVisibleAddressConfig = (config = {}, startingAddressMode = 'single', options = {}) => {
  const sanitizedConfig = sanitizeAddressConfig(config, options.contactType);

  if (normalizeStartingAddressMode(startingAddressMode) !== 'multiple') return sanitizedConfig;

  const multipleConfig = {
    ...sanitizedConfig,
    nearbyTargetMode: ['homes', 'radius'].includes(sanitizedConfig.nearbyTargetMode)
      ? sanitizedConfig.nearbyTargetMode
      : defaultNearbyTargetMode,
  };

  if (options.preserveManualAreas) return multipleConfig;

  return {
    ...multipleConfig,
    areaMode: 'radius',
    customShapePoints: [],
    customShapes: [],
    drawRadiusAreas: [],
    drawRadiusPoints: [],
  };
};

const getIdleAddressTargetingConfig = (config = {}) => ({
  ...sanitizeAddressConfig(config),
  areaMode: 'radius',
  customRadiusMiles: '',
  customTargetCount: '',
  estimatedFilterKey: '',
  estimatedHomes: null,
  estimateStatus: 'idle',
  estimatedRadiusMiles: null,
  isDataAxleConfigured: true,
  nearbyTargetMode: defaultNearbyTargetMode,
  radiusMiles: '',
  targetCount: '',
});

const clearAddressEstimateState = (config = {}) => ({
  ...sanitizeAddressConfig(config),
  estimatedFilterKey: '',
  estimatedHomes: null,
  estimateStatus: 'idle',
  estimatedRadiusMiles: null,
  isDataAxleConfigured: true,
});

const sharedMultipleAddressConfigKeys = [
  'areaMode',
  'customTargetCount',
  'filters',
  'manualAreaOrder',
  'nearbyModeSettings',
  'nearbyTargetMode',
  'targetCount',
];

const sharedMultipleAddressRadiusConfigKeys = [
  'customRadiusMiles',
  'radiusMiles',
];

const sharedMultipleAddressManualAreaConfigKeys = [
  'customShapePoints',
  'customShapes',
  'drawRadiusAreas',
  'drawRadiusPoints',
];

const getSharedMultipleAddressConfig = (config = {}, options = {}) => {
  const sanitizedConfig = getVisibleAddressConfig(config, 'multiple', options);
  const sharedKeys = sanitizedConfig.nearbyTargetMode === 'radius'
    ? [...sharedMultipleAddressConfigKeys, ...sharedMultipleAddressRadiusConfigKeys]
    : sharedMultipleAddressConfigKeys;
  const nextSharedKeys = options.preserveManualAreas
    ? [...sharedKeys, ...sharedMultipleAddressManualAreaConfigKeys]
    : sharedKeys;

  return nextSharedKeys.reduce((sharedConfig, key) => ({
    ...sharedConfig,
    [key]: sanitizedConfig[key],
  }), {});
};

const getSharedMultipleAddressConfigKey = (config = {}, options = {}) =>
  JSON.stringify(getSharedMultipleAddressConfig(config, options));

const canReuseRadiusEstimate = (config = {}, sharedConfig = {}) => {
  const configRadius = Number(config.estimatedRadiusMiles);
  const sharedRadius = Number(sharedConfig.radiusMiles);

  return (
    config.nearbyTargetMode === 'radius' &&
    sharedConfig.nearbyTargetMode === 'radius' &&
    config.estimatedHomes !== null &&
    Number.isFinite(configRadius) &&
    Number.isFinite(sharedRadius) &&
    configRadius === normalizeRadiusMiles(sharedRadius) &&
    JSON.stringify(config.filters || {}) === JSON.stringify(sharedConfig.filters || {})
  );
};

const applySharedMultipleAddressConfig = (config = {}, sharedConfig = {}, options = {}) => {
  const sanitizedConfig = sanitizeAddressConfig(config, options.contactType || sharedConfig.contactType);
  const sanitizedSharedConfig = getVisibleAddressConfig(sharedConfig, 'multiple', options);
  const sharedMultipleAddressConfig = getSharedMultipleAddressConfig(sanitizedSharedConfig, options);
  const shouldPreserveEstimate = options.preserveEstimate === true ||
    canReuseRadiusEstimate(sanitizedConfig, sanitizedSharedConfig) ||
    getSharedMultipleAddressConfigKey(sanitizedConfig, options) ===
      getSharedMultipleAddressConfigKey(sanitizedSharedConfig, options);

  return {
    ...sanitizedConfig,
    ...sharedMultipleAddressConfig,
    customRadiusMiles: sanitizedSharedConfig.nearbyTargetMode === 'homes' && !shouldPreserveEstimate
      ? ''
      : sharedMultipleAddressConfig.customRadiusMiles ?? sanitizedConfig.customRadiusMiles,
    estimatedHomes: shouldPreserveEstimate ? sanitizedConfig.estimatedHomes : null,
    estimateStatus: shouldPreserveEstimate ? sanitizedConfig.estimateStatus : 'idle',
    estimatedRadiusMiles: shouldPreserveEstimate ? sanitizedConfig.estimatedRadiusMiles : null,
    isDataAxleConfigured: shouldPreserveEstimate ? sanitizedConfig.isDataAxleConfigured : true,
    radiusMiles: sanitizedSharedConfig.nearbyTargetMode === 'homes' && !shouldPreserveEstimate
      ? ''
      : sharedMultipleAddressConfig.radiusMiles ?? sanitizedConfig.radiusMiles,
  };
};

const syncMultipleAddressConfigs = (
  startingAddresses = [],
  addressConfigs = {},
  activeAddressKey = '',
  activeAddressConfig = {},
  options = {}
) => {
  const nextAddressConfigs = {};

  startingAddresses.forEach((startingAddress) => {
    const addressKey = getAddressKey(startingAddress);
    if (!addressKey) return;

    const addressConfig = addressKey === activeAddressKey
      ? activeAddressConfig
      : addressConfigs[addressKey];

    nextAddressConfigs[addressKey] = applySharedMultipleAddressConfig(
      addressConfig,
      activeAddressConfig,
      {
        ...options,
        preserveEstimate: options.preserveAllEstimates === true || addressKey === activeAddressKey,
      }
    );
  });

  return nextAddressConfigs;
};

const areAddressConfigsEqual = (first = {}, second = {}) =>
  JSON.stringify(first) === JSON.stringify(second);

const getDraftAddressConfigs = (startingAddresses = [], addressConfigs = {}, contactType = 'consumers') => {
  const nextAddressConfigs = {};

  startingAddresses.forEach((startingAddress) => {
    const addressKey = getAddressKey(startingAddress);
    if (!addressKey || !addressConfigs[addressKey]) return;

    nextAddressConfigs[addressKey] = sanitizeAddressConfig(addressConfigs[addressKey], contactType);
  });

  return nextAddressConfigs;
};

const normalizeFiltersByContactType = (
  filtersByContactType = {},
  fallbackFilters = {},
  fallbackContactType = 'consumers'
) => {
  const nextFiltersByContactType = {};
  const normalizedFallbackContactType = normalizeContactType(fallbackContactType);

  contactTypeKeys.forEach((contactType) => {
    const filters = filtersByContactType?.[contactType];

    if (filters && typeof filters === 'object') {
      nextFiltersByContactType[contactType] = normalizeFindContactFilters(filters, contactType);
    }
  });

  if (!nextFiltersByContactType[normalizedFallbackContactType]) {
    nextFiltersByContactType[normalizedFallbackContactType] = normalizeFindContactFilters(
      fallbackFilters,
      normalizedFallbackContactType
    );
  }

  return nextFiltersByContactType;
};

const sanitizeSettingsDraft = (draft, mode) => {
  if (!draft || typeof draft !== 'object') return null;

  const startingAddresses = Array.isArray(draft.startingAddresses)
    ? draft.startingAddresses.map(normalizeAddress).filter(Boolean)
    : [];
  const limitedStartingAddresses = startingAddresses.slice(0, maxSelectedAddresses);
  const contactType = normalizeContactType(draft.contactType);
  const addressConfigs = getDraftAddressConfigs(limitedStartingAddresses, draft.addressConfigs || {}, contactType);
  const selectedAddressIndex = Number(draft.selectedAddressIndex);
  const normalizedSelectedAddressIndex = Number.isFinite(selectedAddressIndex)
    ? Math.min(Math.max(selectedAddressIndex, 0), Math.max(limitedStartingAddresses.length - 1, 0))
    : 0;
  const selectedAddressKey = getAddressKey(limitedStartingAddresses[normalizedSelectedAddressIndex]);
  const fallbackFilters = addressConfigs[selectedAddressKey]?.filters || draft.filters || {};

  return {
    addressConfigs,
    areaMode: ['radius', ...manualAreaModes].includes(draft.areaMode) ? draft.areaMode : undefined,
    contactType,
    customRadiusMiles: draft.customRadiusMiles ?? '',
    customShapes: Array.isArray(draft.customShapes) ? draft.customShapes : [],
    drawRadiusAreas: Array.isArray(draft.drawRadiusAreas) ? draft.drawRadiusAreas : [],
    filtersByContactType: normalizeFiltersByContactType(
      draft.filtersByContactType,
      fallbackFilters,
      contactType
    ),
    listCleanupOptions: normalizeListCleanupOptions(draft.listCleanupOptions),
    manualAreaOrder: normalizeManualAreaOrder(draft.manualAreaOrder),
    selectedAddressIndex: normalizedSelectedAddressIndex,
    startingAddressMode: normalizeStartingAddressMode(mode || draft.startingAddressMode),
    startingAddresses: limitedStartingAddresses,
  };
};

const readLastStartingAddressMode = (scope = 'default') => {
  if (typeof window === 'undefined') return null;

  try {
    const normalizedScope = normalizeSettingsDraftScope(scope);
    const savedMode = window.localStorage.getItem(getSettingsDraftModeStorageKey(normalizedScope));
    if (savedMode === 'multiple' || savedMode === 'single') return savedMode;
    if (normalizedScope !== 'default') return 'single';

    const legacyDraft = window.localStorage.getItem(settingsDraftStorageKey);
    if (!legacyDraft) return 'single';

    return normalizeStartingAddressMode(JSON.parse(legacyDraft)?.startingAddressMode);
  } catch {
    return 'single';
  }
};

const readSettingsDraft = (mode = 'single', scope = 'default') => {
  if (typeof window === 'undefined') return null;

  const normalizedMode = normalizeStartingAddressMode(mode);
  const normalizedScope = normalizeSettingsDraftScope(scope);

  try {
    const draftStorageKey = getSettingsDraftStorageKey(normalizedMode, normalizedScope);
    const savedDraft = window.localStorage.getItem(draftStorageKey);
    if (savedDraft) {
      if (savedDraft.length > maxSettingsDraftCharacters) {
        window.localStorage.removeItem(draftStorageKey);
        return null;
      }

      return sanitizeSettingsDraft(JSON.parse(savedDraft), normalizedMode);
    }

    if (normalizedScope !== 'default') return null;

    const legacyDraft = window.localStorage.getItem(settingsDraftStorageKey);
    if (!legacyDraft) return null;
    if (legacyDraft.length > maxSettingsDraftCharacters) return null;

    const parsedLegacyDraft = JSON.parse(legacyDraft);
    if (normalizeStartingAddressMode(parsedLegacyDraft.startingAddressMode) !== normalizedMode) return null;

    return sanitizeSettingsDraft(parsedLegacyDraft, normalizedMode);
  } catch {
    return null;
  }
};

const writeSettingsDraft = (draft, scope = 'default') => {
  if (typeof window === 'undefined') return;

  try {
    const draftMode = normalizeStartingAddressMode(draft.startingAddressMode);
    const normalizedScope = normalizeSettingsDraftScope(scope);
    const contactType = normalizeContactType(draft.contactType);
    const filtersByContactType = normalizeFiltersByContactType(
      draft.filtersByContactType,
      draft.filters,
      contactType
    );

    const hasDrawnAreas = Boolean(
      (Array.isArray(draft.customShapes) && draft.customShapes.length) ||
      (Array.isArray(draft.drawRadiusAreas) && draft.drawRadiusAreas.length) ||
      (Array.isArray(draft.manualAreaOrder) && draft.manualAreaOrder.length)
    );

    if (!draft.startingAddresses?.length && !hasDrawnAreas) {
      window.localStorage.removeItem(getSettingsDraftStorageKey(draftMode, normalizedScope));
      if (
        !window.localStorage.getItem(getSettingsDraftStorageKey('single', normalizedScope)) &&
        !window.localStorage.getItem(getSettingsDraftStorageKey('multiple', normalizedScope))
      ) {
        window.localStorage.removeItem(getSettingsDraftModeStorageKey(normalizedScope));
      }
      return;
    }

    window.localStorage.setItem(getSettingsDraftModeStorageKey(normalizedScope), draftMode);
    window.localStorage.setItem(getSettingsDraftStorageKey(draftMode, normalizedScope), JSON.stringify({
      ...draft,
      addressConfigs: getDraftAddressConfigs(draft.startingAddresses, draft.addressConfigs || {}, contactType),
      contactType,
      customRadiusMiles: draft.customRadiusMiles ?? '',
      customShapes: Array.isArray(draft.customShapes) ? draft.customShapes : [],
      drawRadiusAreas: Array.isArray(draft.drawRadiusAreas) ? draft.drawRadiusAreas : [],
      filtersByContactType,
      listCleanupOptions: normalizeListCleanupOptions(draft.listCleanupOptions),
      manualAreaOrder: normalizeManualAreaOrder(draft.manualAreaOrder),
      startingAddressMode: draftMode,
      updatedAt: Date.now(),
    }));
  } catch {}
};

const hasStoredFindNewContactsData = (scope = 'default') => {
  if (typeof window === 'undefined') return false;

  try {
    const normalizedScope = normalizeSettingsDraftScope(scope);
    const draftKeys = [
      getSettingsDraftModeStorageKey(normalizedScope),
      getSettingsDraftStorageKey('single', normalizedScope),
      getSettingsDraftStorageKey('multiple', normalizedScope),
    ];

    if (normalizedScope === 'default') {
      draftKeys.push(settingsDraftStorageKey, settingsDraftModeStorageKey);
    }

    return Object.keys(window.localStorage).some((key) => draftKeys.includes(key));
  } catch {
    return false;
  }
};

const clearStoredFindNewContactsData = (scope = 'default') => {
  if (typeof window === 'undefined') return;

  try {
    const normalizedScope = normalizeSettingsDraftScope(scope);

    window.localStorage.removeItem(getSettingsDraftStorageKey('single', normalizedScope));
    window.localStorage.removeItem(getSettingsDraftStorageKey('multiple', normalizedScope));
    window.localStorage.removeItem(getSettingsDraftModeStorageKey(normalizedScope));
    if (normalizedScope === 'addresses') {
      window.localStorage.removeItem(`${legacyAddressSettingsDraftStorageKey}:single`);
      window.localStorage.removeItem(`${legacyAddressSettingsDraftStorageKey}:multiple`);
      window.localStorage.removeItem(legacyAddressSettingsDraftModeStorageKey);
    }
    if (normalizedScope === 'default') {
      window.localStorage.removeItem(settingsDraftStorageKey);
      window.localStorage.removeItem(settingsDraftModeStorageKey);
    }
  } catch {}
};

const normalizeRadiusMiles = (radius) => {
  const numericRadius = Number(radius);
  const safeRadius = Number.isFinite(numericRadius)
    ? Math.min(Math.max(numericRadius, minimumRadiusMiles), maximumRadiusMiles)
    : 0.25;

  return Number(safeRadius.toFixed(3));
};

const withTimeout = (promise, timeoutMs, message) =>
  Promise.race([
    promise,
    new Promise((_, reject) => {
      window.setTimeout(() => reject(new Error(message)), timeoutMs);
    }),
  ]);

const isCanceledRequest = (error) =>
  error?.code === 'ERR_CANCELED' ||
  error?.name === 'CanceledError' ||
  error?.name === 'AbortError';

const waitForSocketConnection = (socket, timeoutMs = 5000) =>
  new Promise((resolve, reject) => {
    if (!socket) {
      reject(new Error('Socket connection is not available.'));
      return;
    }

    if (socket.connected) {
      resolve();
      return;
    }

    let timeoutId = null;

    const cleanup = () => {
      if (timeoutId) window.clearTimeout(timeoutId);
      socket.off('connect', handleConnect);
      socket.off('connect_error', handleError);
    };
    const handleConnect = () => {
      cleanup();
      resolve();
    };
    const handleError = (error) => {
      cleanup();
      reject(error || new Error('Socket connection failed.'));
    };

    timeoutId = window.setTimeout(() => {
      cleanup();
      reject(new Error('Socket connection timed out.'));
    }, timeoutMs);

    socket.once('connect', handleConnect);
    socket.once('connect_error', handleError);
    socket.connect();
  });

let findNewContactsSocketInstance = null;
let findNewContactsSocketDisconnectTimer = null;

const getFindNewContactsSocket = () => {
  if (findNewContactsSocketDisconnectTimer) {
    window.clearTimeout(findNewContactsSocketDisconnectTimer);
    findNewContactsSocketDisconnectTimer = null;
  }

  if (!findNewContactsSocketInstance) {
    findNewContactsSocketInstance = io(import.meta.env.VITE_APP_BASE_URL, {
      transports: ['websocket'],
      autoConnect: false,
      query: {
        token: getUserToken(),
      },
    });
  }

  return findNewContactsSocketInstance;
};

const releaseFindNewContactsSocket = (socket) => {
  if (!socket || socket !== findNewContactsSocketInstance) return;
  if (findNewContactsSocketDisconnectTimer) window.clearTimeout(findNewContactsSocketDisconnectTimer);

  findNewContactsSocketDisconnectTimer = window.setTimeout(() => {
    if (findNewContactsSocketInstance !== socket) return;
    socket.removeAllListeners();
    socket.disconnect();
    findNewContactsSocketInstance = null;
    findNewContactsSocketDisconnectTimer = null;
  }, 1000);
};

const formatNumber = (value) => Number(value || 0).toLocaleString('en-US');
const formatCurrency = (value) =>
  Number(value || 0).toLocaleString('en-US', {
    currency: 'USD',
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    style: 'currency',
  });
const contactTypeCopyByType = {
  consumers: {
    key: 'consumers',
    singular: 'home',
    plural: 'homes',
    titlePlural: 'Homes',
    uniqueTitle: 'Unique Homes',
    numberTitle: 'Number of Homes',
  },
  businesses: {
    key: 'businesses',
    singular: 'business',
    plural: 'businesses',
    titlePlural: 'Businesses',
    uniqueTitle: 'Unique Businesses',
    numberTitle: 'Number of Businesses',
  },
};
const getDefaultPurchaseListName = (findNewMode = 'locations') => {
  const now = new Date();
  const date = now.toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  const time = now.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
  const listType = findNewMode === 'addresses' ? 'Radius List' : 'Area List';

  return `${listType} - ${date} ${time}`;
};
const getSummaryStatValueFontSize = (...values) => {
  const maxLength = values.reduce(
    (length, value) => Math.max(length, String(value || '').length),
    0
  );

  if (maxLength > 18) return 16;
  if (maxLength > 14) return 18;
  if (maxLength > 12) return 21;
  if (maxLength > 8) return 24;
  return 28;
};

const getRoundedCoordinate = (value) => Number(value).toFixed(6);

const stableStringify = (value) => {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  if (!value || typeof value !== 'object') return JSON.stringify(value);

  return `{${Object.keys(value)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
    .join(',')}}`;
};

const getDrawRadiusAreaEstimateKey = (area, index) => {
  const latitude = Number(area?.center?.latitude);
  const longitude = Number(area?.center?.longitude);
  const radiusMiles = Number(area?.radiusMiles);

  if (![latitude, longitude, radiusMiles].every(Number.isFinite)) return `circle:${index}`;

  return [
    'circle',
    getRoundedCoordinate(latitude),
    getRoundedCoordinate(longitude),
    Number(radiusMiles).toFixed(3),
  ].join(':');
};

const getDrawRadiusAreaOrderKey = (area) => {
  const latitude = Number(area?.center?.latitude);
  const longitude = Number(area?.center?.longitude);
  const radiusMiles = Number(area?.radiusMiles);

  if (![latitude, longitude, radiusMiles].every(Number.isFinite)) return '';

  return [
    getRoundedCoordinate(latitude),
    getRoundedCoordinate(longitude),
    Number(radiusMiles).toFixed(3),
  ].join(':');
};

const getCustomShapeEstimateKey = (shape, index) => {
  if (!Array.isArray(shape) || shape.length === 0) return `custom:${index}`;

  const shapeKey = shape
    .map((point) => `${getRoundedCoordinate(point.latitude)},${getRoundedCoordinate(point.longitude)}`)
    .join('|');

  return `custom:${shapeKey}`;
};

const getCustomShapeOrderKey = (shape) => {
  if (!Array.isArray(shape) || shape.length === 0) return '';

  return shape
    .map((point) => `${getRoundedCoordinate(point.latitude)},${getRoundedCoordinate(point.longitude)}`)
    .join('|');
};

const getManualAreaOrderEntryKey = (sourceType, orderKey) => `${sourceType}:${orderKey}`;

const getLocationEstimateAreaKey = (addressKey, locationFilter = null) => [
  'location',
  addressKey,
  JSON.stringify(locationFilter || {}),
].join(':');

const getManualAreaRequestArea = (area = {}) => {
  if (area.sourceType === 'location' && area.locationFilter) {
    return {
      locationFilter: area.locationFilter,
      type: 'location',
    };
  }

  if (area.sourceType === 'custom' && Array.isArray(area.points)) {
    return {
      points: area.points,
      type: 'polygon',
    };
  }

  return {
    latitude: area.latitude,
    longitude: area.longitude,
    radiusMiles: area.radiusMiles,
    type: 'radius',
  };
};

const normalizeManualAreaOrder = (entries = []) => (
  Array.isArray(entries)
    ? entries
        .map((entry) => ({
          orderKey: typeof entry?.orderKey === 'string' ? entry.orderKey : '',
          sourceType: ['location', 'circle', 'custom'].includes(entry?.sourceType) ? entry.sourceType : '',
        }))
        .filter((entry) => entry.orderKey && entry.sourceType)
    : []
);

const areManualAreaOrdersEqual = (firstEntries = [], secondEntries = []) => {
  const firstOrder = normalizeManualAreaOrder(firstEntries);
  const secondOrder = normalizeManualAreaOrder(secondEntries);
  if (firstOrder.length !== secondOrder.length) return false;

  return firstOrder.every((entry, index) =>
    entry.sourceType === secondOrder[index].sourceType &&
    entry.orderKey === secondOrder[index].orderKey
  );
};

const appendManualAreaOrderEntry = (entries, sourceType, orderKey) => {
  if (!sourceType || !orderKey) return normalizeManualAreaOrder(entries);

  const nextEntryKey = getManualAreaOrderEntryKey(sourceType, orderKey);
  const normalizedEntries = normalizeManualAreaOrder(entries);

  if (normalizedEntries.some((entry) =>
    getManualAreaOrderEntryKey(entry.sourceType, entry.orderKey) === nextEntryKey
  )) {
    return normalizedEntries;
  }

  return [...normalizedEntries, { sourceType, orderKey }];
};

const removeManualAreaOrderEntry = (entries, sourceType, orderKey) => {
  if (!sourceType || !orderKey) return normalizeManualAreaOrder(entries);

  const entryKeyToRemove = getManualAreaOrderEntryKey(sourceType, orderKey);

  return normalizeManualAreaOrder(entries).filter((entry) =>
    getManualAreaOrderEntryKey(entry.sourceType, entry.orderKey) !== entryKeyToRemove
  );
};

const getManualAreaOrderEntriesFromAreas = (areas = []) => areas
  .map((area) => ({
    orderKey: area?.orderKey || '',
    sourceType: area?.sourceType || '',
  }))
  .filter((entry) => entry.orderKey && entry.sourceType);

const repairManualAreaOrder = (orderEntries = [], areas = []) => {
  const areaEntries = getManualAreaOrderEntriesFromAreas(areas);
  const areaEntryKeys = new Set(areaEntries.map((entry) =>
    getManualAreaOrderEntryKey(entry.sourceType, entry.orderKey)
  ));
  const retainedEntryKeys = new Set();
  const retainedEntries = normalizeManualAreaOrder(orderEntries).filter((entry) => {
    const entryKey = getManualAreaOrderEntryKey(entry.sourceType, entry.orderKey);
    if (!areaEntryKeys.has(entryKey) || retainedEntryKeys.has(entryKey)) return false;

    retainedEntryKeys.add(entryKey);
    return true;
  });
  const missingEntries = areaEntries.filter((entry) =>
    !retainedEntryKeys.has(getManualAreaOrderEntryKey(entry.sourceType, entry.orderKey))
  );

  return [...retainedEntries, ...missingEntries];
};

const orderManualEstimateAreas = (areas = [], orderEntries = []) => {
  const orderIndexByKey = repairManualAreaOrder(orderEntries, areas)
    .reduce((orderedKeys, entry, index) => ({
      ...orderedKeys,
      [getManualAreaOrderEntryKey(entry.sourceType, entry.orderKey)]: index,
    }), {});
  const fallbackOrderStart = Object.keys(orderIndexByKey).length;

  return areas
    .map((area, index) => {
      const orderKey = getManualAreaOrderEntryKey(area.sourceType, area.orderKey);
      const orderIndex = Object.prototype.hasOwnProperty.call(orderIndexByKey, orderKey)
        ? orderIndexByKey[orderKey]
        : fallbackOrderStart + index;

      return { area, index, orderIndex };
    })
    .sort((first, second) => first.orderIndex - second.orderIndex || first.index - second.index)
    .map(({ area }) => area);
};

const getManualAreaEstimateLabel = (estimate) => {
  if (!estimate || estimate.status === 'loading') return '...';

  const estimatedHomes = parseNullableNumber(estimate.estimatedHomes);
  if (estimatedHomes === null) return '-';

  return formatNumber(estimatedHomes);
};

const getRecordsSummaryLabel = (count) =>
  `${typeof count === 'string' ? count : formatNumber(count)} records`;

const createAreaDeleteMarkerContent = ({
  label,
  numberLabel,
  onClick,
  status = 'idle',
  title,
  transform = '',
}) => {
  const marker = document.createElement('span');
  const button = document.createElement('button');
  const count = document.createElement('span');
  const number = document.createElement('span');
  const isLoading = status === 'loading';

  marker.style.alignItems = 'center';
  marker.style.background = isLoading ? '#fff8f3' : '#ffffff';
  marker.style.border = `1px solid ${isLoading ? 'rgba(237, 92, 47, 0.34)' : 'rgba(237, 92, 47, 0.72)'}`;
  marker.style.borderRadius = '999px';
  marker.style.boxShadow = '0 6px 18px rgba(0, 0, 0, 0.18)';
  marker.style.display = 'inline-flex';
  marker.style.overflow = 'hidden';
  if (transform) marker.style.transform = transform;
  marker.style.whiteSpace = 'nowrap';

  number.textContent = numberLabel;
  number.style.alignItems = 'center';
  number.style.alignSelf = 'stretch';
  number.style.background = '#ed5c2f';
  number.style.border = '2px solid #ffffff';
  number.style.borderRadius = '999px';
  number.style.color = '#ffffff';
  number.style.display = 'inline-flex';
  number.style.fontFamily = 'Inter, sans-serif';
  number.style.fontSize = '12px';
  number.style.fontWeight = '800';
  number.style.justifyContent = 'center';
  number.style.lineHeight = '1';
  number.style.margin = '-1px 0 -1px -1px';
  number.style.minWidth = '27px';
  number.style.padding = '0 7px';

  count.textContent = label;
  count.style.color = '#2f2f2f';
  count.style.display = 'block';
  count.style.fontFamily = 'Inter, sans-serif';
  count.style.fontSize = '12px';
  count.style.fontWeight = '700';
  count.style.lineHeight = '16px';
  count.style.padding = '4px 9px 4px 10px';

  button.type = 'button';
  button.textContent = '×';
  button.setAttribute('aria-label', title);
  button.style.alignItems = 'center';
  button.style.background = 'transparent';
  button.style.border = '0';
  button.style.borderLeft = '1px solid rgba(237, 92, 47, 0.28)';
  button.style.borderRadius = '0';
  button.style.color = '#ed5c2f';
  button.style.cursor = 'pointer';
  button.style.display = 'flex';
  button.style.fontFamily = 'Inter, sans-serif';
  button.style.fontSize = '17px';
  button.style.fontWeight = '900';
  button.style.height = '24px';
  button.style.justifyContent = 'center';
  button.style.lineHeight = '18px';
  button.style.padding = '0 0 2px';
  button.style.width = '25px';

  button.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    onClick();
  });

  marker.append(number, count, button);

  return marker;
};

const createMapAreaDeleteMarker = ({
  AdvancedMarkerElement,
  Marker,
  label,
  map,
  maps,
  numberLabel,
  onClick,
  position,
  status,
  title,
  transform,
}) => {
  if (shouldUseGoogleMapsMapId && AdvancedMarkerElement) {
    return new AdvancedMarkerElement({
      content: createAreaDeleteMarkerContent({ label, numberLabel, onClick, status, title, transform }),
      map,
      position,
      title,
      zIndex: 60,
    });
  }

  if (Marker) {
    const marker = new Marker({
      icon: maps?.SymbolPath
        ? {
            path: maps.SymbolPath.CIRCLE,
            scale: 0,
          }
        : undefined,
      label: {
        color: '#ed5c2f',
        fontSize: '12px',
        fontWeight: '900',
        text: `${numberLabel} ${label} ×`,
      },
      map,
      optimized: false,
      position,
      title,
      zIndex: 60,
    });

    marker.addListener?.('click', onClick);
    return marker;
  }

  return null;
};

const getSelectedHomesCount = (config) => {
  const target = Number(config?.targetCount || 0);
  const estimate = parseNullableNumber(config?.estimatedHomes);

  if (isManualAreaModeValue(config?.areaMode)) {
    return estimate !== null && estimate >= 0 ? estimate : 0;
  }

  if (config?.nearbyTargetMode === 'radius') {
    return estimate !== null && estimate >= 0 ? estimate : 0;
  }

  if (config?.nearbyTargetMode !== 'homes') return 0;

  return estimate !== null && estimate >= 0
    ? Math.min(target || estimate, estimate)
    : target;
};

const getDistanceMiles = (origin, point) => {
  const toRadians = (value) => (value * Math.PI) / 180;
  const earthRadiusMiles = 3958.8;
  const lat1 = toRadians(origin.latitude);
  const lat2 = toRadians(point.latitude);
  const deltaLat = toRadians(point.latitude - origin.latitude);
  const deltaLon = toRadians(point.longitude - origin.longitude);
  const a = Math.sin(deltaLat / 2) ** 2
    + Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return earthRadiusMiles * c;
};

const toProjectedShapePoints = (points = []) => {
  const normalizedPoints = points.map(normalizeCoordinates).filter(Boolean);
  if (!normalizedPoints.length) return [];

  const toRadians = (value) => (value * Math.PI) / 180;
  const earthRadiusMiles = 3958.8;
  const averageLatitude = normalizedPoints.reduce((sum, point) => sum + point.latitude, 0) / normalizedPoints.length;

  return normalizedPoints.map((point) => ({
    ...point,
    x: earthRadiusMiles * toRadians(point.longitude) * Math.cos(toRadians(averageLatitude)),
    y: earthRadiusMiles * toRadians(point.latitude),
  }));
};

const getProjectedPointDistanceToSegment = (point, segmentStart, segmentEnd) => {
  const deltaX = segmentEnd.x - segmentStart.x;
  const deltaY = segmentEnd.y - segmentStart.y;

  if (deltaX === 0 && deltaY === 0) {
    return Math.hypot(point.x - segmentStart.x, point.y - segmentStart.y);
  }

  const progress = Math.max(0, Math.min(1, (
    ((point.x - segmentStart.x) * deltaX) + ((point.y - segmentStart.y) * deltaY)
  ) / ((deltaX ** 2) + (deltaY ** 2))));
  const projectedX = segmentStart.x + (progress * deltaX);
  const projectedY = segmentStart.y + (progress * deltaY);

  return Math.hypot(point.x - projectedX, point.y - projectedY);
};

const stripProjectedFields = (points = []) =>
  points.map(({ latitude, longitude }) => ({ latitude, longitude }));

const simplifyProjectedShapePoints = (projectedPoints, toleranceMiles) => {
  if (projectedPoints.length <= 2) return stripProjectedFields(projectedPoints);

  const keepPointIndexes = new Set([0, projectedPoints.length - 1]);
  const ranges = [[0, projectedPoints.length - 1]];

  while (ranges.length) {
    const [startIndex, endIndex] = ranges.pop();
    let farthestIndex = null;
    let farthestDistance = 0;

    for (let index = startIndex + 1; index < endIndex; index += 1) {
      const distance = getProjectedPointDistanceToSegment(
        projectedPoints[index],
        projectedPoints[startIndex],
        projectedPoints[endIndex]
      );

      if (distance > farthestDistance) {
        farthestDistance = distance;
        farthestIndex = index;
      }
    }

    if (farthestIndex !== null && farthestDistance > toleranceMiles) {
      keepPointIndexes.add(farthestIndex);
      ranges.push([startIndex, farthestIndex], [farthestIndex, endIndex]);
    }
  }

  return stripProjectedFields(
    projectedPoints.filter((_, index) => keepPointIndexes.has(index))
  );
};

const getProjectedShapeDiagonalMiles = (projectedPoints) => {
  const bounds = projectedPoints.reduce(
    (result, point) => ({
      maxX: Math.max(result.maxX, point.x),
      maxY: Math.max(result.maxY, point.y),
      minX: Math.min(result.minX, point.x),
      minY: Math.min(result.minY, point.y),
    }),
    {
      maxX: -Infinity,
      maxY: -Infinity,
      minX: Infinity,
      minY: Infinity,
    }
  );

  return Math.hypot(bounds.maxX - bounds.minX, bounds.maxY - bounds.minY);
};

const ensureMinimumShapePoints = (points, sourcePoints) => {
  if (points.length >= 3 || sourcePoints.length < 3) return points;

  return stripProjectedFields([
    sourcePoints[0],
    sourcePoints[Math.floor(sourcePoints.length / 2)],
    sourcePoints[sourcePoints.length - 1],
  ]);
};

const simplifyShapePoints = (points, maxPoints = maxFreeformShapePoints) => {
  const projectedPoints = toProjectedShapePoints(points);
  const baselinePoints = ensureMinimumShapePoints(
    simplifyProjectedShapePoints(projectedPoints, freeformSimplificationToleranceMiles),
    projectedPoints
  );

  if (baselinePoints.length <= maxPoints) return baselinePoints;

  let lowTolerance = freeformSimplificationToleranceMiles;
  let highTolerance = Math.max(getProjectedShapeDiagonalMiles(projectedPoints), minimumRadiusMiles);
  let bestPoints = simplifyProjectedShapePoints(projectedPoints, highTolerance);

  for (let attempt = 0; attempt < 24; attempt += 1) {
    const tolerance = (lowTolerance + highTolerance) / 2;
    const candidatePoints = simplifyProjectedShapePoints(projectedPoints, tolerance);

    if (candidatePoints.length > maxPoints) {
      lowTolerance = tolerance;
    } else {
      bestPoints = candidatePoints;
      highTolerance = tolerance;
    }
  }

  return ensureMinimumShapePoints(bestPoints, projectedPoints);
};

const getShapeEstimateArea = (shape) => {
  if (!Array.isArray(shape) || shape.length < 3) return null;

  const toRadians = (value) => (value * Math.PI) / 180;
  const earthRadiusMiles = 3958.8;
  const averageLatitude = shape.reduce((sum, point) => sum + point.latitude, 0) / shape.length;
  const projectedPoints = shape.map((point) => ({
    x: earthRadiusMiles * toRadians(point.longitude) * Math.cos(toRadians(averageLatitude)),
    y: earthRadiusMiles * toRadians(point.latitude),
  }));
  const areaSqMiles = Math.abs(
    projectedPoints.reduce((sum, point, index) => {
      const nextPoint = projectedPoints[(index + 1) % projectedPoints.length];
      return sum + point.x * nextPoint.y - nextPoint.x * point.y;
    }, 0) / 2
  );
  const center = shape.reduce(
    (sum, point) => ({
      latitude: sum.latitude + point.latitude,
      longitude: sum.longitude + point.longitude,
    }),
    { latitude: 0, longitude: 0 }
  );
  const centroid = {
    latitude: center.latitude / shape.length,
    longitude: center.longitude / shape.length,
  };
  const equivalentRadius = areaSqMiles > 0 ? Math.sqrt(areaSqMiles / Math.PI) : 0;
  const fallbackRadius = Math.max(...shape.map((point) => getDistanceMiles(centroid, point)), minimumRadiusMiles);
  const radius = Math.max(equivalentRadius, fallbackRadius, minimumRadiusMiles);

  return {
    latitude: centroid.latitude,
    longitude: centroid.longitude,
    radiusMiles: Number(radius.toFixed(3)),
  };
};

const getBoundsCenter = (bounds, fallbackCoordinates = null) => {
  const normalizedBounds = normalizeAddressBounds(bounds);
  if (normalizedBounds) {
    return {
      latitude: (normalizedBounds.north + normalizedBounds.south) / 2,
      longitude: (normalizedBounds.east + normalizedBounds.west) / 2,
    };
  }

  return normalizeCoordinates(fallbackCoordinates);
};

const getBoundsEstimateArea = (bounds, fallbackCoordinates = null) => {
  const center = getBoundsCenter(bounds, fallbackCoordinates);
  if (!center) return null;

  const normalizedBounds = normalizeAddressBounds(bounds);
  const cornerDistances = normalizedBounds
    ? [
        { latitude: normalizedBounds.north, longitude: normalizedBounds.east },
        { latitude: normalizedBounds.north, longitude: normalizedBounds.west },
        { latitude: normalizedBounds.south, longitude: normalizedBounds.east },
        { latitude: normalizedBounds.south, longitude: normalizedBounds.west },
      ].map((point) => getDistanceMiles(center, point))
    : [];
  const radiusMiles = Math.max(...cornerDistances, minimumRadiusMiles);

  return {
    latitude: center.latitude,
    longitude: center.longitude,
    radiusMiles: Number(radiusMiles.toFixed(3)),
  };
};

const getLatLngValue = (latLng, key) =>
  (typeof latLng?.[key] === 'function' ? latLng[key]() : latLng?.[key]);

const getGoogleAddressBounds = (geometryBounds, geometryViewport, coordinates) => {
  const googleBounds = normalizeAddressBounds(geometryBounds) || normalizeAddressBounds(geometryViewport);
  if (googleBounds) return googleBounds;

  const latitude = Number(coordinates?.latitude);
  const longitude = Number(coordinates?.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;

  const fallbackOffset = 0.0018;
  return {
    east: longitude + fallbackOffset,
    north: latitude + fallbackOffset,
    south: latitude - fallbackOffset,
    west: longitude - fallbackOffset,
  };
};

const extendBoundsForRadiusArea = (bounds, maps, area) => {
  const latitude = Number(area?.center?.latitude);
  const longitude = Number(area?.center?.longitude);
  const radiusMiles = Number(area?.radiusMiles);

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude) || !Number.isFinite(radiusMiles)) return;

  const latitudeDelta = radiusMiles / 69;
  const longitudeDelta = radiusMiles / (69 * Math.max(Math.cos((latitude * Math.PI) / 180), 0.01));

  bounds.extend(new maps.LatLng(latitude + latitudeDelta, longitude + longitudeDelta));
  bounds.extend(new maps.LatLng(latitude + latitudeDelta, longitude - longitudeDelta));
  bounds.extend(new maps.LatLng(latitude - latitudeDelta, longitude + longitudeDelta));
  bounds.extend(new maps.LatLng(latitude - latitudeDelta, longitude - longitudeDelta));
};

const extendBoundsForShape = (bounds, maps, shape) => {
  if (!Array.isArray(shape)) return;

  shape.forEach((point) => {
    const latitude = Number(point?.latitude);
    const longitude = Number(point?.longitude);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;

    bounds.extend(new maps.LatLng(latitude, longitude));
  });
};

const doGoogleBoundsContainBounds = (outerBounds, innerBounds) => {
  if (!outerBounds || !innerBounds) return false;

  const northEast = innerBounds.getNorthEast?.();
  const southWest = innerBounds.getSouthWest?.();
  if (!northEast || !southWest) return false;

  return outerBounds.contains(northEast) && outerBounds.contains(southWest);
};

const fitGoogleMapToBounds = (map, maps, bounds, padding = 88, { onlyIfNeeded = false } = {}) => {
  if (!map || !bounds || bounds.isEmpty?.()) return;

  const currentBounds = map.getBounds?.();
  if (onlyIfNeeded && currentBounds && doGoogleBoundsContainBounds(currentBounds, bounds)) return;

  maps?.event?.trigger?.(map, 'resize');
  map.fitBounds(bounds, padding);
};

const getGoogleMapOptions = (options, { useMapId = shouldUseGoogleMapsMapId } = {}) => {
  const hasMapId = useMapId && shouldUseGoogleMapsMapId;

  return {
    ...options,
    restriction: {
      latLngBounds: usaMapBounds,
      strictBounds: false,
    },
    ...(hasMapId ? { mapId: googleMapsMapId } : { styles: cleanMapStyles }),
  };
};

const waitForGoogleMaps = () =>
  new Promise((resolve, reject) => {
    const startedAt = Date.now();

    const checkMaps = () => {
      const maps = window.google?.maps;
      if (maps?.importLibrary || maps?.Map || maps?.Geocoder) {
        resolve(maps);
        return;
      }

      if (Date.now() - startedAt > 5000) {
        reject(new Error('Google Maps loaded without the Maps library.'));
        return;
      }

      window.setTimeout(checkMaps, 50);
    };

    checkMaps();
  });

const clearMapMarker = (marker) => {
  if (!marker) return;

  if (typeof marker.setMap === 'function') {
    marker.setMap(null);
    return;
  }

  marker.map = null;
};

const createDotMarkerContent = (color = drawAreaStrokeColor, size = 12) => {
  const marker = document.createElement('span');
  marker.style.background = color;
  marker.style.border = '2px solid #ffffff';
  marker.style.borderRadius = '50%';
  marker.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.28)';
  marker.style.display = 'block';
  marker.style.height = `${size}px`;
  marker.style.width = `${size}px`;

  return marker;
};

const createStartingAddressMarkerContent = () => {
  const marker = document.createElement('span');
  const center = document.createElement('span');

  marker.style.background = '#ed5c2f';
  marker.style.border = '2px solid #ffffff';
  marker.style.borderRadius = '50% 50% 50% 0';
  marker.style.boxShadow = '0 4px 12px rgba(15, 23, 42, 0.28)';
  marker.style.display = 'grid';
  marker.style.height = '26px';
  marker.style.placeItems = 'center';
  marker.style.transform = 'rotate(-45deg) translateY(-6px)';
  marker.style.transformOrigin = 'center';
  marker.style.width = '26px';

  center.style.background = '#ffffff';
  center.style.borderRadius = '50%';
  center.style.display = 'block';
  center.style.height = '8px';
  center.style.transform = 'rotate(45deg)';
  center.style.width = '8px';

  marker.appendChild(center);
  return marker;
};

const createStartingAddressMarkerIcon = (maps) => {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="34" height="42" viewBox="0 0 34 42">
      <path fill="#ed5c2f" stroke="#fff" stroke-width="2" d="M17 1C8.2 1 1 8.2 1 17c0 11.6 16 23 16 23s16-11.4 16-23C33 8.2 25.8 1 17 1Z"/>
      <circle cx="17" cy="17" r="5.5" fill="#fff"/>
    </svg>
  `.trim();

  return {
    anchor: maps?.Point ? new maps.Point(17, 40) : undefined,
    scaledSize: maps?.Size ? new maps.Size(34, 42) : undefined,
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
  };
};

const createMapMarker = ({
  AdvancedMarkerElement,
  Marker,
  color,
  icon,
  map,
  maps,
  position,
  title,
  useAdvancedMarker = shouldUseGoogleMapsMapId,
  zIndex = 20,
}) => {
  if (useAdvancedMarker && shouldUseGoogleMapsMapId && AdvancedMarkerElement) {
    return new AdvancedMarkerElement({
      content: icon ? createDotMarkerContent(color) : createStartingAddressMarkerContent(),
      map,
      position,
      title,
      zIndex,
    });
  }

  if (Marker) {
    return new Marker({
      icon: icon || createStartingAddressMarkerIcon(maps),
      map,
      optimized: false,
      position,
      title,
      zIndex,
    });
  }

  return null;
};

const loadGoogleMaps = () => {
  if (window.__olcGoogleMapsAuthFailed) {
    return Promise.reject(new Error(googleMapsAuthErrorMessage));
  }

  if (window.google?.maps) return Promise.resolve(window.google.maps);

  if (googleMapsLoadPromise) return googleMapsLoadPromise;

  googleMapsLoadPromise = new Promise((resolve, reject) => {
    const failAuth = () => {
      window.__olcGoogleMapsAuthFailed = true;
      window.dispatchEvent(new CustomEvent(googleMapsAuthFailureEvent));
      reject(new Error(googleMapsAuthErrorMessage));
    };

    const previousAuthFailure = window.gm_authFailure;
    const previousCallback = window[googleMapsCallbackName];
    window.gm_authFailure = () => {
      if (typeof previousAuthFailure === 'function') previousAuthFailure();
      failAuth();
    };
    window[googleMapsCallbackName] = () => {
      if (typeof previousCallback === 'function') previousCallback();
      handleLoad();
    };

    const handleLoad = () => {
      if (window.__olcGoogleMapsAuthFailed) {
        reject(new Error(googleMapsAuthErrorMessage));
        return;
      }

      waitForGoogleMaps().then(resolve).catch(reject);
    };

    const handleError = () => {
      reject(new Error('Google Maps failed to load. Check the network connection and API key.'));
    };

    const googleMapsScriptSrc = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=places,geocoding,marker&callback=${googleMapsCallbackName}&loading=async`;
    const existingScript = document.getElementById(googleMapsScriptId);
    if (existingScript) {
      if (existingScript.src !== googleMapsScriptSrc) {
        existingScript.remove();
      } else if (existingScript.dataset.loaded === 'true') {
        handleLoad();
        return;
      } else {
        existingScript.addEventListener('load', handleLoad, { once: true });
        existingScript.addEventListener('error', handleError, { once: true });
        return;
      }
    }

    const script = document.createElement('script');
    script.id = googleMapsScriptId;
    script.src = googleMapsScriptSrc;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      script.dataset.loaded = 'true';
      handleLoad();
    };
    script.onerror = handleError;
    document.head.appendChild(script);
  }).catch((error) => {
    googleMapsLoadPromise = null;
    throw error;
  });

  return googleMapsLoadPromise;
};

const loadGoogleMapsLibraries = async () => {
  if (googleMapsLibrariesPromise) return googleMapsLibrariesPromise;

  googleMapsLibrariesPromise = loadGoogleMaps()
    .then(async (maps) => {
      if (!maps.importLibrary) {
        return {
          AdvancedMarkerElement: maps.marker?.AdvancedMarkerElement,
          Autocomplete: maps.places?.Autocomplete,
          Circle: maps.Circle,
          Geocoder: maps.Geocoder,
          Map: maps.Map,
          Marker: maps.Marker,
          OverlayView: maps.OverlayView,
          Polygon: maps.Polygon,
          Polyline: maps.Polyline,
          maps,
        };
      }

      const [mapsLibrary, geocodingLibrary, markerLibrary, placesLibrary] = await Promise.all([
        maps.importLibrary('maps'),
        maps.importLibrary('geocoding'),
        maps.importLibrary('marker'),
        maps.importLibrary('places'),
      ]);

      return {
        AdvancedMarkerElement: markerLibrary.AdvancedMarkerElement || maps.marker?.AdvancedMarkerElement,
        Autocomplete: placesLibrary.Autocomplete || maps.places?.Autocomplete,
        Circle: mapsLibrary.Circle || maps.Circle,
        Geocoder: geocodingLibrary.Geocoder || maps.Geocoder,
        Map: mapsLibrary.Map || maps.Map,
        Marker: markerLibrary.Marker || maps.Marker,
        OverlayView: mapsLibrary.OverlayView || maps.OverlayView,
        Polygon: mapsLibrary.Polygon || maps.Polygon,
        Polyline: mapsLibrary.Polyline || maps.Polyline,
        maps,
      };
    })
    .catch((error) => {
      googleMapsLibrariesPromise = null;
      throw error;
    });

  return googleMapsLibrariesPromise;
};

const FindNewContacts = ({
  costPerRecord,
  costPerRecordCents,
  findNewMode = 'locations',
  onBack,
  onCancel,
  onContinue,
  onPricingChanged,
}) => {
  const dispatch = useDispatch();
  const isFindHomesAroundAddressesMode = findNewMode === 'addresses';
  const isFindHomesAroundLocationsMode = findNewMode === 'locations';
  const settingsDraftScope = isFindHomesAroundAddressesMode
    ? 'addresses'
    : isFindHomesAroundLocationsMode
      ? 'locations'
      : 'default';
  const isForcedMultipleEntryMode = isFindHomesAroundLocationsMode;
  const contacts = useSelector((state) => state.contactsReducers.contacts) || [];
  const contactLabels = useSelector((state) => state.contactsReducers.labels) || [];
  const contactsLoading = useSelector((state) => state.contactsReducers.loading) || false;
  const mapRef = useRef(null);
  const mapPreviewRef = useRef(null);
  const addressInputRef = useRef(null);
  const autocompleteInputElementRef = useRef(null);
  const autocompletePlaceChangedListenerRef = useRef(null);
  const autocompleteRef = useRef(null);
  const isInitialContactSearchRef = useRef(true);
  const isInitialTagSearchRef = useRef(true);
  const isFindHomesAroundLocationsModeRef = useRef(isFindHomesAroundLocationsMode);
  const addressAutocompleteBoundsRef = useRef(usaSearchBounds);
  const googleMapRef = useRef(null);
  const mapProjectionOverlayRef = useRef(null);
  const markerRef = useRef(null);
  const selectedManualAreaMarkerRef = useRef(null);
  const pendingResolvedAddressRef = useRef(null);
  const addressBoundaryRef = useRef(null);
  const boundaryFeatureLayerRefs = useRef([]);
  const boundaryFeatureRetryRef = useRef({ listener: null, timeouts: [] });
  const radiusCircleRef = useRef(null);
  const addStartingAddressValueRef = useRef(null);
  const drawRadiusCircleRefs = useRef([]);
  const drawRadiusPreviewCircleRef = useRef(null);
  const manualAreaDeleteMarkerRefs = useRef([]);
  const removeStartingAddressRef = useRef(null);
  const radiusPointMarkerRefs = useRef([]);
  const customPolygonRefs = useRef([]);
  const customPolylineRef = useRef(null);
  const customPointMarkerRefs = useRef([]);
  const refinePanelRef = useRef(null);
  const refineRowRef = useRef(null);
  const addressFocusTimeoutRef = useRef(null);
  const drawRadiusCenterRef = useRef(null);
  const drawRadiusEdgeRef = useRef(null);
  const isDrawingRadiusRef = useRef(false);
  const mapDrawingListenersRef = useRef([]);
  const lastMapRenderKeyRef = useRef('');
  const suppressManualAreaAutoFitRef = useRef(false);
  const radiusEstimateAbortControllerRef = useRef(null);
  const radiusEstimateDebounceRef = useRef(null);
  const customRadiusCommitDebounceRef = useRef(null);
  const radiusEstimateRunRef = useRef(0);
  const lookupSearchAbortRefs = useRef({});
  const lookupSearchRunRef = useRef({});
  const lookupSearchTimeoutRefs = useRef({});
  const retryingAddressEstimateKeysRef = useRef(new Set());
  const pendingAddressEstimateRequestKeysRef = useRef(new Map());
  const addressAggregateEstimateAbortControllerRef = useRef(null);
  const addressAggregateEstimateDebounceRef = useRef(null);
  const addressAggregateEstimateRunRef = useRef(0);
  const addressAggregateRequestAreasRef = useRef({ areas: [], key: '' });
  const selectedEstimateFilterPayloadRef = useRef({ key: '', payload: {} });
  const settingsDraftDebounceRef = useRef(null);
  const currentRadiusEstimateRef = useRef({
    estimatedHomes: null,
    estimatedRadiusMiles: null,
    estimateStatus: 'idle',
  });
  const mapTypeIdRef = useRef('roadmap');
  const multipleAddressEstimateAbortControllerRef = useRef(null);
  const multipleAddressEstimateDebounceRef = useRef(null);
  const multipleAddressEstimateInputKeyRef = useRef('');
  const multipleAddressEstimateRunRef = useRef(0);
  const activeBulkAddressEstimateCancelRef = useRef(null);
  const activeBulkManualAreaEstimateCancelRef = useRef(null);
  const findNewContactsSocketRef = useRef(null);
  const lastEstimateFailureNotificationRef = useRef({ message: '', timestamp: 0 });
  const restoredDraftRef = useRef();
  const summaryAddressListRef = useRef(null);
  const summaryAddressAutoScrollFrameRef = useRef(null);
  const summaryAddressPreservedScrollTopRef = useRef(null);
  const shouldApplyBrowserMapCenterRef = useRef(true);
  if (restoredDraftRef.current === undefined) {
    const restoredMode = isForcedMultipleEntryMode
      ? 'multiple'
      : readLastStartingAddressMode(settingsDraftScope);
    restoredDraftRef.current = readSettingsDraft(restoredMode, settingsDraftScope) || {
      startingAddressMode: normalizeStartingAddressMode(restoredMode),
    };
  }

  const restoredDraft = restoredDraftRef.current || {};
  const restoredStartingAddresses = restoredDraft.startingAddresses || [];
  const hasRestoredDrawnAreas = Boolean(
    (Array.isArray(restoredDraft.customShapes) && restoredDraft.customShapes.length) ||
    (Array.isArray(restoredDraft.drawRadiusAreas) && restoredDraft.drawRadiusAreas.length) ||
    (Array.isArray(restoredDraft.manualAreaOrder) && restoredDraft.manualAreaOrder.length)
  );
  const restoredSelectedAddressIndex = Math.min(
    Math.max(Number(restoredDraft.selectedAddressIndex || 0), 0),
    Math.max(restoredStartingAddresses.length - 1, 0)
  );
  const restoredActiveAddress = restoredStartingAddresses[restoredSelectedAddressIndex] || '';
  const restoredContactType = normalizeContactType(restoredDraft.contactType);
  const restoredActiveConfig = sanitizeAddressConfig(
    restoredStartingAddresses.length > 0
      ? restoredDraft.addressConfigs?.[getAddressKey(restoredActiveAddress)]
      : hasRestoredDrawnAreas
        ? {
            ...createDefaultAddressConfig(restoredContactType),
            areaMode: restoredDraft.areaMode,
            customRadiusMiles: restoredDraft.customRadiusMiles,
            customShapes: restoredDraft.customShapes,
            drawRadiusAreas: restoredDraft.drawRadiusAreas,
            manualAreaOrder: restoredDraft.manualAreaOrder,
          }
        : undefined,
    restoredContactType
  );
  const restoredStartingAddressMode =
    isForcedMultipleEntryMode
      ? 'multiple'
      : restoredDraft.startingAddressMode || (restoredStartingAddresses.length > 1 ? 'multiple' : 'single');
  const restoredVisibleConfig = getVisibleAddressConfig(
    restoredActiveConfig,
    restoredStartingAddressMode,
    { preserveManualAreas: isFindHomesAroundLocationsMode }
  );
  const initialVisibleConfig = isFindHomesAroundAddressesMode
    ? getIdleAddressTargetingConfig(restoredVisibleConfig)
    : restoredVisibleConfig;
  const restoredFiltersByContactType = normalizeFiltersByContactType(
    restoredDraft.filtersByContactType,
    initialVisibleConfig.filters,
    restoredContactType
  );
  const restoredSelectedFilters = normalizeFindContactFilters(
    restoredFiltersByContactType[restoredContactType] || initialVisibleConfig.filters,
    restoredContactType
  );
  const restoredLocationAreaMode = ['custom', 'draw-radius'].includes(restoredVisibleConfig.areaMode)
    ? restoredVisibleConfig.areaMode
    : 'custom';

  const [address, setAddress] = useState('');
  const [addressEntrySource, setAddressEntrySource] = useState('enter');
  const [addressUploadError, setAddressUploadError] = useState('');
  const [isProcessingAddressUpload, setIsProcessingAddressUpload] = useState(false);
  const [uploadedAddressFileName, setUploadedAddressFileName] = useState('');
  const [uploadedAddressCount, setUploadedAddressCount] = useState(0);
  const [selectedContactIds, setSelectedContactIds] = useState([]);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [selectedContactsCache, setSelectedContactsCache] = useState({});
  const [selectedTagsCache, setSelectedTagsCache] = useState({});
  const [selectedTagContactsCount, setSelectedTagContactsCount] = useState({
    addressTotal: 0,
    isLoaded: false,
    key: '',
    total: 0,
  });
  const [contactSearch, setContactSearch] = useState('');
  const [contactSearchFocusRequest, setContactSearchFocusRequest] = useState(0);
  const [tagSearch, setTagSearch] = useState('');
  const [tagsLoading, setTagsLoading] = useState(false);
  const [isAddingContactAddresses, setIsAddingContactAddresses] = useState(false);
  const [contactAddressMessage, setContactAddressMessage] = useState('');
  const [addressSearchBounds, setAddressSearchBounds] = useState(usaSearchBounds);
  const [localMapCenter, setLocalMapCenter] = useState(null);
  const [hasStoredFindNewContactsDataState, setHasStoredFindNewContactsDataState] = useState(() =>
    hasStoredFindNewContactsData(settingsDraftScope)
  );
  const [isAddressInputSpotlighted, setIsAddressInputSpotlighted] = useState(false);
  const [activeContactType, setActiveContactType] = useState(restoredContactType);
  const [startingAddresses, setStartingAddresses] = useState(restoredStartingAddresses);
  const [startingAddressMode] = useState(restoredStartingAddressMode);
  const [listCleanupOptions, setListCleanupOptions] = useState(
    normalizeListCleanupOptions(restoredDraft.listCleanupOptions)
  );
  const [selectedAddressIndex, setSelectedAddressIndex] = useState(restoredSelectedAddressIndex);
  const [addressConfigs, setAddressConfigs] = useState(restoredDraft.addressConfigs || {});
  const [addressEstimateStatusByKey, setAddressEstimateStatusByKey] = useState({});
  const [addressEstimateResultByKey, setAddressEstimateResultByKey] = useState({});
  const [targetCount, setTargetCount] = useState(initialVisibleConfig.targetCount);
  const [customTargetCount, setCustomTargetCount] = useState(initialVisibleConfig.customTargetCount);
  const [radiusMiles, setRadiusMiles] = useState(initialVisibleConfig.radiusMiles);
  const [customRadiusMiles, setCustomRadiusMiles] = useState(initialVisibleConfig.customRadiusMiles);
  const [areaMode, setAreaMode] = useState(
    isFindHomesAroundLocationsMode ? restoredLocationAreaMode : initialVisibleConfig.areaMode
  );
  const [nearbyTargetMode, setNearbyTargetMode] = useState(initialVisibleConfig.nearbyTargetMode);
  const [isDrawingShape, setIsDrawingShape] = useState(false);
  const [customShapes, setCustomShapes] = useState(initialVisibleConfig.customShapes);
  const [customShapePoints, setCustomShapePoints] = useState(initialVisibleConfig.customShapePoints);
  const [drawRadiusAreas, setDrawRadiusAreas] = useState(initialVisibleConfig.drawRadiusAreas);
  const [drawRadiusPoints, setDrawRadiusPoints] = useState(initialVisibleConfig.drawRadiusPoints);
  const [manualAreaOrder, setManualAreaOrder] = useState(initialVisibleConfig.manualAreaOrder);
  const [selectedManualAreaKey, setSelectedManualAreaKey] = useState('');
  const [selectedFilters, setSelectedFilters] = useState(
    restoredSelectedFilters
  );
  const [showFilters, setShowFilters] = useState(false);
  const [filterDraft, setFilterDraft] = useState(
    restoredSelectedFilters
  );
  const [activeSavedFilter, setActiveSavedFilter] = useState(null);
  const [saveFilterModalOpen, setSaveFilterModalOpen] = useState(false);
  const [saveFilterLoading, setSaveFilterLoading] = useState(false);
  const [filtersByContactType, setFiltersByContactType] = useState(restoredFiltersByContactType);
  const [lookupLoadingByKey, setLookupLoadingByKey] = useState({});
  const [lookupOptionLabelsByKey, setLookupOptionLabelsByKey] = useState({});
  const [lookupOptionsByKey, setLookupOptionsByKey] = useState({});
  const previousContactTypeRef = useRef(activeContactType);
  const filtersByContactTypeRef = useRef(filtersByContactType);
  const [mapStatus, setMapStatus] = useState('idle');
  const [mapError, setMapError] = useState('');
  const [mapTypeId, setMapTypeId] = useState('roadmap');
  const [addressBounds, setAddressBounds] = useState(initialVisibleConfig.addressBounds);
  const [addressViewportBounds, setAddressViewportBounds] = useState(initialVisibleConfig.addressViewportBounds);
  const [coordinates, setCoordinates] = useState(initialVisibleConfig.coordinates);
  const [estimateStatus, setEstimateStatus] = useState('idle');
  const [estimateError, setEstimateError] = useState('');
  const [estimatedFilterKey, setEstimatedFilterKey] = useState(initialVisibleConfig.estimatedFilterKey);
  const [estimatedHomes, setEstimatedHomes] = useState(initialVisibleConfig.estimatedHomes);
  const [addressAggregateEstimatedHomes, setAddressAggregateEstimatedHomes] = useState(null);
  const [addressAggregateEstimateStatus, setAddressAggregateEstimateStatus] = useState('idle');
  const [estimatedRadiusMiles, setEstimatedRadiusMiles] = useState(initialVisibleConfig.estimatedRadiusMiles);
  const [isWaitingForFirstBulkAddressEstimate, setIsWaitingForFirstBulkAddressEstimate] = useState(false);
  currentRadiusEstimateRef.current = {
    estimatedFilterKey,
    estimatedHomes,
    estimatedRadiusMiles,
    estimateStatus,
  };
  const cancelActiveBulkAddressEstimate = useCallback((resetWaitingState = true) => {
    activeBulkAddressEstimateCancelRef.current?.();
    activeBulkAddressEstimateCancelRef.current = null;
    if (resetWaitingState) setIsWaitingForFirstBulkAddressEstimate(false);
  }, []);
  const cancelActiveBulkManualAreaEstimate = useCallback(() => {
    activeBulkManualAreaEstimateCancelRef.current?.();
    activeBulkManualAreaEstimateCancelRef.current = null;
  }, []);
  useEffect(() => () => {
    if (radiusEstimateDebounceRef.current) window.clearTimeout(radiusEstimateDebounceRef.current);
    if (customRadiusCommitDebounceRef.current) window.clearTimeout(customRadiusCommitDebounceRef.current);
    if (multipleAddressEstimateDebounceRef.current) window.clearTimeout(multipleAddressEstimateDebounceRef.current);
    if (addressAggregateEstimateDebounceRef.current) window.clearTimeout(addressAggregateEstimateDebounceRef.current);
    if (manualAreaEstimateDebounceRef.current) window.clearTimeout(manualAreaEstimateDebounceRef.current);
    if (settingsDraftDebounceRef.current) window.clearTimeout(settingsDraftDebounceRef.current);
    radiusEstimateAbortControllerRef.current?.abort?.();
    multipleAddressEstimateAbortControllerRef.current?.abort?.();
    cancelActiveBulkAddressEstimate(false);
    addressAggregateEstimateAbortControllerRef.current?.abort?.();
    manualAreaEstimateAbortControllerRef.current?.abort?.();
    cancelActiveBulkManualAreaEstimate();
    releaseFindNewContactsSocket(findNewContactsSocketRef.current);
  }, [cancelActiveBulkAddressEstimate, cancelActiveBulkManualAreaEstimate]);
  useEffect(() => {
    const socketConnection = getFindNewContactsSocket();

    findNewContactsSocketRef.current = socketConnection;

    return () => {
      releaseFindNewContactsSocket(socketConnection);
      if (findNewContactsSocketRef.current === socketConnection) {
        findNewContactsSocketRef.current = null;
      }
    };
  }, []);
  const rememberFiltersForContactType = useCallback((contactType, filters) => {
    const normalizedContactType = normalizeContactType(contactType);
    const normalizedFilters = normalizeFindContactFilters(filters, normalizedContactType);
    const currentFiltersByContactType = filtersByContactTypeRef.current;
    const nextFiltersByContactType = {
      ...currentFiltersByContactType,
      [normalizedContactType]: normalizedFilters,
    };

    filtersByContactTypeRef.current = nextFiltersByContactType;
    setFiltersByContactType((currentState) =>
      JSON.stringify(currentState) === JSON.stringify(nextFiltersByContactType)
        ? currentState
        : nextFiltersByContactType
    );

    return normalizedFilters;
  }, []);
  const [manualAreaEstimateByKey, setManualAreaEstimateByKey] = useState({});
  const manualAreaEstimateByKeyRef = useRef({});
  const manualAreaEstimateAbortControllerRef = useRef(null);
  const manualAreaEstimateDebounceRef = useRef(null);
  const manualAreaEstimateRunRef = useRef(0);
  const manualAggregateEstimateRunRef = useRef(0);
  const manualAggregateEstimateRequestRef = useRef({ key: '', promise: null, timeoutId: null });
  const manualAggregateEstimateResolvedKeyRef = useRef('');
  const pendingManualAreaEstimateRequestKeysRef = useRef(new Map());
  const retryingManualAreaEstimateKeysRef = useRef(new Set());
  const [isDataAxleConfigured, setIsDataAxleConfigured] = useState(initialVisibleConfig.isDataAxleConfigured);
  const [nearbyModeSettings, setNearbyModeSettings] = useState(initialVisibleConfig.nearbyModeSettings);
  const [placeId, setPlaceId] = useState(initialVisibleConfig.placeId);
  const [placeTypes, setPlaceTypes] = useState(initialVisibleConfig.placeTypes);
  const [locationFilter, setLocationFilter] = useState(initialVisibleConfig.locationFilter);
  const [isGoogleBoundaryVisible, setIsGoogleBoundaryVisible] = useState(false);
  const [clearConfirmModal, setClearConfirmModal] = useState({
    className: 'clearFindContactsModal',
    handleClose: () => {},
    handleOk: () => {},
    header: 'Clear Selection',
    message: '',
    open: false,
    successButtonName: 'Yes',
    cancelButtonName: 'No',
  });
  const [purchaseConfirmModal, setPurchaseConfirmModal] = useState({
    className: 'purchaseConfirmModal findNewContactsPurchaseConfirmModal',
    handleClose: () => {},
    handleOk: () => {},
    header: 'Confirm Purchase',
    message: '',
    open: false,
    successButtonName: 'Purchase',
    cancelButtonName: 'Cancel',
    type: 'export',
  });
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const purchaseIdempotencyKeyRef = useRef(null);
  const purchaseAttemptedRef = useRef(false);
  const notifyEstimateRequestFailure = useCallback((message = MESSAGES.ORDERS.CREATE.FIND_NEW_CONTACTS_ESTIMATE_ERROR) => {
    const timestamp = Date.now();
    const lastNotification = lastEstimateFailureNotificationRef.current;

    if (
      lastNotification.message === message &&
      timestamp - lastNotification.timestamp < estimateFailureNotificationCooldownMs
    ) {
      return;
    }

    lastEstimateFailureNotificationRef.current = { message, timestamp };
    dispatch(failure(message));
  }, [dispatch]);
  const [pendingPurchaseAction, setPendingPurchaseAction] = useState('continue_to_mail');
  const [purchaseListName, setPurchaseListName] = useState(() => getDefaultPurchaseListName(findNewMode));
  const [purchaseTermsAccepted, setPurchaseTermsAccepted] = useState(false);

  const contactOptions = useMemo(() => {
    const mappedContacts = contacts.map((contact) => ({
      id: contact.id,
      object: contact,
      title: buildContactOptionTitle(contact),
    }));
    const existingIds = new Set(mappedContacts.map((contact) => String(contact.id)));
    const missingSelectedOptions = selectedContactIds
      .filter((id) => !existingIds.has(String(id)))
      .map((id) => {
        const cachedContact = selectedContactsCache[String(id)];

        return {
          id: cachedContact?.id || id,
          object: cachedContact || { id },
          title: cachedContact ? buildContactOptionTitle(cachedContact) : `Contact #${id}`,
        };
      });

    return [...mappedContacts, ...missingSelectedOptions];
  }, [contacts, selectedContactIds, selectedContactsCache]);

  const tagOptions = useMemo(() => {
    const mappedTags = [...contactLabels]
      .sort((a, b) => a?.title?.localeCompare(b?.title))
      .map((tag) => ({
        id: tag.id,
        object: tag,
        title: tag.title,
      }));
    const existingIds = new Set(mappedTags.map((tag) => String(tag.id)));
    const missingSelectedOptions = selectedTagIds
      .filter((id) => !existingIds.has(String(id)))
      .map((id) => {
        const cachedTag = selectedTagsCache[String(id)];

        return {
          id: cachedTag?.id || id,
          object: cachedTag || { id, title: `Tag #${id}` },
          title: cachedTag?.title || `Tag #${id}`,
        };
      });

    return [...mappedTags, ...missingSelectedOptions];
  }, [contactLabels, selectedTagIds, selectedTagsCache]);

  const hasContactSourceSelection = selectedContactIds.length > 0 || selectedTagIds.length > 0;
  const selectedTagIdsKey = useMemo(() => selectedTagIds.map(String).join(','), [selectedTagIds]);

  const activeAddressIndex = startingAddresses[selectedAddressIndex] ? selectedAddressIndex : 0;
  const mapAddress = startingAddresses[activeAddressIndex] || '';
  const hasStartingAddress = Boolean(mapAddress);
  const startingAddressCount = startingAddresses.length;
  const shouldExcludeStartingAddresses =
    isFindHomesAroundAddressesMode &&
    listCleanupOptions.excludeTargetAddressesFromMailingList;
  const estimateCleanupPayload = useMemo(() => (
    shouldExcludeStartingAddresses
      ? {
          listCleanupOptions: {
            excludeTargetAddressesFromMailingList: true,
          },
        }
      : {}
  ), [shouldExcludeStartingAddresses]);
  const isMultipleAddressMode = startingAddressMode === 'multiple';
  const usesSharedAddressSettings =
    isMultipleAddressMode || isFindHomesAroundAddressesMode || isFindHomesAroundLocationsMode;
  const canUseTargetingMethods = startingAddressCount > 0;
  const isNearbyHomesMode = areaMode === 'radius' && nearbyTargetMode === 'homes';
  const isNearbyRadiusMode = areaMode === 'radius' && nearbyTargetMode === 'radius';
  const hasSelectedTargetingMethod =
    isManualAreaModeValue(areaMode) ||
    isNearbyHomesMode ||
    isNearbyRadiusMode;
  const canUseTargetMap =
    (hasStartingAddress || isFindHomesAroundLocationsMode) &&
    mapStatus === 'ready';
  const drawAreaCount = drawRadiusAreas.length + customShapes.length;
  const selectedMapLocationCount =
    startingAddressCount + drawAreaCount;
  const hasReachedMaxSelectedAddresses =
    isFindHomesAroundAddressesMode && startingAddressCount >= maxSelectedAddresses;
  const hasReachedMaxSelectedMapLocations =
    isFindHomesAroundLocationsMode && selectedMapLocationCount >= maxSelectedMapLocations;
  const hasReachedMaxAddressEntries =
    hasReachedMaxSelectedAddresses || hasReachedMaxSelectedMapLocations;
  const startingAddressSummaryTitle = isFindHomesAroundLocationsMode
    ? `Locations ${selectedMapLocationCount} / ${maxSelectedMapLocations}`
    : `Addresses ${startingAddressCount} / ${maxSelectedAddresses}`;
  const contactTypeCopy = contactTypeCopyByType[activeContactType] || contactTypeCopyByType.consumers;
  const inputEntityLabel = isFindHomesAroundLocationsMode ? 'location' : 'address';
  const summaryEmptyAddressActionLabel = isFindHomesAroundAddressesMode
    ? addressEntrySource === 'upload'
      ? 'Select file'
      : addressEntrySource === 'contacts'
        ? 'Select contacts'
        : 'Add address'
    : `Add ${inputEntityLabel}`;
  const addressInputLabel = isFindHomesAroundLocationsMode ? 'Enter locations' : 'Starting address';
  const addressInputPlaceholder = isFindHomesAroundLocationsMode
    ? 'Enter a state, county, city, or ZIP code'
    : 'Type an address, then pick from the list';
  const pageHeaderTitle = isFindHomesAroundAddressesMode
    ? 'Find New Contacts Around an Address'
    : 'Find New Contacts in an Area';
  const pageHeaderDescription = isFindHomesAroundAddressesMode
    ? 'Add one or more addresses, set your radius, and apply filters to refine your list.'
    : 'Add one or more areas and apply filters to refine your list.';
  const addressUploadMessage = hasReachedMaxSelectedAddresses ? '' : addressUploadError;
  const addressContactsPanelMessage = hasReachedMaxSelectedAddresses ? '' : contactAddressMessage;
  const addressEntryLimitMessage = isFindHomesAroundAddressesMode
    ? ['upload', 'contacts'].includes(addressEntrySource)
      ? maxSelectedAddressImportMessage
      : maxSelectedAddressEntryLimitMessage
    : maxSelectedItemsPlaceholder;
  const preserveSummaryAddressListScroll = useCallback(() => {
    const listElement = summaryAddressListRef.current;
    if (!listElement) return;

    summaryAddressPreservedScrollTopRef.current = listElement.scrollTop;
  }, []);
  useEffect(() => {
    if (!isFindHomesAroundAddressesMode || startingAddressCount >= maxSelectedAddresses) return;

    setAddressUploadError((currentMessage) =>
      isMaxSelectedAddressMessage(currentMessage) ? '' : currentMessage
    );
    setContactAddressMessage((currentMessage) =>
      isMaxSelectedAddressMessage(currentMessage) ? '' : currentMessage
    );
  }, [isFindHomesAroundAddressesMode, startingAddressCount]);
  const isContactTypeFilterSyncPending = previousContactTypeRef.current !== activeContactType;
  useEffect(() => {
    if (!isContactTypeFilterSyncPending) return;

    const previousContactType = previousContactTypeRef.current;
    rememberFiltersForContactType(previousContactType, selectedFilters);
    previousContactTypeRef.current = activeContactType;
    const savedFilters = filtersByContactTypeRef.current[activeContactType] || createEmptyFilterState(activeContactType);
    const nextFilters = rememberFiltersForContactType(activeContactType, savedFilters);
    cancelActiveBulkAddressEstimate();
    multipleAddressEstimateRunRef.current += 1;
    multipleAddressEstimateAbortControllerRef.current?.abort?.();
    multipleAddressEstimateAbortControllerRef.current = null;
    setSelectedFilters(nextFilters);
    setFilterDraft(nextFilters);
    setActiveSavedFilter(null);
    setLookupLoadingByKey({});
    setLookupOptionsByKey({});
    setShowFilters(false);
    setEstimatedFilterKey('');
    setEstimatedHomes(null);
    setEstimatedRadiusMiles(null);
    setEstimateStatus('idle');
    setEstimateError('');
    setAddressAggregateEstimatedHomes(null);
    setAddressAggregateEstimateStatus('idle');
    setAddressEstimateStatusByKey({});
    setAddressEstimateResultByKey({});
    setManualAreaEstimateByKey({});
    manualAreaEstimateByKeyRef.current = {};
    pendingAddressEstimateRequestKeysRef.current.clear();
    pendingManualAreaEstimateRequestKeysRef.current.clear();
  }, [
    activeContactType,
    cancelActiveBulkAddressEstimate,
    isContactTypeFilterSyncPending,
    rememberFiltersForContactType,
    selectedFilters,
  ]);
  const selectedFilterPayload = useMemo(
    () => getEstimateFiltersPayload(selectedFilters, activeContactType),
    [activeContactType, selectedFilters]
  );
  const hasAppliedFilters = Object.keys(selectedFilterPayload).length > 0;
  const selectedFilterPayloadKey = useMemo(
    () => JSON.stringify({ contactType: activeContactType, filters: selectedFilterPayload }),
    [activeContactType, selectedFilterPayload]
  );
  const selectedEstimateKey = useMemo(
    () => (
      shouldExcludeStartingAddresses
        ? JSON.stringify({
            excludeTargetAddresses: true,
            filters: selectedFilterPayloadKey,
          })
        : selectedFilterPayloadKey
    ),
    [selectedFilterPayloadKey, shouldExcludeStartingAddresses]
  );
  const selectedEstimateFilterPayload = useMemo(() => {
    if (selectedEstimateFilterPayloadRef.current.key === selectedFilterPayloadKey) {
      return selectedEstimateFilterPayloadRef.current.payload;
    }

    selectedEstimateFilterPayloadRef.current = {
      key: selectedFilterPayloadKey,
      payload: selectedFilterPayload,
    };
    return selectedFilterPayload;
  }, [selectedFilterPayload, selectedFilterPayloadKey]);
  const activeFilterDefinitions = useMemo(
    () => getDataAxleFilterDefinitions(activeContactType),
    [activeContactType]
  );
  const activeDataAxleFilterByKey = useMemo(
    () => getDataAxleFilterByKey(activeContactType),
    [activeContactType]
  );
  const selectedFilterItems = useMemo(
    () => getActiveFilterItems(selectedFilters, {
      contactType: activeContactType,
      optionLabelsByKey: lookupOptionLabelsByKey,
    }),
    [activeContactType, lookupOptionLabelsByKey, selectedFilters]
  );
  const selectedFilterLabels = useMemo(
    () => getActiveFilterLabels(selectedFilters, {
      contactType: activeContactType,
      optionLabelsByKey: lookupOptionLabelsByKey,
      showAllOptions: true,
    }),
    [activeContactType, lookupOptionLabelsByKey, selectedFilters]
  );
  const selectedFilterTooltipByKey = useMemo(
    () => getActiveFilterItems(selectedFilters, {
      contactType: activeContactType,
      optionLabelsByKey: lookupOptionLabelsByKey,
      showAllOptions: true,
    }).reduce((itemsByKey, item) => ({
      ...itemsByKey,
      [item.key]: item.label,
    }), {}),
    [activeContactType, lookupOptionLabelsByKey, selectedFilters]
  );
  const normalizedFilterDraft = useMemo(
    () => normalizeFindContactFilters(filterDraft, activeContactType),
    [activeContactType, filterDraft]
  );
  const lookupDropdownOptionsByKey = useMemo(() => {
    if (activeContactType !== 'businesses') return {};

    return activeFilterDefinitions.reduce((optionsByKey, definition) => {
      if (!definition.asyncLookup) return optionsByKey;

      const existingOptions = lookupOptionsByKey[definition.key] || [];
      const existingValues = new Set(existingOptions.map((option) => String(option.id)));
      const labelsByValue = lookupOptionLabelsByKey[definition.key] || {};
      const selectedValues = normalizedFilterDraft[definition.key]?.values || [];
      const selectedOptions = selectedValues
        .filter((value) => !existingValues.has(String(value)))
        .map((value) => ({
          id: value,
          title: labelsByValue[value] || value,
        }));

      return {
        ...optionsByKey,
        [definition.key]: [...selectedOptions, ...existingOptions],
      };
    }, {});
  }, [
    activeContactType,
    activeFilterDefinitions,
    lookupOptionLabelsByKey,
    lookupOptionsByKey,
    normalizedFilterDraft,
  ]);
  const invalidFilterDraftKeys = useMemo(
    () => new Set(getInvalidRangeFilterKeys(filterDraft, activeContactType)),
    [activeContactType, filterDraft]
  );
  const hasInvalidFilterDraft = invalidFilterDraftKeys.size > 0;
  const filterDraftPayload = useMemo(
    () => getEstimateFiltersPayload(filterDraft, activeContactType),
    [activeContactType, filterDraft]
  );

  const handleLookupSearch = useCallback((key, search, options = {}) => {
    const definition = activeDataAxleFilterByKey[key];
    if (activeContactType !== 'businesses' || !definition?.asyncLookup) {
      return Promise.resolve();
    }

    const rawQuery = String(search || '').trim();
    const isInitialLookupSearch = !rawQuery;
    const query = isInitialLookupSearch ? (definition.initialLookupQuery || '') : rawQuery;

    if (lookupSearchTimeoutRefs.current[key]) {
      window.clearTimeout(lookupSearchTimeoutRefs.current[key]);
      lookupSearchTimeoutRefs.current[key] = null;
    }

    lookupSearchAbortRefs.current[key]?.abort?.();

    setLookupLoadingByKey((currentLoading) => ({ ...currentLoading, [key]: true }));

    return new Promise((resolve) => {
      const runSearch = async () => {
        const runId = (lookupSearchRunRef.current[key] || 0) + 1;
        const controller = new AbortController();
        lookupSearchRunRef.current[key] = runId;
        lookupSearchAbortRefs.current[key] = controller;

        try {
          const response = await request.get(
            'find-new-contacts/lookup-options',
            { field: key, q: query },
            controller.signal
          );
          if (lookupSearchRunRef.current[key] !== runId) return;

          const options = normalizeLookupOptions(response.data?.data?.options);
          setLookupOptionsByKey((currentOptions) => ({ ...currentOptions, [key]: options }));
          setLookupOptionLabelsByKey((currentLabels) =>
            mergeLookupOptionLabels(currentLabels, key, options)
          );
        } catch (error) {
          const isCanceled = error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError';
          if (!isCanceled) {
            setLookupOptionsByKey((currentOptions) => ({ ...currentOptions, [key]: [] }));
            if (!isInitialLookupSearch) {
              dispatch(failure(`Could not search ${definition.label} options.`));
            }
          }
        } finally {
          if (lookupSearchRunRef.current[key] === runId) {
            setLookupLoadingByKey((currentLoading) => ({ ...currentLoading, [key]: false }));
          }
          resolve();
        }
      };

      if (options.immediate) {
        runSearch();
        return;
      }

      lookupSearchTimeoutRefs.current[key] = window.setTimeout(runSearch, lookupSearchDebounceMs);
    });
  }, [activeContactType, activeDataAxleFilterByKey, dispatch]);

  useEffect(() => () => {
    Object.values(lookupSearchTimeoutRefs.current).forEach((timeoutId) => {
      if (timeoutId) window.clearTimeout(timeoutId);
    });
    Object.values(lookupSearchAbortRefs.current).forEach((controller) => {
      controller?.abort?.();
    });
  }, []);
  const filterDraftPayloadKey = useMemo(
    () => JSON.stringify({ contactType: activeContactType, filters: filterDraftPayload }),
    [activeContactType, filterDraftPayload]
  );
  const filterDraftCount = useMemo(
    () => Object.keys(filterDraftPayload || {}).length,
    [filterDraftPayload]
  );
  const hasPendingFilterDraftChanges = filterDraftPayloadKey !== selectedFilterPayloadKey;
  const pendingFilterChangeCount = useMemo(() => {
    const changedFilterKeys = new Set(
      [...new Set([
        ...Object.keys(selectedFilterPayload || {}),
        ...Object.keys(filterDraftPayload || {}),
      ])].filter((key) =>
        JSON.stringify(selectedFilterPayload?.[key] || null) !==
        JSON.stringify(filterDraftPayload?.[key] || null)
      )
    );
    invalidFilterDraftKeys.forEach((key) => changedFilterKeys.add(key));

    return changedFilterKeys.size;
  }, [filterDraftPayload, invalidFilterDraftKeys, selectedFilterPayload]);
  const hasUnappliedFilterChanges = hasPendingFilterDraftChanges || hasInvalidFilterDraft;
  const drawRadiusMiles = useMemo(() => {
    if (drawRadiusPoints.length !== 2) return 0;

    return Number(getDistanceMiles(drawRadiusPoints[0], drawRadiusPoints[1]).toFixed(3));
  }, [drawRadiusPoints]);

  useEffect(() => {
    shouldApplyBrowserMapCenterRef.current =
      isFindHomesAroundLocationsMode &&
      selectedMapLocationCount === 0 &&
      !isDrawingShape;
  }, [isDrawingShape, isFindHomesAroundLocationsMode, selectedMapLocationCount]);

  const debouncedFetchContacts = useMemo(
    () =>
      debounce((searchTerm) => {
        const trimmedSearch =
          searchTerm && searchTerm.length
            ? searchTerm.replace(/^(\S+\s+\S+)\s*(.*)/, '$1').trim()
            : null;

        dispatch(
          fetchContacts({
            pagination: DEFAULT_PAGINATION,
            search: trimmedSearch,
            isReturnAddress: false,
            excludeDncList: true,
          })
        );
      }, 300),
    [dispatch]
  );

  const debouncedFetchTags = useMemo(
    () =>
      debounce(async (searchTerm) => {
        setTagsLoading(true);
        try {
          await dispatch(
            fetchContactLabelsWithSearch({
              pageSize: searchTerm && searchTerm.trim() ? 500 : 20,
              search: searchTerm && searchTerm.trim() ? searchTerm.trim() : null,
            })
          );
        } finally {
          setTagsLoading(false);
        }
      }, 300),
    [dispatch]
  );
  const selectedLocationEstimateAreas = useMemo(() => {
    if (!isFindHomesAroundLocationsMode || !startingAddresses.length) return [];

    const activeAddressKey = mapAddress ? getAddressKey(mapAddress) : '';

    return startingAddresses
      .map((startingAddress, index) => {
        const addressKey = getAddressKey(startingAddress);
        const isActiveAddress = addressKey && addressKey === activeAddressKey;
        const addressConfig = sanitizeAddressConfig(isActiveAddress
          ? {
              ...(addressConfigs[addressKey] || {}),
              addressBounds,
              addressViewportBounds,
              coordinates,
              locationFilter,
              placeId,
              placeTypes,
            }
          : addressConfigs[addressKey]);
        const estimateArea = getBoundsEstimateArea(addressConfig.addressBounds, addressConfig.coordinates);
        const labelPosition =
          normalizeCoordinates(addressConfig.coordinates) ||
          getBoundsCenter(addressConfig.addressBounds, addressConfig.coordinates);

        if (!estimateArea || !labelPosition) return null;

        return {
          ...estimateArea,
          address: startingAddress,
          addressBounds: addressConfig.addressBounds,
          key: getLocationEstimateAreaKey(addressKey, addressConfig.locationFilter),
          deletePosition: labelPosition,
          labelPosition,
          locationFilter: addressConfig.locationFilter,
          orderKey: addressKey,
          sourceIndex: index,
          sourceType: 'location',
          type: 'location',
        };
      })
      .filter(Boolean);
  }, [
    activeAddressIndex,
    addressBounds,
    addressConfigs,
    addressViewportBounds,
    coordinates,
    isFindHomesAroundLocationsMode,
    locationFilter,
    mapAddress,
    placeId,
    placeTypes,
    startingAddresses,
  ]);
  const drawRadiusEstimateAreas = useMemo(
    () => drawRadiusAreas.map((area, index) => ({
      key: getDrawRadiusAreaEstimateKey(area, index),
      labelPosition: {
        latitude: area.center.latitude,
        longitude: area.center.longitude,
      },
      deletePosition: {
        latitude: area.center.latitude,
        longitude: area.center.longitude,
      },
      latitude: area.center.latitude,
      longitude: area.center.longitude,
      orderKey: getDrawRadiusAreaOrderKey(area),
      radiusMiles: area.radiusMiles,
      sourceIndex: index,
      sourceType: 'circle',
      type: 'circle',
    })),
    [drawRadiusAreas]
  );
  const isManualAreaMode = isManualAreaModeValue(areaMode);
  const hasClearableSelection =
    startingAddressCount > 0 ||
    drawAreaCount > 0 ||
    selectedFilterItems.length > 0;
  const canClearSelection = hasClearableSelection || hasStoredFindNewContactsDataState;
  const drawTargetAreaLabel = useMemo(() => {
    if (drawAreaCount === 0) return 'Draw area';

    return `${drawAreaCount} drawn area${drawAreaCount === 1 ? '' : 's'}`;
  }, [drawAreaCount]);
  const customShapeEstimateAreas = useMemo(
    () => customShapes
      .map((shape, index) => {
        const estimateArea = getShapeEstimateArea(shape);
        if (!estimateArea) return null;

        return {
          ...estimateArea,
          key: getCustomShapeEstimateKey(shape, index),
          deletePosition: {
            latitude: estimateArea.latitude,
            longitude: estimateArea.longitude,
          },
          labelPosition: {
            latitude: estimateArea.latitude,
            longitude: estimateArea.longitude,
          },
          orderKey: getCustomShapeOrderKey(shape),
          points: shape,
          sourceIndex: index,
          sourceType: 'custom',
          type: 'custom',
        };
      })
      .filter(Boolean),
    [customShapes]
  );
  const naturalManualEstimateAreas = useMemo(
    () => [
      ...selectedLocationEstimateAreas,
      ...drawRadiusEstimateAreas,
      ...customShapeEstimateAreas,
    ],
    [customShapeEstimateAreas, drawRadiusEstimateAreas, selectedLocationEstimateAreas]
  );
  const repairedManualAreaOrder = useMemo(
    () => repairManualAreaOrder(manualAreaOrder, naturalManualEstimateAreas),
    [manualAreaOrder, naturalManualEstimateAreas]
  );
  useEffect(() => {
    if (areManualAreaOrdersEqual(manualAreaOrder, repairedManualAreaOrder)) return;

    setManualAreaOrder(repairedManualAreaOrder);
  }, [manualAreaOrder, repairedManualAreaOrder]);
  const manualEstimateAreas = useMemo(
    () => orderManualEstimateAreas(naturalManualEstimateAreas, repairedManualAreaOrder),
    [naturalManualEstimateAreas, repairedManualAreaOrder]
  );
  const selectedManualArea = useMemo(
    () => manualEstimateAreas.find((area) => area.key === selectedManualAreaKey) || null,
    [manualEstimateAreas, selectedManualAreaKey]
  );
  const removeManualArea = useCallback((area) => {
    preserveSummaryAddressListScroll();

    if (area?.sourceType === 'location') {
      removeStartingAddressRef.current?.(area.sourceIndex);
      return;
    }

    suppressManualAreaAutoFitRef.current = true;

    if (area?.sourceType === 'circle') {
      setDrawRadiusAreas((currentAreas) =>
        currentAreas.filter((_, index) => index !== area.sourceIndex)
      );
    }

    if (area?.sourceType === 'custom') {
      setCustomShapes((currentShapes) =>
        currentShapes.filter((_, index) => index !== area.sourceIndex)
      );
    }

    if (area?.orderKey) {
      setManualAreaOrder((currentOrder) =>
        removeManualAreaOrderEntry(currentOrder, area.sourceType, area.orderKey)
      );
    }

    if (area?.key) {
      setManualAreaEstimateByKey((currentEstimates) => {
        if (!currentEstimates[area.key]) return currentEstimates;

        const nextEstimates = { ...currentEstimates };
        delete nextEstimates[area.key];
        manualAreaEstimateByKeyRef.current = nextEstimates;
        return nextEstimates;
      });
    }
  }, [preserveSummaryAddressListScroll]);
  const hasManualEstimateArea = useMemo(
    () => isManualAreaMode &&
      manualEstimateAreas.length > 0,
    [isManualAreaMode, manualEstimateAreas.length]
  );
  const isEstimatingManualArea = hasManualEstimateArea && estimateStatus === 'loading';
  const shouldBlockMapForManualEstimate =
    isEstimatingManualArea &&
    !isDrawingShape &&
    !isFindHomesAroundLocationsMode;
  const canUseAreaControls = canUseTargetMap && !hasReachedMaxSelectedMapLocations;
  const isMultiAddressAggregateRadiusMode =
    !isFindHomesAroundLocationsMode &&
    usesSharedAddressSettings &&
    isNearbyRadiusMode &&
    startingAddressCount > 1;
  const canContinue = useMemo(() => {
    const requiresTargetCount = isNearbyHomesMode;
    if (requiresTargetCount && !targetCount) return false;
    if (
      requiresTargetCount &&
      (Number(targetCount) < minimumTargetCount || Number(targetCount) > maximumTargetCount)
    ) return false;
    if (isNearbyRadiusMode && (!radiusMiles || Number(radiusMiles) <= 0)) return false;
    if (mapStatus !== 'ready') return false;
    if (!isFindHomesAroundLocationsMode && !mapAddress) return false;
    if (!hasSelectedTargetingMethod) return false;
    if (!isMultiAddressAggregateRadiusMode && (estimateStatus === 'loading' || estimateStatus === 'error')) return false;
    if (isNearbyRadiusMode) {
      return isMultiAddressAggregateRadiusMode || (estimateStatus === 'ready' && estimatedHomes !== null);
    }
    if (isManualAreaMode) {
      return (
        (isFindHomesAroundLocationsMode ? selectedMapLocationCount > 0 : drawAreaCount > 0) &&
        estimateStatus === 'ready' &&
        estimatedHomes !== null &&
        (!isFindHomesAroundLocationsMode || estimatedFilterKey === selectedEstimateKey)
      );
    }
    return Boolean(coordinates);
  }, [
    areaMode,
    coordinates,
    customShapeEstimateAreas.length,
    customShapes.length,
    drawAreaCount,
    drawRadiusAreas.length,
    drawRadiusEstimateAreas.length,
    estimatedHomes,
    estimatedFilterKey,
    estimateStatus,
    hasSelectedTargetingMethod,
    isMultiAddressAggregateRadiusMode,
    isManualAreaMode,
    isFindHomesAroundLocationsMode,
    isNearbyHomesMode,
    isNearbyRadiusMode,
    mapAddress,
    mapStatus,
    nearbyTargetMode,
    radiusMiles,
    selectedMapLocationCount,
    selectedEstimateKey,
    selectedFilterPayloadKey,
    targetCount,
  ]);

  const getFindNewContactsConfig = () => ({
    address: mapAddress || (isFindHomesAroundLocationsMode ? 'Selected map area' : ''),
    addresses: startingAddresses.length ? startingAddresses : mapAddress ? [mapAddress] : [],
    addressConfigs: listAddressConfigs.map((config) => ({
      ...config,
      filterLabels: getActiveFilterLabels(config.filters, {
        contactType: activeContactType,
        optionLabelsByKey: lookupOptionLabelsByKey,
        showAllOptions: true,
      }),
    })),
    areaMode,
    contactType: activeContactType,
    coordinates,
    customShapes,
    drawRadiusAreas,
    estimatedHomes,
    filterLabels: selectedFilterLabels,
    filters: selectedFilters,
    listName: purchaseListName.trim(),
    listCleanupOptions: normalizeListCleanupOptions({
      ...listCleanupOptions,
      excludeTargetAddressesFromMailingList: isFindHomesAroundAddressesMode
        ? listCleanupOptions.excludeTargetAddressesFromMailingList
        : false,
      removeDuplicateHomesAcrossAreas: startingAddressMode === 'multiple'
        ? true
        : false,
    }),
    manualAreaOrder: repairedManualAreaOrder,
    nearbyTargetMode,
    purchaseStatus: 'not_purchased',
    radiusMiles,
    selectedAddressIndex: activeAddressIndex,
    skipDataAxlePurchase: true,
    startingAddressMode,
    targetCount,
    totalSelectedHomes,
    usesSharedAddressSettings,
  });

  const getPurchasableHomesCount = () => {
    if (shouldUseAddressAggregateEstimate) {
      if (addressAggregateEstimateStatus !== 'ready' || addressAggregateEstimatedHomes === null) {
        return 0;
      }

      return isNearbyHomesMode
        ? Math.min(totalSelectedHomes, Number(addressAggregateEstimatedHomes || 0))
        : Number(addressAggregateEstimatedHomes || 0);
    }
    if (estimateStatus === 'loading') return 0;
    if (shouldHidePartialAddressEstimates) return 0;
    if (isNearbyHomesMode) return totalSelectedHomes > 0 ? totalSelectedHomes : 0;
    if (estimateStatus === 'error') return 0;
    if (hasLocationManualEstimate) return Number(estimatedHomes || 0);
    if (!startingAddressCount) return 0;

    return hasAnyEstimatedHomes ? Number(totalEstimatedHomes || 0) : 0;
  };

  const closePurchaseConfirmModal = () => {
    if (purchaseLoading) return;
    if (!purchaseAttemptedRef.current) {
      purchaseIdempotencyKeyRef.current = null;
    }
    setPurchaseTermsAccepted(false);
    setPurchaseConfirmModal((currentModal) => ({ ...currentModal, open: false }));
  };

  const confirmPurchase = async (actionOverride = pendingPurchaseAction) => {
    const purchaseAction = actionOverride || pendingPurchaseAction;
    const quantity = getPurchasableHomesCount();
    const listName = purchaseListName.trim();
    if (!quantity) {
      dispatch(failure('Select at least one record before purchasing this list.'));
      return;
    }
    if (!listName) {
      dispatch(failure('Name this mailing list before purchasing.'));
      return;
    }
    if (!purchaseTermsAccepted) {
      dispatch(failure('Agree to the End User Agreement before purchasing this list.'));
      return;
    }

    try {
      setPurchaseLoading(true);
      setPurchaseConfirmModal((currentModal) => ({ ...currentModal, loading: true }));
      if (!purchaseIdempotencyKeyRef.current) {
        purchaseIdempotencyKeyRef.current = createPurchaseIdempotencyKey();
      }
      purchaseAttemptedRef.current = true;

      const response = await request.post('find-new-contacts/purchase', {
        action: purchaseAction,
        config: {
          ...getFindNewContactsConfig(),
          listName,
        },
        listName,
        quantity,
        termsAccepted: purchaseTermsAccepted,
        costPerRecordCents,
      }, {
        headers: {
          'Idempotency-Key': purchaseIdempotencyKeyRef.current,
        },
      });

      const purchaseData = response?.data?.data || {};
      const purchasedConfig = purchaseData.config || {
        ...getFindNewContactsConfig(),
        purchaseStatus: 'purchased',
        skipDataAxlePurchase: false,
      };

      dispatch(success(response?.data?.message || 'Find New Contacts list purchased successfully.'));
      dispatch(getUserData());
      purchaseIdempotencyKeyRef.current = null;
      purchaseAttemptedRef.current = false;
      setPurchaseConfirmModal((currentModal) => ({ ...currentModal, open: false }));
      clearList();
      setPurchaseListName(getDefaultPurchaseListName(findNewMode));
      setPurchaseTermsAccepted(false);

      if (typeof onContinue === 'function') {
        onContinue(purchasedConfig, purchaseAction, purchaseData);
        return;
      }

      if (purchaseAction === 'exit' && typeof onCancel === 'function') {
        onCancel();
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.data?.message || '';
      const isPricingChanged = error?.response?.status === 409
        && errorMessage.toLowerCase().includes('pricing changed');
      if (isPricingChanged && typeof onPricingChanged === 'function') {
        await onPricingChanged();
        purchaseIdempotencyKeyRef.current = null;
        purchaseAttemptedRef.current = false;
        setPurchaseConfirmModal((currentModal) => ({ ...currentModal, open: false }));
      }
      dispatch(
        failure(errorMessage || 'Purchase failed. Please try again.')
      );
    } finally {
      setPurchaseLoading(false);
      setPurchaseConfirmModal((currentModal) => ({ ...currentModal, loading: false }));
    }
  };

  const openPurchaseConfirm = (action = 'continue_to_mail') => {
    if (
      !canContinueWithAddressEstimates ||
      hasUnappliedFilterChanges
    ) return;

    const quantity = getPurchasableHomesCount();
    if (!quantity) {
      dispatch(failure('Select at least one record before purchasing this list.'));
      return;
    }

    if (!purchaseIdempotencyKeyRef.current) {
      purchaseIdempotencyKeyRef.current = createPurchaseIdempotencyKey();
    }
    setPendingPurchaseAction(action);
    setPurchaseListName((currentListName) =>
      currentListName.trim() ? currentListName : getDefaultPurchaseListName(findNewMode)
    );
    setPurchaseTermsAccepted(false);
    setPurchaseConfirmModal({
      type: 'export',
      open: true,
      message: '',
      successButtonName: 'Purchase',
      loading: false,
      header: 'Confirm Purchase',
      icon: ConfirmPurchaseCIcon,
      className: 'purchaseConfirmModal findNewContactsPurchaseConfirmModal',
      modalName: 'purchaseConfirmModal',
      handleClose: closePurchaseConfirmModal,
      cancelButtonName: 'Cancel',
    });
  };
  const estimatedRecordsLabel = useMemo(() => {
    if (!mapAddress) return '-';
    if (!targetCount && isNearbyHomesMode) return 'Choose count';
    if (estimateStatus === 'loading') return '...';
    if (estimateStatus === 'error') return '-';
    if (estimateStatus === 'ready' && estimatedHomes !== null) return estimatedHomes;
    return '-';
  }, [estimateStatus, estimatedHomes, isNearbyHomesMode, mapAddress, targetCount]);
  const targetCountLabel = targetCount || 'Not selected';
  const activeAddressStoredConfig = mapAddress
    ? addressConfigs[getAddressKey(mapAddress)]
    : null;

  const currentAddressConfig = useMemo(() => getVisibleAddressConfig({
    dataAxlePlaceIds: activeAddressStoredConfig?.dataAxlePlaceIds || [],
    addressBounds,
    addressViewportBounds,
    areaMode,
    contactType: activeContactType,
    customRadiusMiles,
    customShapePoints: [],
    customShapes,
    customTargetCount,
    coordinates,
    drawRadiusAreas,
    drawRadiusPoints: [],
    estimatedHomes,
    estimatedFilterKey,
    estimatedRadiusMiles,
    filters: selectedFilters,
    isDataAxleConfigured,
    locationFilter,
    manualAreaOrder: repairedManualAreaOrder,
    nearbyModeSettings,
    nearbyTargetMode,
    placeId,
    placeTypes,
    radiusMiles,
    targetCount,
  }, startingAddressMode, {
    contactType: activeContactType,
    preserveManualAreas: isFindHomesAroundLocationsMode,
  }), [
    addressBounds,
    addressViewportBounds,
    activeAddressStoredConfig,
    activeContactType,
    areaMode,
    customRadiusMiles,
    customShapes,
    customTargetCount,
    coordinates,
    drawRadiusAreas,
    estimatedFilterKey,
    estimatedHomes,
    estimatedRadiusMiles,
    isDataAxleConfigured,
    locationFilter,
    nearbyModeSettings,
    nearbyTargetMode,
    repairedManualAreaOrder,
    placeId,
    placeTypes,
    radiusMiles,
    selectedFilters,
    isFindHomesAroundLocationsMode,
    startingAddressMode,
    targetCount,
  ]);
  const listAddressConfigs = useMemo(() => {
    const activeAddressKey = mapAddress ? getAddressKey(mapAddress) : '';

    return startingAddresses.map((startingAddress) => {
      const addressKey = getAddressKey(startingAddress);
      const addressConfig = addressKey === activeAddressKey
        ? currentAddressConfig
        : addressConfigs[addressKey];
      const visibleConfig = usesSharedAddressSettings
        ? applySharedMultipleAddressConfig(
            addressConfig,
            currentAddressConfig,
            {
              preserveEstimate: addressKey === activeAddressKey,
              preserveManualAreas: isFindHomesAroundLocationsMode,
            }
          )
        : getVisibleAddressConfig(
            addressConfig,
            startingAddressMode,
            { preserveManualAreas: isFindHomesAroundLocationsMode }
          );
      const estimateResult = addressEstimateResultByKey[addressKey];
      const shouldUseEstimateResult =
        isNearbyRadiusMode &&
        estimateResult &&
        estimateResult.estimatedFilterKey === selectedEstimateKey &&
        Number(estimateResult.estimatedRadiusMiles) === normalizeRadiusMiles(radiusMiles);
      const visibleConfigWithEstimate = shouldUseEstimateResult
        ? {
            ...visibleConfig,
            estimatedFilterKey: estimateResult.estimatedFilterKey,
            estimatedHomes: estimateResult.estimatedHomes,
            estimatedRadiusMiles: estimateResult.estimatedRadiusMiles,
            estimateStatus: estimateResult.estimateStatus,
            isDataAxleConfigured: estimateResult.isDataAxleConfigured,
          }
        : visibleConfig;

      return {
        address: startingAddress,
        ...visibleConfigWithEstimate,
      };
    });
  }, [
    addressConfigs,
    addressEstimateResultByKey,
    currentAddressConfig,
    isFindHomesAroundLocationsMode,
    isNearbyRadiusMode,
    mapAddress,
    radiusMiles,
    selectedEstimateKey,
    selectedFilterPayloadKey,
    startingAddresses,
    startingAddressMode,
    usesSharedAddressSettings,
  ]);
  const shouldUseAddressAggregateEstimate =
    (
      !isFindHomesAroundLocationsMode &&
      usesSharedAddressSettings &&
      isNearbyRadiusMode &&
      listAddressConfigs.length > 1
    ) || (
      isNearbyHomesMode &&
      hasAppliedFilters &&
      listAddressConfigs.length > 0
    );
  const multipleAddressRadiusEstimateConfigs = useMemo(() => {
    const normalizedRadius = Number(radiusMiles);
    const normalizedSelectedRadius = normalizeRadiusMiles(radiusMiles);
    if (
      isContactTypeFilterSyncPending ||
      !usesSharedAddressSettings ||
      !isNearbyRadiusMode ||
      mapStatus !== 'ready' ||
      !Number.isFinite(normalizedRadius) ||
      normalizedRadius <= 0 ||
      listAddressConfigs.length <= 1
    ) return [];

    return listAddressConfigs
      .map((config) => {
        const addressKey = getAddressKey(config.address);
        const coordinates = normalizeCoordinates(config.coordinates);

        return {
          ...config,
          addressKey,
          coordinates,
          estimateRequestKey: coordinates ? JSON.stringify({
            addressKey,
            ...(shouldExcludeStartingAddresses ? { excludeTargetAddresses: true } : {}),
            dataAxlePlaceIds: normalizeDataAxlePlaceIds(config.dataAxlePlaceIds),
            filters: selectedFilterPayloadKey,
            latitude: getRoundedCoordinate(coordinates.latitude),
            longitude: getRoundedCoordinate(coordinates.longitude),
            radiusMiles: normalizedSelectedRadius,
          }) : '',
        };
      })
      .filter((config) => {
        const status = addressEstimateStatusByKey[config.addressKey] || config.estimateStatus;
        const pendingEstimateRequestKey = pendingAddressEstimateRequestKeysRef.current.get(config.addressKey);

        return (
          config.addressKey &&
          status !== 'error' &&
          !(status === 'loading' && retryingAddressEstimateKeysRef.current.has(config.addressKey)) &&
          pendingEstimateRequestKey !== config.estimateRequestKey &&
          config.coordinates &&
          (
            config.estimatedHomes === null ||
            Number(config.estimatedRadiusMiles) !== normalizedSelectedRadius ||
            config.estimatedFilterKey !== selectedEstimateKey
          )
        );
      });
  }, [
    addressEstimateStatusByKey,
    isContactTypeFilterSyncPending,
    isNearbyRadiusMode,
    listAddressConfigs,
    mapStatus,
    radiusMiles,
    selectedEstimateKey,
    selectedFilterPayloadKey,
    shouldExcludeStartingAddresses,
    usesSharedAddressSettings,
  ]);
  const multipleAddressRadiusEstimateKey = useMemo(() => {
    if (!multipleAddressRadiusEstimateConfigs.length) return '';

    return JSON.stringify({
      ...(shouldExcludeStartingAddresses ? { excludeTargetAddresses: true } : {}),
      filters: selectedFilterPayloadKey,
      radiusMiles: normalizeRadiusMiles(radiusMiles),
      requests: multipleAddressRadiusEstimateConfigs.map((config) => ({
        addressKey: config.addressKey,
        dataAxlePlaceIds: normalizeDataAxlePlaceIds(config.dataAxlePlaceIds),
        latitude: getRoundedCoordinate(config.coordinates.latitude),
        longitude: getRoundedCoordinate(config.coordinates.longitude),
      })),
    });
  }, [
    multipleAddressRadiusEstimateConfigs,
    radiusMiles,
    selectedFilterPayloadKey,
    shouldExcludeStartingAddresses,
  ]);
  const multipleAddressEstimateInputKey = useMemo(() => JSON.stringify({
    contactType: activeContactType,
    ...(shouldExcludeStartingAddresses ? { excludeTargetAddresses: true } : {}),
    filters: selectedFilterPayloadKey,
    radiusMiles: normalizeRadiusMiles(radiusMiles),
    addresses: listAddressConfigs.map((config) => {
      const coordinates = normalizeCoordinates(config.coordinates);

      return {
        addressKey: getAddressKey(config.address),
        dataAxlePlaceIds: normalizeDataAxlePlaceIds(config.dataAxlePlaceIds),
        latitude: coordinates ? getRoundedCoordinate(coordinates.latitude) : null,
        longitude: coordinates ? getRoundedCoordinate(coordinates.longitude) : null,
      };
    }),
  }), [
    activeContactType,
    listAddressConfigs,
    radiusMiles,
    selectedFilterPayloadKey,
    shouldExcludeStartingAddresses,
  ]);
  multipleAddressEstimateInputKeyRef.current = multipleAddressEstimateInputKey;
  const isRadiusEstimatePending =
    isWaitingForFirstBulkAddressEstimate ||
    addressAggregateEstimateStatus === 'loading';
  const pendingAddressEstimateKeys = useMemo(
    () => new Set(
      Object.entries(addressEstimateStatusByKey)
        .filter(([, status]) => status === 'loading')
        .map(([addressKey]) => addressKey)
    ),
    [addressEstimateStatusByKey]
  );
  const failedAddressEstimateKeys = useMemo(
    () => new Set(
      Object.entries(addressEstimateStatusByKey)
        .filter(([, status]) => status === 'error')
        .map(([addressKey]) => addressKey)
    ),
    [addressEstimateStatusByKey]
  );
  const selectedRadiusMiles = Number(radiusMiles);
  const hasCurrentRadiusEstimate = useCallback((config = {}) => {
    if (!isNearbyRadiusMode) return config.estimatedHomes !== null;
    if (!Number.isFinite(selectedRadiusMiles) || selectedRadiusMiles <= 0) return false;

    return (
      config.estimatedHomes !== null &&
      Number(config.estimatedRadiusMiles) === normalizeRadiusMiles(radiusMiles) &&
      config.estimatedFilterKey === selectedEstimateKey
    );
  }, [isNearbyRadiusMode, radiusMiles, selectedEstimateKey, selectedRadiusMiles]);
  const addressAggregateRadiusAreas = useMemo(() => {
    const normalizedRadius = isNearbyHomesMode
      ? defaultHomesSearchRadiusMiles
      : Number(radiusMiles);
    if (
      isContactTypeFilterSyncPending ||
      !shouldUseAddressAggregateEstimate ||
      mapStatus !== 'ready' ||
      !Number.isFinite(normalizedRadius) ||
      normalizedRadius <= 0
    ) return [];

    const normalizedSelectedRadius = isNearbyHomesMode
      ? defaultHomesSearchRadiusMiles
      : normalizeRadiusMiles(radiusMiles);

    return listAddressConfigs
      .map((config) => {
        if (isNearbyHomesMode && config.locationFilter) {
          return {
            address: config.address,
            locationFilter: config.locationFilter,
            type: 'location',
          };
        }

        const normalizedCoordinates = normalizeCoordinates(config.coordinates);
        if (!normalizedCoordinates) return null;

        return {
          address: config.address,
          dataAxlePlaceIds: normalizeDataAxlePlaceIds(config.dataAxlePlaceIds),
          latitude: normalizedCoordinates.latitude,
          longitude: normalizedCoordinates.longitude,
          radiusMiles: normalizedSelectedRadius,
          type: 'radius',
        };
      })
      .filter(Boolean);
  }, [
    isContactTypeFilterSyncPending,
    isNearbyHomesMode,
    listAddressConfigs,
    mapStatus,
    radiusMiles,
    shouldUseAddressAggregateEstimate,
  ]);
  const addressAggregateEstimateKey = useMemo(() => {
    if (
      isContactTypeFilterSyncPending ||
      !shouldUseAddressAggregateEstimate ||
      addressAggregateRadiusAreas.length !== listAddressConfigs.length
    ) return '';

    return JSON.stringify({
      ...(shouldExcludeStartingAddresses ? { excludeTargetAddresses: true } : {}),
      filters: selectedFilterPayloadKey,
      radiusMiles: isNearbyHomesMode
        ? defaultHomesSearchRadiusMiles
        : normalizeRadiusMiles(radiusMiles),
      areas: addressAggregateRadiusAreas.map((area) => ({
        address: area.address,
        dataAxlePlaceIds: normalizeDataAxlePlaceIds(area.dataAxlePlaceIds),
        latitude: area.latitude === undefined ? null : getRoundedCoordinate(area.latitude),
        locationFilter: area.locationFilter || null,
        longitude: area.longitude === undefined ? null : getRoundedCoordinate(area.longitude),
      })),
    });
  }, [
    addressAggregateRadiusAreas,
    isContactTypeFilterSyncPending,
    isNearbyHomesMode,
    listAddressConfigs.length,
    radiusMiles,
    selectedFilterPayloadKey,
    shouldExcludeStartingAddresses,
    shouldUseAddressAggregateEstimate,
  ]);
  const addressAggregateRequestAreas = useMemo(() => {
    if (!addressAggregateEstimateKey) {
      addressAggregateRequestAreasRef.current = { areas: [], key: '' };
      return [];
    }

    if (addressAggregateRequestAreasRef.current.key === addressAggregateEstimateKey) {
      return addressAggregateRequestAreasRef.current.areas;
    }

    const areas = addressAggregateRadiusAreas.map((area) => ({
      address: area.address,
      latitude: area.latitude,
      longitude: area.longitude,
      radiusMiles: area.radiusMiles,
      type: 'radius',
    }));

    addressAggregateRequestAreasRef.current = {
      areas,
      key: addressAggregateEstimateKey,
    };
    return areas;
  }, [addressAggregateEstimateKey, addressAggregateRadiusAreas]);
  const totalSelectedHomes = useMemo(
    () => listAddressConfigs.reduce((sum, config) => sum + getSelectedHomesCount(config), 0),
    [listAddressConfigs]
  );
  const totalEstimatedHomes = useMemo(
    () => listAddressConfigs.reduce((sum, config) => {
      if (isNearbyRadiusMode && !hasCurrentRadiusEstimate(config)) return sum;

      const estimate = Number(config?.estimatedHomes);

      return sum + (Number.isFinite(estimate) && estimate >= 0 ? estimate : 0);
    }, 0),
    [hasCurrentRadiusEstimate, isNearbyRadiusMode, listAddressConfigs]
  );
  const hasAnyEstimatedHomes = useMemo(
    () => listAddressConfigs.some((config) => hasCurrentRadiusEstimate(config)),
    [hasCurrentRadiusEstimate, listAddressConfigs]
  );
  const hasAllAddressEstimates = useMemo(() => {
    if (!listAddressConfigs.length) return false;

    return listAddressConfigs.every((config) => {
      return hasCurrentRadiusEstimate(config);
    });
  }, [hasCurrentRadiusEstimate, listAddressConfigs]);
  const selectedTargetCount = Number(targetCount);
  const hasSelectedNearbyTarget =
    (isNearbyRadiusMode && Number.isFinite(selectedRadiusMiles) && selectedRadiusMiles > 0) ||
    (
      isNearbyHomesMode &&
      Number.isFinite(selectedTargetCount) &&
      selectedTargetCount >= minimumTargetCount &&
      selectedTargetCount <= maximumTargetCount
    );
  const shouldHidePartialAddressEstimates =
    usesSharedAddressSettings &&
    isNearbyRadiusMode &&
    hasSelectedNearbyTarget &&
    startingAddressCount > 0 &&
    !shouldUseAddressAggregateEstimate &&
    !hasAllAddressEstimates;
  const hasAddressAggregateEstimate =
    !shouldUseAddressAggregateEstimate ||
    (
      addressAggregateEstimateStatus === 'ready' &&
      addressAggregateEstimatedHomes !== null
    );
  const hasRequiredAddressTotals =
    !shouldUseAddressAggregateEstimate ||
    isNearbyHomesMode ||
    hasAllAddressEstimates;
  const canContinueWithAddressEstimates =
    canContinue &&
    hasAddressAggregateEstimate &&
    hasRequiredAddressTotals;
  const hasLocationManualAreaSelection =
    isFindHomesAroundLocationsMode && isManualAreaMode && selectedMapLocationCount > 0;
  const hasCurrentLocationManualEstimate =
    hasLocationManualAreaSelection && estimatedFilterKey === selectedEstimateKey;
  const hasLocationManualEstimate = hasCurrentLocationManualEstimate && estimatedHomes !== null;
  const hasPendingLocationManualAreaEstimate = useMemo(() => {
    if (!hasLocationManualAreaSelection) return false;

    return manualEstimateAreas.some((area) => {
      const estimate = manualAreaEstimateByKey[area.key];

      return !estimate || estimate.filterKey !== selectedEstimateKey || estimate.status === 'loading';
    });
  }, [
    hasLocationManualAreaSelection,
    manualAreaEstimateByKey,
    manualEstimateAreas,
    selectedEstimateKey,
  ]);
  const manualAllAreasHomesCount = useMemo(() => {
    if (!isManualAreaMode || manualEstimateAreas.length < 2) return null;

    return manualEstimateAreas.reduce((sum, area) => {
      if (sum === null) return null;

      const estimate = parseNullableNumber(manualAreaEstimateByKey[area.key]?.estimatedHomes);

      return estimate === null ? null : sum + estimate;
    }, 0);
  }, [isManualAreaMode, manualAreaEstimateByKey, manualEstimateAreas]);
  const summaryLocationItems = useMemo(() => {
    if (!isFindHomesAroundLocationsMode || !isManualAreaMode) return [];

    return manualEstimateAreas.map((area, index) => {
      const storedEstimate = manualAreaEstimateByKey[area.key];
      const estimate = storedEstimate?.filterKey === selectedEstimateKey
        ? storedEstimate
        : { estimatedHomes: null, status: 'loading' };
      const estimateLabel = getManualAreaEstimateLabel(estimate);
      const recordsLabel = estimateLabel === '-'
        ? ''
        : estimateLabel === '...'
          ? ''
          : getRecordsSummaryLabel(estimateLabel);
      const title = area.sourceType === 'location'
        ? getDisplayAddress(area.address) || 'Selected location'
        : area.sourceType === 'circle'
          ? 'Circle area'
          : 'Freeform area';

      return {
        area,
        number: index + 1,
        recordsLabel,
        recordsStatus: estimate.status || 'pending',
        title,
      };
    });
  }, [
    isFindHomesAroundLocationsMode,
    isManualAreaMode,
    manualAreaEstimateByKey,
    manualEstimateAreas,
    selectedEstimateKey,
  ]);
  useEffect(() => {
    const listElement = summaryAddressListRef.current;
    if (!listElement) return undefined;

    const preservedScrollTop = summaryAddressPreservedScrollTopRef.current;
    if (preservedScrollTop !== null) {
      summaryAddressPreservedScrollTopRef.current = null;

      const firstFrameId = window.requestAnimationFrame(() => {
        const secondFrameId = window.requestAnimationFrame(() => {
          const currentListElement = summaryAddressListRef.current;
          if (!currentListElement) return;

          const maxScrollTop = Math.max(
            currentListElement.scrollHeight - currentListElement.clientHeight,
            0
          );
          currentListElement.scrollTop = Math.min(preservedScrollTop, maxScrollTop);
        });
        summaryAddressAutoScrollFrameRef.current = secondFrameId;
      });
      summaryAddressAutoScrollFrameRef.current = firstFrameId;

      return () => {
        if (summaryAddressAutoScrollFrameRef.current) {
          window.cancelAnimationFrame(summaryAddressAutoScrollFrameRef.current);
          summaryAddressAutoScrollFrameRef.current = null;
        }
      };
    }

    const scrollSelectedSummaryItemIntoView = () => {
      const selectedElement = listElement.querySelector('.summaryAddressItem.selected');
      if (!selectedElement) return;

      const summaryItems = Array.from(listElement.querySelectorAll('.summaryAddressItem'));
      const selectedIndex = summaryItems.indexOf(selectedElement);
      const isLastSelectedItem = selectedIndex === summaryItems.length - 1;

      if (isLastSelectedItem) {
        listElement.scrollTop = listElement.scrollHeight;
      } else {
        const listRect = listElement.getBoundingClientRect();
        const selectedRect = selectedElement.getBoundingClientRect();

        if (selectedRect.bottom > listRect.bottom) {
          listElement.scrollTop += selectedRect.bottom - listRect.bottom;
        } else if (selectedRect.top < listRect.top) {
          listElement.scrollTop -= listRect.top - selectedRect.top;
        }
      }

    };

    const firstFrameId = window.requestAnimationFrame(() => {
      const secondFrameId = window.requestAnimationFrame(scrollSelectedSummaryItemIntoView);
      summaryAddressAutoScrollFrameRef.current = secondFrameId;
    });
    summaryAddressAutoScrollFrameRef.current = firstFrameId;

    return () => {
      if (summaryAddressAutoScrollFrameRef.current) {
        window.cancelAnimationFrame(summaryAddressAutoScrollFrameRef.current);
        summaryAddressAutoScrollFrameRef.current = null;
      }
    };
  }, [
    activeAddressIndex,
    listAddressConfigs.length,
    selectedManualAreaKey,
    summaryLocationItems.length,
  ]);
  const focusManualAreaOnMap = useCallback((area) => {
    const map = googleMapRef.current;
    const maps = window.google?.maps;
    if (!area) return;

    setSelectedManualAreaKey(area.key || '');
    if (!map || !maps) return;

    const bounds = new maps.LatLngBounds();
    if (area.sourceType === 'custom') {
      extendBoundsForShape(bounds, maps, customShapes[area.sourceIndex]);
    } else if (area.sourceType === 'location') {
      const areaBounds = normalizeAddressBounds(area.addressBounds);

      if (areaBounds) {
        bounds.extend(new maps.LatLng(areaBounds.north, areaBounds.east));
        bounds.extend(new maps.LatLng(areaBounds.south, areaBounds.west));
      } else if (area.labelPosition) {
        bounds.extend(new maps.LatLng(area.labelPosition.latitude, area.labelPosition.longitude));
      }
    } else {
      extendBoundsForRadiusArea(bounds, maps, {
        center: {
          latitude: area.latitude,
          longitude: area.longitude,
        },
        radiusMiles: area.radiusMiles || minimumRadiusMiles,
      });
    }

    const focusMap = () => {
      maps.event?.trigger?.(map, 'resize');
      if (!bounds.isEmpty()) {
        map.fitBounds(bounds, 88);
        return;
      }

      if (area.labelPosition) {
        map.panTo({
          lat: area.labelPosition.latitude,
          lng: area.labelPosition.longitude,
        });
      }
    };

    focusMap();
    window.requestAnimationFrame(focusMap);
  }, [customShapes]);
  const summaryHomesCount = useMemo(() => {
    if (shouldUseAddressAggregateEstimate) {
      if (addressAggregateEstimateStatus !== 'ready' || addressAggregateEstimatedHomes === null) {
        return null;
      }

      return isNearbyHomesMode
        ? Math.min(totalSelectedHomes, addressAggregateEstimatedHomes)
        : addressAggregateEstimatedHomes;
    }
    if (estimateStatus === 'loading') return null;
    if (shouldHidePartialAddressEstimates) return null;
    if (isNearbyHomesMode) return totalSelectedHomes > 0 ? totalSelectedHomes : null;
    if (estimateStatus === 'error') return null;
    if (hasLocationManualEstimate) return estimatedHomes;
    if (!startingAddressCount) return null;

    return hasAnyEstimatedHomes ? totalEstimatedHomes : null;
  }, [
    addressAggregateEstimatedHomes,
    addressAggregateEstimateStatus,
    estimatedHomes,
    estimateStatus,
    hasLocationManualEstimate,
    isNearbyHomesMode,
    hasAnyEstimatedHomes,
    shouldHidePartialAddressEstimates,
    shouldUseAddressAggregateEstimate,
    startingAddressCount,
    totalEstimatedHomes,
    totalSelectedHomes,
  ]);
  const summaryPrimaryLabel = contactTypeCopy.uniqueTitle;
  const hasAddressEstimateErrors = failedAddressEstimateKeys.size > 0;
  const isSummaryHomesLoading =
    shouldUseAddressAggregateEstimate
      ? Boolean(addressAggregateEstimateKey) &&
        (addressAggregateEstimateStatus === 'idle' || addressAggregateEstimateStatus === 'loading')
      : (hasPendingLocationManualAreaEstimate && !hasLocationManualEstimate) ||
        estimateStatus === 'loading' ||
        (shouldHidePartialAddressEstimates && !hasAddressEstimateErrors);
  const summaryPrimaryValue = useMemo(() => {
    if (isSummaryHomesLoading) return '';
    if (!shouldUseAddressAggregateEstimate && estimateStatus === 'error') return '-';
    if (summaryHomesCount === null) return '-';

    return formatNumber(summaryHomesCount);
  }, [
    estimateStatus,
    isSummaryHomesLoading,
    shouldUseAddressAggregateEstimate,
    summaryHomesCount,
  ]);
  const summaryTotalCostValue = summaryHomesCount === null
    ? '-'
    : formatCurrency(summaryHomesCount * costPerRecord);
  const summaryStatValueStyle = useMemo(() => ({
    '--summary-stat-value-font-size': `${getSummaryStatValueFontSize(
      summaryPrimaryValue,
      summaryTotalCostValue
    )}px`,
  }), [summaryPrimaryValue, summaryTotalCostValue]);
  const summaryAllAreasHomesCount = useMemo(() => {
    if (summaryHomesCount === null) return null;
    if (shouldUseAddressAggregateEstimate) {
      if (isNearbyHomesMode) return null;
      return hasAllAddressEstimates ? totalEstimatedHomes : null;
    }
    if (isManualAreaMode && manualAllAreasHomesCount !== null) {
      return manualAllAreasHomesCount;
    }

    return null;
  }, [
    hasAllAddressEstimates,
    isManualAreaMode,
    isNearbyHomesMode,
    manualAllAreasHomesCount,
    shouldUseAddressAggregateEstimate,
    summaryHomesCount,
    totalEstimatedHomes,
  ]);
  const summaryDuplicatesText = useMemo(() => {
    if (summaryAllAreasHomesCount === null || summaryHomesCount === null) return '';

    const duplicatesRemovedCount = summaryAllAreasHomesCount - summaryHomesCount;
    if (duplicatesRemovedCount <= 0) return '';

    return `${formatNumber(summaryAllAreasHomesCount)} across all areas · ${formatNumber(duplicatesRemovedCount)} duplicates removed`;
  }, [summaryAllAreasHomesCount, summaryHomesCount]);
  const costPerRecordLabel = `${formatCurrency(costPerRecord)} per record`;
  const activeAddressRecordLabel =
    isFindHomesAroundLocationsMode && isManualAreaMode
      ? summaryHomesCount !== null
        ? getRecordsSummaryLabel(summaryHomesCount)
        : estimateStatus === 'loading'
          ? ''
          : 'Pending'
      : shouldHidePartialAddressEstimates
        ? 'Pending'
        : isNearbyHomesMode && targetCount
        ? getRecordsSummaryLabel(getSelectedHomesCount(currentAddressConfig))
        : estimateStatus === 'ready' && estimatedHomes !== null
          ? getRecordsSummaryLabel(getSelectedHomesCount(currentAddressConfig))
          : 'Pending';
  const getAddressSummaryRecordMeta = useCallback((config, index) => {
    const isActiveAddress = index === activeAddressIndex;
    const selectedCount = getSelectedHomesCount(config);
    const addressKey = getAddressKey(config.address);
    const addressEstimateStatus = addressEstimateStatusByKey[addressKey] || config.estimateStatus;

    if (config.areaMode === 'radius' && config.nearbyTargetMode === 'homes') {
      if (!config.targetCount) return { label: 'Choose count', status: 'pending' };

      return { label: getRecordsSummaryLabel(selectedCount), status: 'ready' };
    }

    if (config.areaMode === 'radius' && config.nearbyTargetMode === 'radius') {
      const hasCurrentEstimate = hasCurrentRadiusEstimate(config);

      if (isActiveAddress && estimateStatus === 'loading') return { label: '', status: 'loading' };
      if (hasCurrentEstimate) return { label: getRecordsSummaryLabel(selectedCount), status: 'ready' };
      if (isActiveAddress && estimateStatus === 'error') return { label: '', status: 'error' };
      if (addressEstimateStatus === 'loading' || pendingAddressEstimateKeys.has(addressKey)) return { label: '', status: 'loading' };
      if (addressEstimateStatus === 'error' || failedAddressEstimateKeys.has(addressKey)) return { label: '', status: 'error' };
      if (shouldHidePartialAddressEstimates) return { label: '', status: 'loading' };

      return { label: 'Pending', status: 'pending' };
    }

    if (isManualAreaModeValue(config.areaMode)) {
      if (isActiveAddress && estimateStatus === 'loading') return { label: '', status: 'loading' };
      if (config.estimatedHomes !== null) return { label: getRecordsSummaryLabel(selectedCount), status: 'ready' };
      if (isActiveAddress && estimateStatus === 'error') return { label: '', status: 'error' };

      return { label: 'Pending', status: 'pending' };
    }

    if (isActiveAddress && activeAddressRecordLabel !== '') {
      return { label: activeAddressRecordLabel, status: activeAddressRecordLabel === 'Pending' ? 'pending' : 'ready' };
    }

    if (isActiveAddress && estimateStatus === 'loading') return { label: '', status: 'loading' };

    return { label: 'Pending', status: 'pending' };
  }, [
    activeAddressIndex,
    activeAddressRecordLabel,
    addressEstimateStatusByKey,
    estimateStatus,
    failedAddressEstimateKeys,
    hasCurrentRadiusEstimate,
    pendingAddressEstimateKeys,
    shouldHidePartialAddressEstimates,
  ]);
  const summarySelectionLabel = areaMode === 'radius'
    ? nearbyTargetMode === 'radius'
      ? radiusMiles
        ? `${radiusMiles} miles radius`
        : 'Choose distance'
      : nearbyTargetMode === 'homes'
        ? isMultipleAddressMode
          ? `${targetCountLabel} ${contactTypeCopy.plural} per address`
          : `${targetCountLabel} ${contactTypeCopy.plural} around address`
        : 'Choose method'
    : drawAreaCount > 0
      ? drawTargetAreaLabel
      : 'Draw an area';
  const summaryRows = [
    {
      label: isFindHomesAroundLocationsMode ? 'Locations' : 'Addresses',
      value: isFindHomesAroundLocationsMode
        ? selectedMapLocationCount
          ? `${formatNumber(selectedMapLocationCount)} location${selectedMapLocationCount === 1 ? '' : 's'}`
          : 'None yet'
        : startingAddressCount
          ? `${formatNumber(startingAddressCount)} ${inputEntityLabel}${startingAddressCount === 1 ? '' : 's'}`
          : 'None yet',
    },
    {
      label: 'Targeting',
      value: summarySelectionLabel,
    },
    {
      label: 'Filters',
      value: selectedFilterLabels.length
        ? `${formatNumber(selectedFilterLabels.length)} filter${selectedFilterLabels.length === 1 ? '' : 's'}`
        : 'No filters',
    },
  ];

  const clearBoundaryFeatureLayer = useCallback(() => {
    if (boundaryFeatureRetryRef.current.listener) {
      boundaryFeatureRetryRef.current.listener.remove?.();
    }
    boundaryFeatureRetryRef.current.timeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
    boundaryFeatureRetryRef.current = { listener: null, timeouts: [] };

    boundaryFeatureLayerRefs.current.forEach((featureLayer) => {
      featureLayer.style = null;
    });
    boundaryFeatureLayerRefs.current = [];
    setIsGoogleBoundaryVisible(false);
  }, []);

  const clearAreaOverlays = useCallback(() => {
    clearBoundaryFeatureLayer();
    if (addressBoundaryRef.current) {
      addressBoundaryRef.current.setMap(null);
      addressBoundaryRef.current = null;
    }
    if (radiusCircleRef.current) {
      radiusCircleRef.current.setMap(null);
      radiusCircleRef.current = null;
    }
    drawRadiusCircleRefs.current.forEach((circle) => circle.setMap(null));
    drawRadiusCircleRefs.current = [];
    if (drawRadiusPreviewCircleRef.current) {
      drawRadiusPreviewCircleRef.current.setMap(null);
      drawRadiusPreviewCircleRef.current = null;
    }
    manualAreaDeleteMarkerRefs.current.forEach(clearMapMarker);
    manualAreaDeleteMarkerRefs.current = [];
    clearMapMarker(selectedManualAreaMarkerRef.current);
    selectedManualAreaMarkerRef.current = null;
    customPolygonRefs.current.forEach((polygon) => polygon.setMap(null));
    customPolygonRefs.current = [];
    if (customPolylineRef.current) customPolylineRef.current.setMap(null);
    customPointMarkerRefs.current.forEach(clearMapMarker);
    radiusPointMarkerRefs.current.forEach(clearMapMarker);
    customPointMarkerRefs.current = [];
    radiusPointMarkerRefs.current = [];
  }, [clearBoundaryFeatureLayer]);

  const applyAddressConfig = useCallback((config, addressKey = '') => {
    const cachedEstimate = addressKey ? addressEstimateResultByKey[addressKey] : null;
    const shouldUseCachedEstimate =
      isNearbyRadiusMode &&
      cachedEstimate &&
      cachedEstimate.estimatedFilterKey === selectedEstimateKey &&
      Number(cachedEstimate.estimatedRadiusMiles) === normalizeRadiusMiles(config?.radiusMiles || radiusMiles);
    const nextConfig = sanitizeAddressConfig(
      shouldUseCachedEstimate
        ? {
            ...config,
            estimatedFilterKey: cachedEstimate.estimatedFilterKey,
            estimatedHomes: cachedEstimate.estimatedHomes,
            estimatedRadiusMiles: cachedEstimate.estimatedRadiusMiles,
            estimateStatus: cachedEstimate.estimateStatus,
            isDataAxleConfigured: cachedEstimate.isDataAxleConfigured,
          }
        : config,
      activeContactType
    );
    const nextLocationAreaMode = ['custom', 'draw-radius'].includes(nextConfig.areaMode)
      ? nextConfig.areaMode
      : 'custom';
    const nextFilters = rememberFiltersForContactType(activeContactType, nextConfig.filters);

    clearAreaOverlays();
    setTargetCount(nextConfig.targetCount);
    setCustomTargetCount(nextConfig.customTargetCount);
    setRadiusMiles(nextConfig.radiusMiles);
    setCustomRadiusMiles(nextConfig.customRadiusMiles);
    setAreaMode(isFindHomesAroundLocationsMode ? nextLocationAreaMode : 'radius');
    setNearbyTargetMode(nextConfig.nearbyTargetMode);
    setIsDrawingShape(false);
    setCustomShapes(nextConfig.customShapes.map((shape) => [...shape]));
    setCustomShapePoints(nextConfig.customShapePoints);
    setDrawRadiusAreas(nextConfig.drawRadiusAreas.map((area) => ({ ...area })));
    setDrawRadiusPoints(nextConfig.drawRadiusPoints);
    if (!isFindHomesAroundLocationsMode) {
      setManualAreaOrder(nextConfig.manualAreaOrder);
    }
    setSelectedFilters(nextFilters);
    setFilterDraft(nextFilters);
    setShowFilters(false);
    setAddressBounds(nextConfig.addressBounds);
    setAddressViewportBounds(nextConfig.addressViewportBounds);
    setCoordinates(nextConfig.coordinates);
    setEstimatedFilterKey(nextConfig.estimatedFilterKey);
    setEstimatedHomes(nextConfig.estimatedHomes);
    setEstimatedRadiusMiles(nextConfig.estimatedRadiusMiles);
    setIsDataAxleConfigured(nextConfig.isDataAxleConfigured);
    setLocationFilter(nextConfig.locationFilter);
    setNearbyModeSettings(nextConfig.nearbyModeSettings);
    setPlaceId(nextConfig.placeId);
    setPlaceTypes(nextConfig.placeTypes);
    setEstimateStatus(nextConfig.estimateStatus === 'ready' ? 'ready' : 'idle');
    setEstimateError('');
  }, [
    activeContactType,
    addressEstimateResultByKey,
    clearAreaOverlays,
    isFindHomesAroundLocationsMode,
    isNearbyRadiusMode,
    radiusMiles,
    rememberFiltersForContactType,
    selectedEstimateKey,
    selectedFilterPayloadKey,
  ]);

  useEffect(() => {
    if (!isFindHomesAroundAddressesMode || addressEntrySource !== 'contacts') return;

    dispatch(
      fetchContacts({
        pagination: DEFAULT_PAGINATION,
        isReturnAddress: false,
        excludeDncList: true,
      })
    );
    dispatch(fetchContactLabels());
  }, [addressEntrySource, dispatch, isFindHomesAroundAddressesMode]);

  useEffect(() => {
    if (!isFindHomesAroundAddressesMode || addressEntrySource !== 'contacts') {
      isInitialContactSearchRef.current = true;
      return;
    }

    if (isInitialContactSearchRef.current) {
      isInitialContactSearchRef.current = false;
      return;
    }

    debouncedFetchContacts(contactSearch);
  }, [addressEntrySource, contactSearch, debouncedFetchContacts, isFindHomesAroundAddressesMode]);

  useEffect(() => {
    if (!isFindHomesAroundAddressesMode || addressEntrySource !== 'contacts') {
      isInitialTagSearchRef.current = true;
      return;
    }

    if (isInitialTagSearchRef.current) {
      isInitialTagSearchRef.current = false;
      return;
    }

    debouncedFetchTags(tagSearch);
  }, [addressEntrySource, debouncedFetchTags, isFindHomesAroundAddressesMode, tagSearch]);

  useEffect(() => {
    if (!selectedContactIds.length) return;

    setSelectedContactsCache((currentCache) => {
      const nextCache = { ...currentCache };
      let didUpdate = false;

      selectedContactIds.forEach((contactId) => {
        const foundContact = contacts.find((contact) => String(contact.id) === String(contactId));
        if (foundContact && nextCache[String(contactId)] !== foundContact) {
          nextCache[String(contactId)] = foundContact;
          didUpdate = true;
        }
      });

      return didUpdate ? nextCache : currentCache;
    });
  }, [contacts, selectedContactIds]);

  useEffect(() => {
    setSelectedContactsCache((currentCache) => {
      const selectedIds = new Set(selectedContactIds.map(String));
      const nextCache = {};
      let didUpdate = false;

      Object.keys(currentCache).forEach((id) => {
        if (selectedIds.has(id)) {
          nextCache[id] = currentCache[id];
        } else {
          didUpdate = true;
        }
      });

      return didUpdate ? nextCache : currentCache;
    });
  }, [selectedContactIds]);

  useEffect(() => {
    if (!isFindHomesAroundAddressesMode || !startingAddresses.length) return;

    const contactsById = new Map(contacts.map((contact) => [String(contact.id), contact]));
    Object.values(selectedContactsCache).forEach((contact) => {
      if (contact?.id) contactsById.set(String(contact.id), contact);
    });

    const contactAddressMetadataByKey = Array.from(contactsById.values()).reduce((metadata, contact) => (
      mergeAddressMetadata(metadata, buildContactAddress(contact), getContactDataAxlePlaceIds(contact))
    ), {});

    if (!Object.keys(contactAddressMetadataByKey).length) return;

    setAddressConfigs((currentConfigs) => {
      let didUpdate = false;
      const nextConfigs = { ...currentConfigs };

      startingAddresses.forEach((startingAddress) => {
        const addressKey = getAddressKey(startingAddress);
        if (!addressKey) return;

        const dataAxlePlaceIds = getDataAxlePlaceIdsForAddress(contactAddressMetadataByKey, startingAddress);
        if (!dataAxlePlaceIds.length) return;

        const currentConfig = currentConfigs[addressKey] || createDefaultAddressConfig(activeContactType);
        const currentDataAxlePlaceIds = normalizeDataAxlePlaceIds(currentConfig.dataAxlePlaceIds);
        const nextDataAxlePlaceIds = [...new Set([...currentDataAxlePlaceIds, ...dataAxlePlaceIds])];
        if (nextDataAxlePlaceIds.length === currentDataAxlePlaceIds.length) return;

        nextConfigs[addressKey] = sanitizeAddressConfig(clearAddressEstimateState({
          ...currentConfig,
          dataAxlePlaceIds: nextDataAxlePlaceIds,
        }), activeContactType);
        didUpdate = true;
      });

      return didUpdate ? nextConfigs : currentConfigs;
    });
  }, [
    activeContactType,
    contacts,
    isFindHomesAroundAddressesMode,
    selectedContactsCache,
    startingAddresses,
  ]);

  useEffect(() => {
    if (!selectedTagIds.length) return;

    setSelectedTagsCache((currentCache) => {
      const nextCache = { ...currentCache };
      let didUpdate = false;

      selectedTagIds.forEach((tagId) => {
        const foundTag = contactLabels.find((tag) => String(tag.id) === String(tagId));
        if (foundTag && nextCache[String(tagId)] !== foundTag) {
          nextCache[String(tagId)] = foundTag;
          didUpdate = true;
        }
      });

      return didUpdate ? nextCache : currentCache;
    });
  }, [contactLabels, selectedTagIds]);

  useEffect(() => {
    setSelectedTagsCache((currentCache) => {
      const selectedIds = new Set(selectedTagIds.map(String));
      const nextCache = {};
      let didUpdate = false;

      Object.keys(currentCache).forEach((id) => {
        if (selectedIds.has(id)) {
          nextCache[id] = currentCache[id];
        } else {
          didUpdate = true;
        }
      });

      return didUpdate ? nextCache : currentCache;
    });
  }, [selectedTagIds]);

  useEffect(() => {
    if (!selectedTagIds.length) {
      setSelectedTagContactsCount({
        addressTotal: 0,
        isLoaded: false,
        key: '',
        total: 0,
      });
      return;
    }

    let didCancel = false;
    const controller = new AbortController();
    const currentTagIds = selectedTagIdsKey.split(',').filter(Boolean);
    const currentAddressKeys = startingAddresses.map(getAddressKey);
    const currentKey = `${selectedTagIdsKey}|${currentAddressKeys.join('|')}`;

    setSelectedTagContactsCount((currentCount) =>
      currentCount.key === currentKey && currentCount.isLoaded
        ? currentCount
        : {
            addressTotal: 0,
            isLoaded: false,
            key: currentKey,
            total: 0,
          }
    );

    const fetchSelectedTagContactsCount = async () => {
      try {
        const tagContacts = [];
        let page = 1;
        let totalTagContacts = null;
        let lastPage = null;

        while (tagContacts.length < maxSelectedAddresses) {
          const response = await request.get(
            '/contacts',
            {
              page,
              pageSize: 500,
              tags: `[${currentTagIds.join(',')}]`,
            },
            controller.signal
          );
          const data = response?.data?.data || {};
          const rows = Array.isArray(data.rows) ? data.rows : [];
          const rawTotal = data.count ?? data.total ?? data.totalRecordsInDB;
          const nextLastPage = parsePositiveInteger(data.lastPage ?? data.last_page);

          tagContacts.push(...rows);
          if (rawTotal !== undefined && rawTotal !== null) totalTagContacts = Number(rawTotal);
          if (nextLastPage) lastPage = nextLastPage;

          if (!rows.length) break;
          if (Number.isFinite(totalTagContacts) && tagContacts.length >= totalTagContacts) break;
          if (lastPage && page >= lastPage) break;

          page += 1;
        }

        const existingAddressKeys = new Set(currentAddressKeys.map(getAddressMatchKey));
        const remainingSlots = Math.max(maxSelectedAddresses - currentAddressKeys.length, 0);
        const addressTotal = getUniqueAddresses(tagContacts.map(buildContactAddress).filter(Boolean))
          .filter((addressValue) => !existingAddressKeys.has(getAddressMatchKey(addressValue)))
          .slice(0, remainingSlots).length;
        const count = Number.isFinite(totalTagContacts) ? totalTagContacts : tagContacts.length;

        if (!didCancel) {
          setSelectedTagContactsCount({
            addressTotal,
            isLoaded: true,
            key: currentKey,
            total: count || 0,
          });
        }
      } catch (error) {
        if (isCanceledRequest(error)) return;

        if (!didCancel) {
          setSelectedTagContactsCount({
            addressTotal: 0,
            isLoaded: true,
            key: currentKey,
            total: 0,
          });
        }
      }
    };

    fetchSelectedTagContactsCount();

    return () => {
      didCancel = true;
      controller.abort();
    };
  }, [selectedTagIdsKey, startingAddresses]);

  useEffect(() => {
    if (!mapAddress) return;

    const activeAddressKey = getAddressKey(mapAddress);
    const currentConfigForStorage = clearAddressEstimateState(currentAddressConfig);
    setAddressConfigs((currentConfigs) => {
      if (usesSharedAddressSettings) {
        const nextConfigs = syncMultipleAddressConfigs(
          startingAddresses,
          currentConfigs,
          activeAddressKey,
          currentConfigForStorage,
          { preserveManualAreas: isFindHomesAroundLocationsMode }
        );

        return areAddressConfigsEqual(currentConfigs, nextConfigs) ? currentConfigs : nextConfigs;
      }

      const savedConfig = currentConfigs[activeAddressKey];
      if (areAddressConfigsEqual(savedConfig, currentConfigForStorage)) return currentConfigs;

      return {
        ...currentConfigs,
        [activeAddressKey]: currentConfigForStorage,
      };
    });
  }, [
    currentAddressConfig,
    isFindHomesAroundLocationsMode,
    mapAddress,
    startingAddresses,
    usesSharedAddressSettings,
  ]);

  useEffect(() => {
    if (settingsDraftDebounceRef.current) {
      window.clearTimeout(settingsDraftDebounceRef.current);
    }

    settingsDraftDebounceRef.current = window.setTimeout(() => {
      settingsDraftDebounceRef.current = null;
      const activeAddressKey = mapAddress ? getAddressKey(mapAddress) : '';
      const currentConfigForDraft = clearAddressEstimateState(currentAddressConfig);
      const nextAddressConfigs = activeAddressKey && usesSharedAddressSettings
        ? syncMultipleAddressConfigs(
            startingAddresses,
            addressConfigs,
            activeAddressKey,
            currentConfigForDraft,
            { preserveManualAreas: isFindHomesAroundLocationsMode }
          )
        : activeAddressKey
          ? {
              ...addressConfigs,
              [activeAddressKey]: currentConfigForDraft,
            }
          : addressConfigs;

      writeSettingsDraft({
        addressConfigs: nextAddressConfigs,
        areaMode,
        contactType: activeContactType,
        customRadiusMiles,
        customShapes,
        drawRadiusAreas,
        filtersByContactType: filtersByContactTypeRef.current,
        listCleanupOptions,
        manualAreaOrder,
        selectedAddressIndex: activeAddressIndex,
        startingAddressMode,
        startingAddresses,
      }, settingsDraftScope);
      setHasStoredFindNewContactsDataState(hasStoredFindNewContactsData(settingsDraftScope));
    }, 750);

    return () => {
      if (!settingsDraftDebounceRef.current) return;
      window.clearTimeout(settingsDraftDebounceRef.current);
      settingsDraftDebounceRef.current = null;
    };
  }, [
    activeAddressIndex,
    activeContactType,
    addressConfigs,
    areaMode,
    currentAddressConfig,
    customRadiusMiles,
    customShapes,
    drawRadiusAreas,
    isFindHomesAroundLocationsMode,
    listCleanupOptions,
    manualAreaOrder,
    mapAddress,
    settingsDraftScope,
    startingAddressMode,
    startingAddresses,
    usesSharedAddressSettings,
  ]);

  const requestRadiusEstimate = useCallback(async (searchRadiusMiles, signal) => {
    const normalizedRadius = normalizeRadiusMiles(searchRadiusMiles);

    if (!coordinates) throw new Error('Missing starting address coordinates.');

    const response = await withTimeout(
      request.post('find-new-contacts/neighborhood-estimate', {
        address: mapAddress,
        addressConfigs: [{ ...currentAddressConfig, address: mapAddress }],
        contactType: activeContactType,
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
        radiusMiles: normalizedRadius,
        filters: selectedFilterPayload,
        ...estimateCleanupPayload,
      }, signal ? { signal } : undefined),
      estimateRequestTimeoutMs,
      'Estimate request timed out.'
    );
    const estimatedCount = parseNullableNumber(response.data?.data?.estimatedHomes);

    if (estimatedCount === null) {
      logNeighborhoodEstimateError('Response did not include estimatedHomes.', {
        address: mapAddress,
        status: response.status,
        responseData: response.data,
      });
      throw new Error('Could not load contact estimate.');
    }

    const estimate = {
      dataAxleConfigured: response.data?.data?.dataAxleConfigured !== false,
      estimatedHomes: estimatedCount,
      radiusMiles: normalizedRadius,
    };

    return estimate;
  }, [activeContactType, coordinates, currentAddressConfig, estimateCleanupPayload, mapAddress, selectedFilterPayload]);

  const requestAddressRadiusEstimate = useCallback(async (config, searchRadiusMiles = radiusMiles) => {
    const normalizedRadius = normalizeRadiusMiles(searchRadiusMiles);
    const normalizedCoordinates = normalizeCoordinates(config?.coordinates);

    if (!normalizedCoordinates) throw new Error('Missing starting address coordinates.');

    const response = await withTimeout(
      request.post('find-new-contacts/neighborhood-estimate', {
        address: config.address,
        addressConfigs: [config],
        contactType: activeContactType,
        latitude: normalizedCoordinates.latitude,
        longitude: normalizedCoordinates.longitude,
        radiusMiles: normalizedRadius,
        filters: selectedFilterPayload,
        ...estimateCleanupPayload,
      }),
      estimateRequestTimeoutMs,
      'Estimate request timed out.'
    );
    const estimatedCount = parseNullableNumber(response.data?.data?.estimatedHomes);

    if (estimatedCount === null) {
      logNeighborhoodEstimateError('Response did not include estimatedHomes.', {
        address: config.address,
        status: response.status,
        responseData: response.data,
      });
      throw new Error('Could not load contact estimate.');
    }

    return {
      dataAxleConfigured: response.data?.data?.dataAxleConfigured !== false,
      estimatedHomes: estimatedCount,
      radiusMiles: normalizedRadius,
    };
  }, [activeContactType, estimateCleanupPayload, radiusMiles, selectedFilterPayload]);

  const retryStartingAddressEstimate = useCallback((config, index) => {
    const addressKey = getAddressKey(config?.address);
    if (!addressKey) return;

    const requestConfig = {
      ...config,
      radiusMiles: config?.radiusMiles || radiusMiles,
    };
    const isActiveAddress = index === activeAddressIndex;

    retryingAddressEstimateKeysRef.current.add(addressKey);

    setAddressEstimateStatusByKey((currentStatuses) => ({
      ...currentStatuses,
      [addressKey]: 'loading',
    }));
    setAddressEstimateResultByKey((currentResults) => {
      if (!currentResults[addressKey]) return currentResults;

      const nextResults = { ...currentResults };
      delete nextResults[addressKey];
      return nextResults;
    });

    if (isActiveAddress) {
      radiusEstimateRunRef.current += 1;
      setEstimatedHomes(null);
      setEstimatedFilterKey('');
      setEstimatedRadiusMiles(null);
      setEstimateStatus('loading');
      setEstimateError('');
    }

    requestAddressRadiusEstimate(requestConfig, requestConfig.radiusMiles)
      .then((estimate) => {
        retryingAddressEstimateKeysRef.current.delete(addressKey);
        setAddressEstimateStatusByKey((currentStatuses) => {
          if (currentStatuses[addressKey] !== 'loading') return currentStatuses;

          const nextStatuses = { ...currentStatuses };
          delete nextStatuses[addressKey];
          return nextStatuses;
        });
        setAddressEstimateResultByKey((currentResults) => {
          const nextResult = {
            estimatedFilterKey: selectedEstimateKey,
            estimatedHomes: estimate.estimatedHomes,
            estimateStatus: 'ready',
            estimatedRadiusMiles: estimate.radiusMiles,
            isDataAxleConfigured: estimate.dataAxleConfigured !== false,
          };
          const currentResult = currentResults[addressKey];
          const isSameEstimate =
            currentResult?.estimatedFilterKey === nextResult.estimatedFilterKey &&
            currentResult?.estimatedHomes === nextResult.estimatedHomes &&
            currentResult?.estimateStatus === nextResult.estimateStatus &&
            Number(currentResult?.estimatedRadiusMiles) === Number(nextResult.estimatedRadiusMiles) &&
            currentResult?.isDataAxleConfigured === nextResult.isDataAxleConfigured;

          if (isSameEstimate) return currentResults;

          return {
            ...currentResults,
            [addressKey]: nextResult,
          };
        });

        if (isActiveAddress) {
          setEstimatedHomes(estimate.estimatedHomes);
          setEstimatedFilterKey(selectedEstimateKey);
          setEstimatedRadiusMiles(estimate.radiusMiles);
          setIsDataAxleConfigured(estimate.dataAxleConfigured !== false);
          setEstimateStatus('ready');
          setEstimateError('');
        }
      })
      .catch((error) => {
        retryingAddressEstimateKeysRef.current.delete(addressKey);
        logNeighborhoodEstimateError('Retry request failed.', {
          address: requestConfig.address,
          error: error?.message,
          responseData: error?.response?.data,
          status: error?.response?.status,
        });
        setAddressEstimateStatusByKey((currentStatuses) => ({
          ...currentStatuses,
          [addressKey]: 'error',
        }));
        setAddressEstimateResultByKey((currentResults) => {
          if (!currentResults[addressKey]) return currentResults;

          const nextResults = { ...currentResults };
          delete nextResults[addressKey];
          return nextResults;
        });

        if (isActiveAddress) {
          setEstimatedHomes(null);
          setEstimatedFilterKey('');
          setEstimatedRadiusMiles(null);
          setEstimateStatus('error');
          setEstimateError('Could not load contact estimate.');
        }
        notifyEstimateRequestFailure();
      });
  }, [
    activeAddressIndex,
    estimateCleanupPayload,
    notifyEstimateRequestFailure,
    radiusMiles,
    requestAddressRadiusEstimate,
    selectedFilterPayloadKey,
  ]);

  const buildManualAreaRetryPayload = useCallback((area) => {
    const estimateAddress = mapAddress || (isFindHomesAroundLocationsMode ? 'Selected map area' : '');
    const basePayload = {
      address: area.address || estimateAddress,
      contactType: activeContactType,
      filters: selectedFilterPayload,
      ...estimateCleanupPayload,
    };
    const requestArea = getManualAreaRequestArea(area);

    if (requestArea.type === 'location' && requestArea.locationFilter) {
      return {
        ...basePayload,
        locationFilter: requestArea.locationFilter,
      };
    }

    if (requestArea.type === 'polygon') {
      return {
        ...basePayload,
        area: requestArea,
      };
    }

    return {
      ...basePayload,
      area: requestArea,
      latitude: requestArea.latitude,
      longitude: requestArea.longitude,
      radiusMiles: requestArea.radiusMiles,
    };
  }, [
    activeContactType,
    estimateCleanupPayload,
    isFindHomesAroundLocationsMode,
    mapAddress,
    selectedFilterPayload,
  ]);

  const refreshManualAggregateEstimate = useCallback((nextEstimatesByKey = manualAreaEstimateByKeyRef.current) => {
    const estimateAddress = mapAddress || (isFindHomesAroundLocationsMode ? 'Selected map area' : '');
    const areas = manualEstimateAreas.map(getManualAreaRequestArea).filter(Boolean);

    if (!isManualAreaMode || !estimateAddress || areas.length === 0) {
      manualAggregateEstimateRunRef.current += 1;
      if (manualAggregateEstimateRequestRef.current.timeoutId) {
        window.clearTimeout(manualAggregateEstimateRequestRef.current.timeoutId);
      }
      manualAggregateEstimateRequestRef.current = { key: '', promise: null, timeoutId: null };
      manualAggregateEstimateResolvedKeyRef.current = '';
      setEstimatedFilterKey('');
      return Promise.resolve(null);
    }

    if (areas.length === 1) {
      if (manualAggregateEstimateRequestRef.current.timeoutId) {
        window.clearTimeout(manualAggregateEstimateRequestRef.current.timeoutId);
      }
      manualAggregateEstimateRequestRef.current = { key: '', promise: null, timeoutId: null };
      manualAggregateEstimateResolvedKeyRef.current = '';
      const singleArea = manualEstimateAreas[0];
      const singleEstimate = nextEstimatesByKey[singleArea?.key];
      const estimatedCount = parseNullableNumber(singleEstimate?.estimatedHomes);

      if (singleEstimate?.status === 'error') {
        manualAggregateEstimateRunRef.current += 1;
        setEstimatedHomes(null);
        setEstimatedFilterKey('');
        setEstimatedRadiusMiles(null);
        setEstimateStatus('error');
        setEstimateError('Could not load contact estimate.');
        return Promise.resolve(null);
      }

      if (singleEstimate?.status === 'ready' && estimatedCount !== null) {
        manualAggregateEstimateRunRef.current += 1;
        setEstimatedHomes(estimatedCount);
        setEstimatedFilterKey(selectedEstimateKey);
        setEstimatedRadiusMiles(null);
        setIsDataAxleConfigured(singleEstimate.dataAxleConfigured !== false);
        setEstimateStatus('ready');
        setEstimateError('');
        return Promise.resolve(estimatedCount);
      }

      manualAggregateEstimateRunRef.current += 1;
      setEstimatedHomes(null);
      setEstimatedFilterKey('');
      setEstimatedRadiusMiles(null);
      setEstimateStatus('loading');
      setEstimateError('');
      return Promise.resolve(null);
    }

    const areaEstimates = manualEstimateAreas.map((area) => nextEstimatesByKey[area.key]);

    const aggregateRequestKey = stableStringify({
      address: estimateAddress,
      areas,
      contactType: activeContactType,
      ...(shouldExcludeStartingAddresses ? { excludeTargetAddresses: true } : {}),
      filters: selectedFilterPayloadKey,
    });

    if (manualAggregateEstimateRequestRef.current.key === aggregateRequestKey) {
      return manualAggregateEstimateRequestRef.current.promise || Promise.resolve(null);
    }

    if (manualAggregateEstimateRequestRef.current.timeoutId) {
      window.clearTimeout(manualAggregateEstimateRequestRef.current.timeoutId);
    }

    if (manualAggregateEstimateResolvedKeyRef.current === aggregateRequestKey) {
      return Promise.resolve(null);
    }

    const aggregateRunId = manualAggregateEstimateRunRef.current + 1;
    manualAggregateEstimateRunRef.current = aggregateRunId;
    setEstimatedHomes(null);
    setEstimatedFilterKey('');
    setEstimatedRadiusMiles(null);
    setEstimateStatus('loading');
    setEstimateError('');

    const aggregatePromise = withTimeout(
      request.post('find-new-contacts/neighborhood-estimate', {
        address: estimateAddress,
        areas,
        contactType: activeContactType,
        filters: selectedFilterPayload,
        ...estimateCleanupPayload,
      }),
      estimateRequestTimeoutMs,
      'Estimate request timed out.'
    )
      .then((response) => {
        if (manualAggregateEstimateRunRef.current !== aggregateRunId) return null;

        const estimatedCount = parseNullableNumber(response.data?.data?.estimatedHomes);
        if (estimatedCount === null) {
          logNeighborhoodEstimateError('Manual aggregate estimate response did not include estimatedHomes.', {
            address: estimateAddress,
            responseData: response.data,
            status: response.status,
          });
          throw new Error('Manual aggregate estimate response did not include estimatedHomes.');
        }

        setEstimatedHomes(estimatedCount);
        setEstimatedFilterKey(selectedEstimateKey);
        setEstimatedRadiusMiles(null);
        setIsDataAxleConfigured(
          response.data?.data?.dataAxleConfigured !== false &&
            areaEstimates.every((estimate) => estimate?.dataAxleConfigured !== false)
        );
        setEstimateStatus('ready');
        setEstimateError('');
        manualAggregateEstimateResolvedKeyRef.current = aggregateRequestKey;

        return estimatedCount;
      })
      .catch((error) => {
        if (manualAggregateEstimateRunRef.current !== aggregateRunId) return null;

        logNeighborhoodEstimateError('Manual aggregate estimate request failed.', {
          address: estimateAddress,
          error: error?.message,
          responseData: error?.response?.data,
          status: error?.response?.status,
        });
        setEstimatedHomes(null);
        setEstimatedFilterKey('');
        setEstimatedRadiusMiles(null);
        setEstimateStatus('error');
        setEstimateError('Could not load contact estimate.');
        notifyEstimateRequestFailure();

        return null;
      })
      .finally(() => {
        if (manualAggregateEstimateRequestRef.current.key === aggregateRequestKey) {
          manualAggregateEstimateRequestRef.current = { key: '', promise: null, timeoutId: null };
        }
      });

    manualAggregateEstimateRequestRef.current = {
      key: aggregateRequestKey,
      promise: aggregatePromise,
      timeoutId: null,
    };

    return aggregatePromise;
  }, [
    activeContactType,
    isFindHomesAroundLocationsMode,
    isManualAreaMode,
    manualEstimateAreas,
    mapAddress,
    notifyEstimateRequestFailure,
    estimateCleanupPayload,
    selectedFilterPayload,
    selectedFilterPayloadKey,
    shouldExcludeStartingAddresses,
  ]);

  const retryManualAreaEstimate = useCallback((area) => {
    if (!area?.key) return;

    retryingManualAreaEstimateKeysRef.current.add(area.key);

    const loadingEstimatesByKey = {
      ...manualAreaEstimateByKeyRef.current,
      [area.key]: {
        dataAxleConfigured: true,
        estimatedHomes: null,
        filterKey: selectedEstimateKey,
        status: 'loading',
      },
    };

    setManualAreaEstimateByKey(loadingEstimatesByKey);
    manualAreaEstimateByKeyRef.current = loadingEstimatesByKey;
    setEstimateStatus('loading');
    setEstimateError('');

    withTimeout(
      request.post('find-new-contacts/neighborhood-estimate', buildManualAreaRetryPayload(area)),
      estimateRequestTimeoutMs,
      'Estimate request timed out.'
    )
      .then((response) => {
        const estimatedCount = parseNullableNumber(response.data?.data?.estimatedHomes);
        if (estimatedCount === null) {
          logNeighborhoodEstimateError('Response did not include estimatedHomes.', {
            address: area.address,
            areaKey: area.key,
            status: response.status,
            responseData: response.data,
          });
          notifyEstimateRequestFailure();
        }
        const nextEstimate = {
          dataAxleConfigured: response.data?.data?.dataAxleConfigured !== false,
          estimatedHomes: estimatedCount,
          filterKey: selectedEstimateKey,
          status: estimatedCount !== null ? 'ready' : 'error',
        };
        const nextEstimatesByKey = {
          ...manualAreaEstimateByKeyRef.current,
          [area.key]: nextEstimate,
        };

        setManualAreaEstimateByKey(nextEstimatesByKey);
        manualAreaEstimateByKeyRef.current = nextEstimatesByKey;
        retryingManualAreaEstimateKeysRef.current.delete(area.key);
        refreshManualAggregateEstimate(nextEstimatesByKey);
      })
      .catch((error) => {
          logNeighborhoodEstimateError('Manual area retry request failed.', {
            address: area.address,
            areaKey: area.key,
            error: error?.message,
            responseData: error?.response?.data,
            status: error?.response?.status,
          });
          const validationMessage = getNeighborhoodEstimateValidationMessage(error);
          if (validationMessage) dispatch(failure(validationMessage));
        const nextEstimatesByKey = {
          ...manualAreaEstimateByKeyRef.current,
          [area.key]: {
            dataAxleConfigured: true,
            estimatedHomes: null,
            filterKey: selectedEstimateKey,
            status: 'error',
          },
        };

        setManualAreaEstimateByKey(nextEstimatesByKey);
        manualAreaEstimateByKeyRef.current = nextEstimatesByKey;
        retryingManualAreaEstimateKeysRef.current.delete(area.key);
        refreshManualAggregateEstimate(nextEstimatesByKey);
        notifyEstimateRequestFailure();
      });
  }, [
    buildManualAreaRetryPayload,
    notifyEstimateRequestFailure,
    refreshManualAggregateEstimate,
    selectedFilterPayloadKey,
  ]);

  const handleTargetCountSelect = (count) => {
    if (!canUseTargetingMethods) return;

    if (areaMode !== 'radius' || nearbyTargetMode !== 'homes') {
      handleNearbyTargetModeSelect('homes');
    }
    const isSelectedPreset = !customTargetCount && Number(targetCount) === count;

    if (isSelectedPreset) {
      setCustomTargetCount('');
      setTargetCount('');
      setNearbyModeSettings((currentSettings) =>
        updateNearbyModeSettings(currentSettings, {
          customTargetCount: '',
          nearbyTargetMode: 'homes',
          targetCount: '',
        })
      );
      setEstimatedHomes(null);
      setEstimatedRadiusMiles(null);
      setEstimateStatus('idle');
      return;
    }

    setCustomTargetCount('');
    setTargetCount(count);
    setEstimatedHomes(null);
    setEstimatedRadiusMiles(null);
    setEstimateStatus('idle');
    setEstimateError('');
    setNearbyModeSettings((currentSettings) =>
      updateNearbyModeSettings(currentSettings, {
        customTargetCount: '',
        nearbyTargetMode: 'homes',
        targetCount: count,
      })
    );
  };

  const handleCustomTargetCountChange = (event) => {
    if (!canUseTargetingMethods) return;

    const inputValue = event.target.value;
    if (areaMode !== 'radius' || nearbyTargetMode !== 'homes') {
      handleNearbyTargetModeSelect('homes');
    }
    setCustomTargetCount(inputValue);

    if (inputValue === '') {
      setTargetCount('');
    }
  };

  const commitCustomTargetCount = useCallback(() => {
    if (!canUseTargetingMethods) return;

    const nextTargetCount = Number(customTargetCount);

    if (!Number.isFinite(nextTargetCount) || nextTargetCount <= 0) {
      setCustomTargetCount('');
      setTargetCount('');
      setNearbyModeSettings((currentSettings) =>
        updateNearbyModeSettings(currentSettings, {
          customTargetCount: '',
          nearbyTargetMode: 'homes',
          targetCount: '',
        })
      );
      return;
    }

    const safeTargetCount = Math.min(Math.max(Math.floor(nextTargetCount), minimumTargetCount), maximumTargetCount);
    const safeTargetCountLabel = String(safeTargetCount);

    if (Number(targetCount) === safeTargetCount && customTargetCount === safeTargetCountLabel) return;

    if (areaMode === 'radius') setNearbyTargetMode('homes');
    setCustomTargetCount(safeTargetCountLabel);
    setTargetCount(safeTargetCount);
    setEstimatedHomes(null);
    setEstimatedRadiusMiles(null);
    setEstimateStatus('idle');
    setEstimateError('');
    setNearbyModeSettings((currentSettings) =>
      updateNearbyModeSettings(currentSettings, {
        customTargetCount: safeTargetCountLabel,
        nearbyTargetMode: 'homes',
        targetCount: safeTargetCount,
      })
    );
  }, [canUseTargetingMethods, customTargetCount, targetCount]);

  const handleCustomTargetCountKeyDown = (event) => {
    if (event.key !== 'Enter') return;

    event.preventDefault();
    event.currentTarget.blur();
  };

  useEffect(() => {
    if (!customTargetCount) return undefined;

    const timeoutId = window.setTimeout(commitCustomTargetCount, customTargetDebounceMs);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [commitCustomTargetCount, customTargetCount]);

  const focusAddressInput = useCallback(() => {
    if (addressFocusTimeoutRef.current) {
      window.clearTimeout(addressFocusTimeoutRef.current);
    }
    setIsAddressInputSpotlighted(true);
    window.requestAnimationFrame(() => {
      addressInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      addressInputRef.current?.focus();
      addressFocusTimeoutRef.current = window.setTimeout(() => {
        setIsAddressInputSpotlighted(false);
      }, 1600);
    });
  }, []);

  useEffect(() => () => {
    if (addressFocusTimeoutRef.current) {
      window.clearTimeout(addressFocusTimeoutRef.current);
    }
  }, []);

  const addStartingAddressValue = useCallback((value, placeCoordinates = null, placeBounds = null, placeDetails = {}) => {
    const nextAddress = normalizeAddress(value);
    if (!nextAddress) return;

    const nextCoordinates = normalizeCoordinates(placeCoordinates);
    const nextAddressBounds = normalizeAddressBounds(placeBounds);
    const nextAddressViewportBounds = normalizeAddressBounds(
      placeDetails.viewportBounds || placeDetails.viewport || placeBounds
    );
    const nextPlaceId = typeof placeDetails.placeId === 'string' ? placeDetails.placeId : '';
    const nextPlaceTypes = normalizePlaceTypes(placeDetails.placeTypes);
    const nextLocationFilter = placeDetails.locationFilter ||
      getPlaceLocationFilter({
        addressComponents: placeDetails.addressComponents,
        types: nextPlaceTypes,
      });
    const nextAddressKey = getAddressKey(nextAddress);
    const currentAddressKey = mapAddress ? getAddressKey(mapAddress) : '';
    const existingIndex = startingAddresses.findIndex((currentAddress) =>
      isMatchingStartingAddress(currentAddress, nextAddress, {
        addressConfigs,
        nextPlaceId,
      })
    );
    const existingAddress = existingIndex >= 0 ? startingAddresses[existingIndex] : '';
    const selectedAddressKey = existingAddress ? getAddressKey(existingAddress) : nextAddressKey;

    if (
      existingIndex < 0 &&
      (hasReachedMaxSelectedAddresses ||
        (isFindHomesAroundLocationsMode && selectedMapLocationCount >= maxSelectedMapLocations))
    ) {
      setAddress('');
      return;
    }

    if (nextCoordinates) {
      pendingResolvedAddressRef.current = {
        addressBounds: nextAddressBounds,
        addressViewportBounds: nextAddressViewportBounds,
        coordinates: nextCoordinates,
        key: selectedAddressKey,
        locationFilter: nextLocationFilter,
        placeId: nextPlaceId,
        placeTypes: nextPlaceTypes,
        rawBounds: placeBounds,
      };
    }

    const baseSelectedAddressConfig = getVisibleAddressConfig(
      nextCoordinates || nextAddressBounds || nextPlaceId
        ? {
            ...(addressConfigs[selectedAddressKey] || createDefaultAddressConfig()),
            addressBounds: nextAddressBounds || addressConfigs[selectedAddressKey]?.addressBounds || null,
            addressViewportBounds:
              nextAddressViewportBounds || addressConfigs[selectedAddressKey]?.addressViewportBounds || null,
            coordinates: nextCoordinates,
            locationFilter: nextLocationFilter || addressConfigs[selectedAddressKey]?.locationFilter || null,
            placeId: nextPlaceId || addressConfigs[selectedAddressKey]?.placeId || '',
            placeTypes: nextPlaceTypes.length ? nextPlaceTypes : addressConfigs[selectedAddressKey]?.placeTypes || [],
          }
        : addressConfigs[selectedAddressKey] || createDefaultAddressConfig(),
      startingAddressMode,
      { preserveManualAreas: isFindHomesAroundLocationsMode }
    );
    const selectedAddressConfig = usesSharedAddressSettings
      ? applySharedMultipleAddressConfig(
          baseSelectedAddressConfig,
          currentAddressConfig,
          { preserveManualAreas: isFindHomesAroundLocationsMode }
        )
      : baseSelectedAddressConfig;

    if (startingAddressMode === 'single' && !isFindHomesAroundAddressesMode) {
      setStartingAddresses([nextAddress]);
      setSelectedAddressIndex(0);
      if (isFindHomesAroundLocationsMode) {
        setSelectedManualAreaKey(
          getLocationEstimateAreaKey(selectedAddressKey, selectedAddressConfig.locationFilter)
        );
      }
      setAddressConfigs((currentConfigs) => ({
        [selectedAddressKey]: nextCoordinates
          ? sanitizeAddressConfig({
              ...(currentConfigs[selectedAddressKey] || selectedAddressConfig),
              addressBounds: nextAddressBounds || currentConfigs[selectedAddressKey]?.addressBounds || null,
              addressViewportBounds:
                nextAddressViewportBounds || currentConfigs[selectedAddressKey]?.addressViewportBounds || null,
              coordinates: nextCoordinates,
              locationFilter: nextLocationFilter || currentConfigs[selectedAddressKey]?.locationFilter || null,
              placeId: nextPlaceId || currentConfigs[selectedAddressKey]?.placeId || '',
              placeTypes: nextPlaceTypes.length ? nextPlaceTypes : currentConfigs[selectedAddressKey]?.placeTypes || [],
            })
          : currentConfigs[selectedAddressKey] || selectedAddressConfig,
      }));
      applyAddressConfig(selectedAddressConfig, selectedAddressKey);
      if (isFindHomesAroundLocationsMode) {
        setManualAreaOrder((currentOrder) =>
          appendManualAreaOrderEntry(currentOrder, 'location', selectedAddressKey)
        );
      }
      setAddress('');
      return;
    }

    const nextAddresses = existingIndex >= 0
      ? startingAddresses
      : [...startingAddresses, nextAddress];
    const seededAddressConfig = nextCoordinates
      ? (usesSharedAddressSettings
        ? applySharedMultipleAddressConfig({
            ...(addressConfigs[selectedAddressKey] || selectedAddressConfig),
            addressBounds: nextAddressBounds || addressConfigs[selectedAddressKey]?.addressBounds || null,
            addressViewportBounds:
              nextAddressViewportBounds || addressConfigs[selectedAddressKey]?.addressViewportBounds || null,
            coordinates: nextCoordinates,
            locationFilter: nextLocationFilter || addressConfigs[selectedAddressKey]?.locationFilter || null,
            placeId: nextPlaceId || addressConfigs[selectedAddressKey]?.placeId || '',
            placeTypes: nextPlaceTypes.length ? nextPlaceTypes : addressConfigs[selectedAddressKey]?.placeTypes || [],
          }, currentAddressConfig, { preserveManualAreas: isFindHomesAroundLocationsMode })
        : sanitizeAddressConfig({
            ...(addressConfigs[selectedAddressKey] || selectedAddressConfig),
            addressBounds: nextAddressBounds || addressConfigs[selectedAddressKey]?.addressBounds || null,
            addressViewportBounds:
              nextAddressViewportBounds || addressConfigs[selectedAddressKey]?.addressViewportBounds || null,
            coordinates: nextCoordinates,
            locationFilter: nextLocationFilter || addressConfigs[selectedAddressKey]?.locationFilter || null,
            placeId: nextPlaceId || addressConfigs[selectedAddressKey]?.placeId || '',
            placeTypes: nextPlaceTypes.length ? nextPlaceTypes : addressConfigs[selectedAddressKey]?.placeTypes || [],
          }))
      : addressConfigs[selectedAddressKey] || selectedAddressConfig;
    const nextAddressConfigs = usesSharedAddressSettings
      ? syncMultipleAddressConfigs(
          nextAddresses,
          {
            ...addressConfigs,
            [selectedAddressKey]: seededAddressConfig,
          },
          currentAddressKey,
          currentAddressConfig,
          { preserveManualAreas: isFindHomesAroundLocationsMode }
        )
      : {
          ...addressConfigs,
          ...(currentAddressKey ? { [currentAddressKey]: currentAddressConfig } : {}),
          [selectedAddressKey]: seededAddressConfig,
        };

    if (existingIndex < 0) setStartingAddresses(nextAddresses);
    if (isFindHomesAroundLocationsMode) {
      setSelectedManualAreaKey(
        getLocationEstimateAreaKey(selectedAddressKey, seededAddressConfig.locationFilter)
      );
    }
    setAddressConfigs((currentConfigs) =>
      areAddressConfigsEqual(currentConfigs, nextAddressConfigs) ? currentConfigs : nextAddressConfigs
    );
    setSelectedAddressIndex(existingIndex >= 0 ? existingIndex : nextAddresses.length - 1);
    applyAddressConfig(selectedAddressConfig, selectedAddressKey);
    if (isFindHomesAroundLocationsMode && existingIndex < 0) {
      setManualAreaOrder((currentOrder) =>
        appendManualAreaOrderEntry(currentOrder, 'location', nextAddressKey)
      );
    }
    setAddress('');
  }, [
    addressConfigs,
    applyAddressConfig,
    currentAddressConfig,
    hasReachedMaxSelectedAddresses,
    isFindHomesAroundAddressesMode,
    isFindHomesAroundLocationsMode,
    mapAddress,
    selectedMapLocationCount,
    startingAddressMode,
    startingAddresses,
    usesSharedAddressSettings,
  ]);

  useEffect(() => {
    addStartingAddressValueRef.current = addStartingAddressValue;
  }, [addStartingAddressValue]);

  const resolveVerifiedAddressRows = useCallback(async (addresses, {
    existingAddressKeys = new Set(),
    maxResolvedRows = Infinity,
    onlyLocations = false,
  } = {}) => {
    const { Geocoder } = await loadGoogleMapsLibraries();
    if (!Geocoder) throw new Error('Google Maps loaded without the required geocoding library.');

    const geocoder = new Geocoder();
    const nextAddressKeys = new Set(Array.from(existingAddressKeys).map(getAddressMatchKey));
    const resolvedRows = [];
    const skippedRows = [];
    const resolvedLimit = Number.isFinite(Number(maxResolvedRows)) && Number(maxResolvedRows) > 0
      ? Number(maxResolvedRows)
      : Infinity;
    const candidateAddresses = addresses.map(normalizeAddress).filter(Boolean);

    const geocodeAddress = async (addressToVerify) => {
      try {
        const result = await new Promise((resolve, reject) => {
          geocoder.geocode({
            address: addressToVerify,
            bounds: addressAutocompleteBoundsRef.current || usaSearchBounds,
            componentRestrictions: { country: 'US' },
            region: 'us',
          }, (results, status) => {
            if (status === 'OK' && results?.[0]) {
              resolve(results[0]);
              return;
            }

            reject(new Error('Could not find that address on Google Maps.'));
          });
        });
        const restrictionMessage = getPlaceSearchRestrictionMessage({
          addressComponents: result.address_components,
          onlyLocations,
          types: result.types,
        });

        if (restrictionMessage) throw new Error(restrictionMessage);
        const location = result.geometry.location;
        const resolvedAddress = result.formatted_address || addressToVerify;

        return {
          address: resolvedAddress,
          addressBounds: normalizeAddressBounds(result.geometry.bounds || result.geometry.viewport),
          addressViewportBounds: normalizeAddressBounds(result.geometry.viewport),
          coordinates: {
            latitude: location.lat(),
            longitude: location.lng(),
          },
          locationFilter: getPlaceLocationFilter({
            addressComponents: result.address_components,
            types: result.types,
          }),
          originalAddress: addressToVerify,
          placeId: result.place_id,
          placeTypes: normalizePlaceTypes(result.types),
        };
      } catch {
        return {
          originalAddress: addressToVerify,
          skipped: true,
        };
      }
    };

    for (let index = 0; index < candidateAddresses.length; index += addressVerificationBatchSize) {
      if (resolvedRows.length >= resolvedLimit) break;
      const batch = candidateAddresses.slice(index, index + addressVerificationBatchSize);
      const batchRows = await Promise.all(batch.map(geocodeAddress));

      for (const row of batchRows) {
        if (resolvedRows.length >= resolvedLimit) break;
        if (row.skipped) {
          skippedRows.push(row.originalAddress);
          continue;
        }

        const resolvedAddressKey = getAddressMatchKey(row.address);
        if (!resolvedAddressKey || nextAddressKeys.has(resolvedAddressKey)) {
          skippedRows.push(row.originalAddress);
          continue;
        }

        nextAddressKeys.add(resolvedAddressKey);
        resolvedRows.push(row);
      }
    }

    return { resolvedRows, skippedRows };
  }, []);

  const handleUploadedAddressFiles = useCallback((files) => {
    const file = files?.[0];
    if (!file) return;
    if (hasReachedMaxSelectedAddresses) {
      setAddressUploadError(maxSelectedItemsPlaceholder);
      setUploadedAddressFileName('');
      setUploadedAddressCount(0);
      return;
    }

    const allowedExtensions = ['.csv'];
    const fileName = file.name || '';
    const isAllowedFile = allowedExtensions.some((extension) => fileName.toLowerCase().endsWith(extension));

    if (!isAllowedFile) {
      setAddressUploadError(invalidAddressUploadFileMessage);
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      const parseResult = parseUploadedAddressText(reader.result);
      const parsedAddresses = getUniqueAddresses(parseResult.addresses);

      if (!parsedAddresses.length) {
        setAddressUploadError(parseResult.rowCount
          ? 'Rows were found, but no usable address columns could be detected.'
          : 'No addresses were found in that file.');
        setUploadedAddressFileName('');
        setUploadedAddressCount(0);
        return;
      }

      const existingAddressKeys = new Set(startingAddresses.map(getAddressMatchKey));
      const remainingAddressSlots = Math.max(maxSelectedAddresses - startingAddresses.length, 0);
      const newAddresses = parsedAddresses
        .filter((nextAddress) => !existingAddressKeys.has(getAddressMatchKey(nextAddress)));

      setUploadedAddressFileName(fileName);
      setUploadedAddressCount(parsedAddresses.length);
      if (remainingAddressSlots <= 0) {
        setAddressUploadError(maxSelectedItemsPlaceholder);
        return;
      }
      if (!newAddresses.length) {
        setAddressUploadError('Those addresses are already in the list.');
        return;
      }

      const hasMoreAddressesThanAvailableSlots = newAddresses.length > remainingAddressSlots;

      setIsProcessingAddressUpload(true);
      setAddressUploadError('');

      try {
        const { resolvedRows, skippedRows } = await resolveVerifiedAddressRows(newAddresses, {
          existingAddressKeys,
          maxResolvedRows: remainingAddressSlots,
          onlyLocations: false,
        });

        if (!resolvedRows.length) {
          setAddressUploadError(`${formatNumber(newAddresses.length)} address${newAddresses.length === 1 ? '' : 'es'} found, but none could be verified.`);
          return;
        }

        const nextAddresses = [...startingAddresses, ...resolvedRows.map((row) => row.address)];
        const activeAddressKey = mapAddress ? getAddressKey(mapAddress) : '';
        const seedConfigs = resolvedRows.reduce((configs, row) => {
          const nextAddressKey = getAddressKey(row.address);
          if (!nextAddressKey || configs[nextAddressKey]) return configs;

          return {
            ...configs,
            [nextAddressKey]: applySharedMultipleAddressConfig(
              {
                ...(configs[nextAddressKey] || createDefaultAddressConfig()),
                addressBounds: row.addressBounds,
                addressViewportBounds: row.addressViewportBounds,
                coordinates: row.coordinates,
                locationFilter: row.locationFilter || null,
                placeId: row.placeId || '',
                placeTypes: row.placeTypes,
              },
              currentAddressConfig,
              { preserveManualAreas: isFindHomesAroundLocationsMode }
            ),
          };
        }, addressConfigs);
        const nextAddressConfigs = syncMultipleAddressConfigs(
          nextAddresses,
          seedConfigs,
          activeAddressKey,
          currentAddressConfig,
          { preserveManualAreas: isFindHomesAroundLocationsMode }
        );
        const nextSelectedIndex = resolvedRows.length
          ? nextAddresses.length - resolvedRows.length
          : Math.min(selectedAddressIndex, Math.max(nextAddresses.length - 1, 0));
        const nextSelectedAddress = nextAddresses[nextSelectedIndex] || '';
        const nextSelectedConfig = nextSelectedAddress
          ? applySharedMultipleAddressConfig(
              nextAddressConfigs[getAddressKey(nextSelectedAddress)] || createDefaultAddressConfig(),
              currentAddressConfig,
              { preserveManualAreas: isFindHomesAroundLocationsMode }
            )
          : currentAddressConfig;

        setStartingAddresses(nextAddresses);
        setSelectedAddressIndex(nextSelectedIndex);
        setAddressConfigs((currentConfigs) =>
          areAddressConfigsEqual(currentConfigs, nextAddressConfigs) ? currentConfigs : nextAddressConfigs
        );
        if (nextSelectedAddress) applyAddressConfig(nextSelectedConfig, getAddressKey(nextSelectedAddress));
        const duplicateCount = parseResult.addresses.length - parsedAddresses.length;
        if (hasMoreAddressesThanAvailableSlots) {
          setAddressUploadError(maxSelectedAddressImportMessage);
          setAddress('');
          return;
        }

        const skippedMessages = [
          parseResult.skippedCount
            ? `${formatNumber(parseResult.skippedCount)} row${parseResult.skippedCount === 1 ? '' : 's'} had no usable address`
            : '',
          duplicateCount
            ? `${formatNumber(duplicateCount)} duplicate address${duplicateCount === 1 ? '' : 'es'} skipped`
            : '',
          skippedRows.length
            ? `${formatNumber(skippedRows.length)} address${skippedRows.length === 1 ? '' : 'es'} could not be verified`
            : '',
        ].filter(Boolean);

        setAddressUploadError(skippedMessages.length ? `${skippedMessages.join('. ')}.` : '');
        setAddress('');
      } catch (error) {
        setAddressUploadError(getGoogleMapsErrorMessage(error));
      } finally {
        setIsProcessingAddressUpload(false);
      }
    };
    reader.onerror = () => {
      setAddressUploadError('Could not read that file.');
      setUploadedAddressFileName('');
      setUploadedAddressCount(0);
      setIsProcessingAddressUpload(false);
    };
    reader.readAsText(file);
  }, [
    addressConfigs,
    applyAddressConfig,
    currentAddressConfig,
    hasReachedMaxSelectedAddresses,
    isFindHomesAroundLocationsMode,
    mapAddress,
    resolveVerifiedAddressRows,
    selectedAddressIndex,
    startingAddresses,
  ]);

  const handleAddContactAddresses = useCallback(async ({
    contactIds = selectedContactIds,
    tagIds = selectedTagIds,
  } = {}) => {
    const nextContactIds = Array.isArray(contactIds) ? contactIds : [];
    const nextTagIds = Array.isArray(tagIds) ? tagIds : [];

    if ((!nextContactIds.length && !nextTagIds.length) || isAddingContactAddresses) return;
    if (hasReachedMaxSelectedAddresses) {
      setContactAddressMessage(maxSelectedItemsPlaceholder);
      return;
    }

    setIsAddingContactAddresses(true);
    setContactAddressMessage('');

    try {
      let contactRows = nextContactIds
        .map((id) => selectedContactsCache[String(id)] || contacts.find((contact) => String(contact.id) === String(id)))
        .filter(Boolean);

      if (nextTagIds.length) {
        const tagContacts = [];
        let page = 1;
        let totalTagContacts = null;
        let lastPage = null;

        while (true) {
          const tagResponse = await request.get('/contacts', {
            page,
            pageSize: 500,
            tags: `[${nextTagIds.join(',')}]`,
          });
          const data = tagResponse?.data?.data || {};
          const rows = Array.isArray(data.rows) ? data.rows : [];
          const rawTotal = data.count ?? data.total ?? data.totalRecordsInDB;
          const nextLastPage = parsePositiveInteger(data.lastPage ?? data.last_page);

          tagContacts.push(...rows);
          if (rawTotal !== undefined && rawTotal !== null) totalTagContacts = Number(rawTotal);
          if (nextLastPage) lastPage = nextLastPage;

          if (!rows.length) break;
          if (Number.isFinite(totalTagContacts) && tagContacts.length >= totalTagContacts) break;
          if (lastPage && page >= lastPage) break;

          page += 1;
        }

        const contactsById = new Map(contactRows.map((contact) => [String(contact.id), contact]));

        tagContacts.forEach((contact) => {
          if (contact?.id) contactsById.set(String(contact.id), contact);
        });
        contactRows = Array.from(contactsById.values());
      }

      const contactAddressMetadataByKey = contactRows.reduce((metadata, contact) => {
        const contactAddress = buildContactAddress(contact);
        const dataAxlePlaceIds = getContactDataAxlePlaceIds(contact);
        return mergeAddressMetadata(metadata, contactAddress, dataAxlePlaceIds);
      }, {});
      const contactAddresses = getUniqueAddresses(contactRows.map(buildContactAddress).filter(Boolean));
      if (!contactAddresses.length) {
        setContactAddressMessage('The selected contacts do not have usable mailing addresses.');
        return;
      }

      const existingAddressKeys = new Set(startingAddresses.map(getAddressMatchKey));
      const remainingAddressSlots = Math.max(maxSelectedAddresses - startingAddresses.length, 0);
      const newAddresses = contactAddresses
        .filter((nextAddress) => !existingAddressKeys.has(getAddressMatchKey(nextAddress)));
      if (remainingAddressSlots <= 0) {
        setContactAddressMessage(maxSelectedItemsPlaceholder);
        return;
      }
      if (!newAddresses.length) {
        setContactAddressMessage('Those contact addresses are already in the list.');
        return;
      }

      const hasMoreAddressesThanAvailableSlots = newAddresses.length > remainingAddressSlots;

      const { resolvedRows, skippedRows } = await resolveVerifiedAddressRows(newAddresses, {
        existingAddressKeys,
        maxResolvedRows: remainingAddressSlots,
        onlyLocations: false,
      });

      if (!resolvedRows.length) {
        setContactAddressMessage(`${formatNumber(newAddresses.length)} contact address${newAddresses.length === 1 ? '' : 'es'} found, but none could be verified.`);
        return;
      }

      const nextAddresses = [...startingAddresses, ...resolvedRows.map((row) => row.address)];
      const activeAddressKey = mapAddress ? getAddressKey(mapAddress) : '';
      const seedConfigs = resolvedRows.reduce((configs, row) => {
        const nextAddressKey = getAddressKey(row.address);
        if (!nextAddressKey || configs[nextAddressKey]) return configs;

        return {
          ...configs,
          [nextAddressKey]: applySharedMultipleAddressConfig(
            {
              ...(configs[nextAddressKey] || createDefaultAddressConfig()),
              addressBounds: row.addressBounds,
              addressViewportBounds: row.addressViewportBounds,
              coordinates: row.coordinates,
              dataAxlePlaceIds: [
                ...new Set([
                  ...getDataAxlePlaceIdsForAddress(contactAddressMetadataByKey, row.originalAddress),
                  ...getDataAxlePlaceIdsForAddress(contactAddressMetadataByKey, row.address),
                ]),
              ],
              locationFilter: row.locationFilter || null,
              placeId: row.placeId || '',
              placeTypes: row.placeTypes,
            },
            currentAddressConfig,
            { preserveManualAreas: isFindHomesAroundLocationsMode }
          ),
        };
      }, addressConfigs);
      const nextAddressConfigs = syncMultipleAddressConfigs(
        nextAddresses,
        seedConfigs,
        activeAddressKey,
        currentAddressConfig,
        { preserveManualAreas: isFindHomesAroundLocationsMode }
      );
      const nextSelectedIndex = nextAddresses.length - resolvedRows.length;
      const nextSelectedAddress = nextAddresses[nextSelectedIndex] || '';
      const nextSelectedConfig = nextSelectedAddress
        ? applySharedMultipleAddressConfig(
            nextAddressConfigs[getAddressKey(nextSelectedAddress)] || createDefaultAddressConfig(),
            currentAddressConfig,
            { preserveManualAreas: isFindHomesAroundLocationsMode }
          )
        : currentAddressConfig;

      setStartingAddresses(nextAddresses);
      setSelectedAddressIndex(nextSelectedIndex);
      setAddressConfigs((currentConfigs) =>
        areAddressConfigsEqual(currentConfigs, nextAddressConfigs) ? currentConfigs : nextAddressConfigs
      );
      if (nextSelectedAddress) applyAddressConfig(nextSelectedConfig, getAddressKey(nextSelectedAddress));
      if (hasMoreAddressesThanAvailableSlots) {
        setContactAddressMessage(maxSelectedAddressImportMessage);
        return;
      }

      if (skippedRows.length) {
        setContactAddressMessage(`${formatNumber(resolvedRows.length)} verified address${resolvedRows.length === 1 ? '' : 'es'} added. ${formatNumber(skippedRows.length)} contact address${skippedRows.length === 1 ? '' : 'es'} could not be verified.`);
      } else {
        setContactAddressMessage(`${formatNumber(resolvedRows.length)} verified address${resolvedRows.length === 1 ? '' : 'es'} added.`);
      }
    } catch (error) {
      setContactAddressMessage(
        error?.response?.data?.message || error?.data?.message || getGoogleMapsErrorMessage(error)
      );
    } finally {
      setIsAddingContactAddresses(false);
    }
  }, [
    addressConfigs,
    applyAddressConfig,
    contacts,
    currentAddressConfig,
    hasReachedMaxSelectedAddresses,
    isFindHomesAroundLocationsMode,
    isAddingContactAddresses,
    mapAddress,
    resolveVerifiedAddressRows,
    selectedContactIds,
    selectedContactsCache,
    selectedTagIds,
    startingAddresses,
  ]);

  const addressUploadDropzone = useDropzone({
    accept: {
      'text/csv': ['.csv'],
    },
    disabled: hasReachedMaxSelectedAddresses || isProcessingAddressUpload,
    maxFiles: 1,
    onDropAccepted: handleUploadedAddressFiles,
    onDropRejected: () => {
      setAddressUploadError(invalidAddressUploadFileMessage);
      setUploadedAddressFileName('');
      setUploadedAddressCount(0);
    },
  });

  const focusContactAddressSearch = useCallback(() => {
    setAddressEntrySource('contacts');
    setContactAddressMessage('');
    setContactSearchFocusRequest((requestCount) => requestCount + 1);
  }, []);

  const handleAddressEntrySourceSelect = useCallback((nextSource) => {
    setAddressEntrySource(nextSource);
    if (nextSource === 'contacts') {
      setContactAddressMessage('');
      setContactSearchFocusRequest(0);
    }
  }, []);

  const handleSummaryAddAddress = useCallback(() => {
    if (isFindHomesAroundAddressesMode && addressEntrySource === 'upload') {
      addressUploadDropzone.open?.();
      return;
    }

    if (isFindHomesAroundAddressesMode && addressEntrySource === 'contacts') {
      focusContactAddressSearch();
      return;
    }

    focusAddressInput();
  }, [
    addressEntrySource,
    addressUploadDropzone,
    focusAddressInput,
    focusContactAddressSearch,
    isFindHomesAroundAddressesMode,
  ]);

  const resolveStartingAddressValue = useCallback(async (value) => {
    const nextAddress = normalizeAddress(value);
    if (!nextAddress) return;
    const isExistingAddress = startingAddresses.some((currentAddress) =>
      isMatchingStartingAddress(currentAddress, nextAddress, { addressConfigs })
    );

    if (hasReachedMaxSelectedAddresses && !isExistingAddress) {
      setAddress('');
      return;
    }

    setMapStatus('loading');
    setMapError('');

    try {
      const { Geocoder } = await loadGoogleMapsLibraries();
      if (!Geocoder) throw new Error('Google Maps loaded without the required geocoding library.');

      const result = await new Promise((resolve, reject) => {
        new Geocoder().geocode({
          address: nextAddress,
          bounds: addressAutocompleteBoundsRef.current || usaSearchBounds,
          componentRestrictions: { country: 'US' },
          region: 'us',
        }, (results, status) => {
          if (status === 'OK' && results?.[0]) {
            resolve(results[0]);
            return;
          }

          reject(new Error('Could not find that address on Google Maps.'));
        });
      });
      const restrictionMessage = getPlaceSearchRestrictionMessage({
        addressComponents: result.address_components,
        onlyLocations: isFindHomesAroundLocationsMode,
        types: result.types,
      });

      if (restrictionMessage) throw new Error(restrictionMessage);
      const location = result.geometry.location;
      const resolvedAddress = result.formatted_address || nextAddress;
      const resolvedCoordinates = {
        latitude: location.lat(),
        longitude: location.lng(),
      };
      const resolvedBounds = result.geometry.bounds || result.geometry.viewport;
      const resolvedViewportBounds = result.geometry.viewport;
      const resolvedPlaceDetails = {
        addressComponents: result.address_components,
        locationFilter: getPlaceLocationFilter({
          addressComponents: result.address_components,
          types: result.types,
        }),
        placeId: result.place_id,
        placeTypes: result.types,
        viewportBounds: resolvedViewportBounds,
      };

      pendingResolvedAddressRef.current = {
        addressBounds: normalizeAddressBounds(resolvedBounds),
        addressViewportBounds: normalizeAddressBounds(resolvedViewportBounds),
        coordinates: resolvedCoordinates,
        key: getAddressKey(resolvedAddress),
        locationFilter: resolvedPlaceDetails.locationFilter,
        placeId: resolvedPlaceDetails.placeId,
        placeTypes: normalizePlaceTypes(resolvedPlaceDetails.placeTypes),
        rawBounds: resolvedBounds,
      };
      addStartingAddressValue(resolvedAddress, resolvedCoordinates, resolvedBounds, resolvedPlaceDetails);
    } catch (error) {
      setMapStatus('error');
      setMapError(getGoogleMapsErrorMessage(error));
    }
  }, [
    addStartingAddressValue,
    addressConfigs,
    hasReachedMaxSelectedAddresses,
    isFindHomesAroundLocationsMode,
    startingAddresses,
  ]);

  const handleSelectStartingAddress = (indexToSelect) => {
    const nextAddress = startingAddresses[indexToSelect];
    if (!nextAddress) return;

    const currentAddressKey = mapAddress ? getAddressKey(mapAddress) : '';
    const nextAddressKey = getAddressKey(nextAddress);
    const nextAddressConfigs = usesSharedAddressSettings
      ? syncMultipleAddressConfigs(
          startingAddresses,
          addressConfigs,
          currentAddressKey,
          currentAddressConfig,
          { preserveManualAreas: isFindHomesAroundLocationsMode }
        )
      : {
          ...addressConfigs,
          ...(currentAddressKey ? { [currentAddressKey]: currentAddressConfig } : {}),
        };
    const nextSelectedConfig = usesSharedAddressSettings
      ? applySharedMultipleAddressConfig(
          nextAddressConfigs[nextAddressKey] || createDefaultAddressConfig(),
          currentAddressConfig,
          { preserveManualAreas: isFindHomesAroundLocationsMode }
        )
      : getVisibleAddressConfig(
          nextAddressConfigs[nextAddressKey] || createDefaultAddressConfig(),
          startingAddressMode,
          { preserveManualAreas: isFindHomesAroundLocationsMode }
        );

    setAddressConfigs((currentConfigs) =>
      areAddressConfigsEqual(currentConfigs, nextAddressConfigs) ? currentConfigs : nextAddressConfigs
    );
    setSelectedAddressIndex(indexToSelect);
    applyAddressConfig(nextSelectedConfig, nextAddressKey);
  };

  const removeStartingAddress = (indexToRemove) => {
    preserveSummaryAddressListScroll();

    const nextAddresses = startingAddresses.filter((_, index) => index !== indexToRemove);
    const removedAddress = startingAddresses[indexToRemove];
    const currentAddressKey = mapAddress ? getAddressKey(mapAddress) : '';
    const removedAddressKey = removedAddress ? getAddressKey(removedAddress) : '';
    const nextManualAreaOrder = isFindHomesAroundLocationsMode && removedAddressKey
      ? removeManualAreaOrderEntry(repairedManualAreaOrder, 'location', removedAddressKey)
      : repairedManualAreaOrder;
    const nextSelectedAddressIndex = nextAddresses.length === 0
      ? 0
      : Math.min(
          indexToRemove < activeAddressIndex ? activeAddressIndex - 1 : activeAddressIndex,
          nextAddresses.length - 1
        );
    const nextSelectedAddress = nextAddresses[nextSelectedAddressIndex] || '';
    const nextSelectedAddressKey = nextSelectedAddress ? getAddressKey(nextSelectedAddress) : '';
    const didSelectedAddressChange = nextSelectedAddressKey !== currentAddressKey;
    const currentSyncedConfigs = {
      ...addressConfigs,
      ...(currentAddressKey ? { [currentAddressKey]: currentAddressConfig } : {}),
    };
    const nextAddressConfigs = usesSharedAddressSettings
      ? syncMultipleAddressConfigs(
          nextAddresses,
          currentSyncedConfigs,
          currentAddressKey,
          currentAddressConfig,
          {
            preserveAllEstimates: true,
            preserveManualAreas: isFindHomesAroundLocationsMode,
          }
        )
      : currentSyncedConfigs;

    if (removedAddressKey) delete nextAddressConfigs[removedAddressKey];
    if (removedAddressKey) {
      pendingAddressEstimateRequestKeysRef.current.delete(removedAddressKey);
      retryingAddressEstimateKeysRef.current.delete(removedAddressKey);
      setAddressEstimateStatusByKey((currentStatuses) => {
        if (!currentStatuses[removedAddressKey]) return currentStatuses;

        const nextStatuses = { ...currentStatuses };
        delete nextStatuses[removedAddressKey];
        return nextStatuses;
      });
      setAddressEstimateResultByKey((currentResults) => {
        if (!currentResults[removedAddressKey]) return currentResults;

        const nextResults = { ...currentResults };
        delete nextResults[removedAddressKey];
        return nextResults;
      });
    }

    setStartingAddresses(nextAddresses);
    if (isFindHomesAroundLocationsMode && removedAddressKey) {
      setManualAreaOrder(nextManualAreaOrder);
    }
    setAddressConfigs((currentConfigs) =>
      areAddressConfigsEqual(currentConfigs, nextAddressConfigs) ? currentConfigs : nextAddressConfigs
    );
    setSelectedAddressIndex(nextSelectedAddressIndex);
    if (nextSelectedAddress) {
      const nextSelectedConfig = didSelectedAddressChange
        ? usesSharedAddressSettings
          ? applySharedMultipleAddressConfig(
              nextAddressConfigs[nextSelectedAddressKey] || createDefaultAddressConfig(),
              currentAddressConfig,
              { preserveManualAreas: isFindHomesAroundLocationsMode }
            )
          : getVisibleAddressConfig(
              nextAddressConfigs[nextSelectedAddressKey] || createDefaultAddressConfig(),
              startingAddressMode,
              { preserveManualAreas: isFindHomesAroundLocationsMode }
            )
        : nextAddressConfigs[nextSelectedAddressKey] || currentAddressConfig;

      applyAddressConfig(nextSelectedConfig, nextSelectedAddressKey);
    } else {
      applyAddressConfig(isFindHomesAroundLocationsMode
        ? {
            ...createDefaultAddressConfig(),
            areaMode: isManualAreaMode ? areaMode : 'custom',
            customShapes: customShapes.map((shape) => [...shape]),
            drawRadiusAreas: drawRadiusAreas.map((area) => ({ ...area })),
            filters: selectedFilters,
            manualAreaOrder: nextManualAreaOrder,
          }
        : createDefaultAddressConfig());
      setEstimatedHomes(null);
      setEstimatedRadiusMiles(null);
      setEstimateStatus('idle');
    }
  };
  removeStartingAddressRef.current = removeStartingAddress;

  const handleAddressKeyDown = (event) => {
    if (event.key !== 'Enter') return;

    event.preventDefault();
    resolveStartingAddressValue(address);
  };

  const clearList = () => {
    if (settingsDraftDebounceRef.current) {
      window.clearTimeout(settingsDraftDebounceRef.current);
      settingsDraftDebounceRef.current = null;
    }
    setAddress('');
    setAddressUploadError('');
    setUploadedAddressFileName('');
    setUploadedAddressCount(0);
    setSelectedContactIds([]);
    setSelectedTagIds([]);
    setSelectedContactsCache({});
    setSelectedTagsCache({});
    setContactSearch('');
    setTagSearch('');
    setContactAddressMessage('');
    setStartingAddresses([]);
    setSelectedAddressIndex(0);
    setAddressConfigs({});
    setAddressEstimateStatusByKey({});
    setAddressEstimateResultByKey({});
    const emptyFilters = rememberFiltersForContactType(activeContactType, createEmptyFilterState(activeContactType));
    setSelectedFilters(emptyFilters);
    setFilterDraft(emptyFilters);
    setListCleanupOptions(defaultListCleanupOptions);
    setShowFilters(false);
    setTargetCount(defaultTargetCount);
    setCustomTargetCount('');
    setRadiusMiles('');
    setCustomRadiusMiles('');
    setAreaMode(isFindHomesAroundLocationsMode ? 'custom' : 'radius');
    setNearbyTargetMode(defaultNearbyTargetMode);
    setIsDrawingShape(false);
    setCustomShapes([]);
    setCustomShapePoints([]);
    setDrawRadiusAreas([]);
    setDrawRadiusPoints([]);
    setManualAreaOrder([]);
    setAddressBounds(null);
    setAddressViewportBounds(null);
    setPlaceId('');
    setPlaceTypes([]);
    setEstimatedFilterKey('');
    setEstimatedHomes(null);
    setEstimatedRadiusMiles(null);
    setEstimateStatus('idle');
    setEstimateError('');
    setManualAreaEstimateByKey({});
    manualAreaEstimateByKeyRef.current = {};
    clearStoredFindNewContactsData(settingsDraftScope);
    setHasStoredFindNewContactsDataState(false);
    setClearConfirmModal((currentModal) => ({ ...currentModal, open: false }));
    clearBoundaryFeatureLayer();

    if (radiusCircleRef.current) {
      radiusCircleRef.current.setMap(null);
      radiusCircleRef.current = null;
    }
    if (addressBoundaryRef.current) {
      addressBoundaryRef.current.setMap(null);
      addressBoundaryRef.current = null;
    }
    drawRadiusCircleRefs.current.forEach((circle) => circle.setMap(null));
    drawRadiusCircleRefs.current = [];
    if (drawRadiusPreviewCircleRef.current) {
      drawRadiusPreviewCircleRef.current.setMap(null);
      drawRadiusPreviewCircleRef.current = null;
    }
    manualAreaDeleteMarkerRefs.current.forEach(clearMapMarker);
    manualAreaDeleteMarkerRefs.current = [];
    customPolygonRefs.current.forEach((polygon) => polygon.setMap(null));
    customPolygonRefs.current = [];
    if (customPolylineRef.current) customPolylineRef.current.setMap(null);
    customPointMarkerRefs.current.forEach(clearMapMarker);
    radiusPointMarkerRefs.current.forEach(clearMapMarker);
    customPointMarkerRefs.current = [];
    radiusPointMarkerRefs.current = [];
  };

  const openClearListConfirm = () => {
    if (!canClearSelection) return;

    setClearConfirmModal({
      className: 'clearFindContactsModal',
      handleClose: () => setClearConfirmModal((currentModal) => ({ ...currentModal, open: false })),
      handleOk: clearList,
      header: 'Clear Selection',
      message: "Are you sure you want to clear all selections and filters? This can't be undone.",
      open: true,
      successButtonName: 'Yes',
      cancelButtonName: 'No',
    });
  };

  const handleBackToCreateNewOrder = () => {
    if (typeof onBack === 'function') {
      onBack();
      return;
    }

    if (typeof onCancel === 'function') onCancel();
  };

  useEffect(() => {
    const handleAuthFailure = () => {
      setMapStatus('error');
      setMapError(googleMapsAuthErrorMessage);
      setCoordinates(null);
    };

    window.addEventListener(googleMapsAuthFailureEvent, handleAuthFailure);
    return () => {
      window.removeEventListener(googleMapsAuthFailureEvent, handleAuthFailure);
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current) return;

    if (!googleMapsApiKey) {
      setMapStatus('error');
      setMapError('Missing VITE_GOOGLE_MAPS_API_KEY.');
      return;
    }

    let isCancelled = false;
    const mapRenderKey = mapAddress ? getAddressKey(mapAddress) : '';

    if (!googleMapRef.current || lastMapRenderKeyRef.current !== mapRenderKey) {
      setMapStatus('loading');
    }
    setMapError('');

    if (!mapAddress) {
      const emptyMapCenter = isFindHomesAroundLocationsMode && localMapCenter ? localMapCenter : defaultMapCenter;
      const emptyMapZoom = isFindHomesAroundLocationsMode && localMapCenter ? localMapZoom : defaultMapZoom;

      loadGoogleMapsLibraries()
        .then(({ Map, OverlayView, maps }) => {
          if (isCancelled) return;

          if (!Map) {
            setMapStatus('error');
            setMapError('Google Maps loaded without the required map library.');
            setCoordinates(null);
            return;
          }

          if (!googleMapRef.current) {
            googleMapRef.current = new Map(
              mapRef.current,
              getGoogleMapOptions({
                center: emptyMapCenter,
                clickableIcons: false,
                streetViewControl: false,
                fullscreenControl: false,
                gestureHandling: 'greedy',
                mapTypeControl: false,
                mapTypeId: mapTypeIdRef.current,
                zoom: emptyMapZoom,
              }, { useMapId: isFindHomesAroundLocationsMode })
            );
          } else {
            googleMapRef.current.setCenter(emptyMapCenter);
            googleMapRef.current.setMapTypeId(mapTypeIdRef.current);
            googleMapRef.current.setZoom(emptyMapZoom);
          }

          if (OverlayView && !mapProjectionOverlayRef.current) {
            mapProjectionOverlayRef.current = new OverlayView();
            mapProjectionOverlayRef.current.onAdd = () => {};
            mapProjectionOverlayRef.current.draw = () => {};
            mapProjectionOverlayRef.current.onRemove = () => {};
            mapProjectionOverlayRef.current.setMap(googleMapRef.current);
          }

          clearMapMarker(markerRef.current);
          markerRef.current = null;
          clearBoundaryFeatureLayer();
          if (addressBoundaryRef.current) {
            addressBoundaryRef.current.setMap(null);
            addressBoundaryRef.current = null;
          }
          if (radiusCircleRef.current) {
            radiusCircleRef.current.setMap(null);
            radiusCircleRef.current = null;
          }
          if (!isFindHomesAroundLocationsMode) {
            drawRadiusCircleRefs.current.forEach((circle) => circle.setMap(null));
            drawRadiusCircleRefs.current = [];
            if (drawRadiusPreviewCircleRef.current) {
              drawRadiusPreviewCircleRef.current.setMap(null);
              drawRadiusPreviewCircleRef.current = null;
            }
            customPolygonRefs.current.forEach((polygon) => polygon.setMap(null));
            customPolygonRefs.current = [];
            if (customPolylineRef.current) customPolylineRef.current.setMap(null);
            customPointMarkerRefs.current.forEach(clearMapMarker);
            radiusPointMarkerRefs.current.forEach(clearMapMarker);
            customPointMarkerRefs.current = [];
            radiusPointMarkerRefs.current = [];
            setCustomShapes((currentShapes) => (currentShapes.length ? [] : currentShapes));
            setCustomShapePoints((currentPoints) => (currentPoints.length ? [] : currentPoints));
            setDrawRadiusAreas((currentAreas) => (currentAreas.length ? [] : currentAreas));
            setDrawRadiusPoints((currentPoints) => (currentPoints.length ? [] : currentPoints));
            setIsDrawingShape(false);
          }
          setCoordinates(null);
          setAddressBounds((currentBounds) => (currentBounds ? null : currentBounds));
          setAddressViewportBounds((currentBounds) => (currentBounds ? null : currentBounds));
          setPlaceId('');
          setPlaceTypes((currentTypes) => (currentTypes.length ? [] : currentTypes));
          lastMapRenderKeyRef.current = mapRenderKey;
          setMapStatus('ready');
        })
        .catch((error) => {
          if (isCancelled) return;
          setMapStatus('error');
          setMapError(getGoogleMapsErrorMessage(error));
          setCoordinates(null);
        });

      return () => {
        isCancelled = true;
      };
    }

    loadGoogleMapsLibraries()
      .then(({ AdvancedMarkerElement, Circle, Geocoder, Map, Marker, OverlayView, maps }) => {
        if (isCancelled) return;

        if (!Geocoder || !Map) {
          setMapStatus('error');
          setMapError('Google Maps loaded without the required map and geocoding libraries.');
          setCoordinates(null);
          return;
        }

        const renderStartingAddress = (location, nextCoordinates, addressGeometry = {}) => {
          if (isCancelled) return;
          const shouldCenterOnStartingAddress = !isManualAreaMode && !isNearbyRadiusMode;
          const isNewSelectedMapAddress = lastMapRenderKeyRef.current !== mapRenderKey;
          const centerMapOnStartingAddress = () => {
            if (isCancelled || !googleMapRef.current) return;

            googleMapRef.current.setCenter(location);
            googleMapRef.current.setZoom(17);
            googleMapRef.current.setMapTypeId(mapTypeIdRef.current);
          };

          if (!googleMapRef.current) {
            googleMapRef.current = new Map(
              mapRef.current,
              getGoogleMapOptions({
                center: location,
                clickableIcons: false,
                streetViewControl: false,
                fullscreenControl: false,
                gestureHandling: 'greedy',
                mapTypeControl: false,
                mapTypeId: mapTypeIdRef.current,
                zoom: 17,
              }, { useMapId: isFindHomesAroundLocationsMode })
            );
          } else if (!isManualAreaMode) {
            centerMapOnStartingAddress();
          } else if (lastMapRenderKeyRef.current !== mapRenderKey) {
            googleMapRef.current.setMapTypeId(mapTypeIdRef.current);
          }

          if (OverlayView && !mapProjectionOverlayRef.current) {
            mapProjectionOverlayRef.current = new OverlayView();
            mapProjectionOverlayRef.current.onAdd = () => {};
            mapProjectionOverlayRef.current.draw = () => {};
            mapProjectionOverlayRef.current.onRemove = () => {};
            mapProjectionOverlayRef.current.setMap(googleMapRef.current);
          }

          clearMapMarker(markerRef.current);
          markerRef.current = createMapMarker({
            AdvancedMarkerElement,
            Marker,
            map: googleMapRef.current,
            maps,
            position: location,
            title: getDisplayAddress(mapAddress) || 'Selected address',
            useAdvancedMarker: isFindHomesAroundLocationsMode,
          });

          if (radiusCircleRef.current) {
            radiusCircleRef.current.setMap(null);
            radiusCircleRef.current = null;
          }
          if (addressBoundaryRef.current) {
            addressBoundaryRef.current.setMap(null);
            addressBoundaryRef.current = null;
          }
          clearBoundaryFeatureLayer();
          if (!isManualAreaMode) {
            drawRadiusCircleRefs.current.forEach((circle) => circle.setMap(null));
            drawRadiusCircleRefs.current = [];
            if (drawRadiusPreviewCircleRef.current) {
              drawRadiusPreviewCircleRef.current.setMap(null);
              drawRadiusPreviewCircleRef.current = null;
            }
            customPolygonRefs.current.forEach((polygon) => polygon.setMap(null));
            customPolygonRefs.current = [];
            if (customPolylineRef.current) customPolylineRef.current.setMap(null);
            customPointMarkerRefs.current.forEach(clearMapMarker);
            radiusPointMarkerRefs.current.forEach(clearMapMarker);
            customPointMarkerRefs.current = [];
            radiusPointMarkerRefs.current = [];
          }
          const nextAddressBounds = getGoogleAddressBounds(
            addressGeometry.bounds || addressBounds,
            addressGeometry.viewport,
            nextCoordinates
          );
          const nextAddressViewportBounds = normalizeAddressBounds(
            addressGeometry.viewport || addressGeometry.addressViewportBounds || addressViewportBounds
          );
          const nextPlaceId = addressGeometry.placeId || placeId;
          const nextPlaceTypes = normalizePlaceTypes(addressGeometry.types || placeTypes);
          const nextLocationFilter = addressGeometry.locationFilter ||
            getPlaceLocationFilter({
              addressComponents: addressGeometry.addressComponents,
              types: nextPlaceTypes,
            }) ||
            locationFilter;
          const boundaryFeatureType = getBoundaryFeatureType(nextPlaceTypes);
          const selectedBoundaryPlaceIdsByType = {};
          const addSelectedBoundaryPlaceId = (featureType, featurePlaceId) => {
            if (!featureType || !featurePlaceId) return;

            if (!selectedBoundaryPlaceIdsByType[featureType]) {
              selectedBoundaryPlaceIdsByType[featureType] = new Set();
            }
            selectedBoundaryPlaceIdsByType[featureType].add(featurePlaceId);
          };

          addSelectedBoundaryPlaceId(boundaryFeatureType, nextPlaceId);

          if (isFindHomesAroundLocationsMode && boundaryFeatureType) {
            const activeAddressKey = mapAddress ? getAddressKey(mapAddress) : '';

            startingAddresses.forEach((startingAddress) => {
              const addressKey = getAddressKey(startingAddress);
              const config = sanitizeAddressConfig(addressKey === activeAddressKey
                ? {
                    ...(addressConfigs[addressKey] || {}),
                    locationFilter: nextLocationFilter,
                    placeId: nextPlaceId,
                    placeTypes: nextPlaceTypes,
                  }
                : addressConfigs[addressKey]);
              const configBoundaryFeatureType = getBoundaryFeatureType(config.placeTypes);

              addSelectedBoundaryPlaceId(configBoundaryFeatureType, config.placeId);
            });
          }
          const selectedBoundaryEntries = Object.entries(selectedBoundaryPlaceIdsByType);
          let didStyleGoogleBoundary = false;

          if (
            isFindHomesAroundLocationsMode &&
            selectedBoundaryEntries.length > 0 &&
            typeof googleMapRef.current.getFeatureLayer === 'function'
          ) {
            const getBoundaryLayerDiagnostics = () => ({
              boundaryFeatureType,
              boundaryFeatureTypes: selectedBoundaryEntries.map(([featureType]) => featureType),
              hasMapId: shouldUseGoogleMapsMapId,
              isDataDrivenStylingAvailable:
                googleMapRef.current?.getMapCapabilities?.()?.isDataDrivenStylingAvailable,
              mapId: googleMapsMapId || '',
              placeId: nextPlaceId,
              placeTypes: nextPlaceTypes,
            });
            const applyGoogleBoundaryStyles = () => {
              if (!googleMapRef.current) {
                return {
                  didStyleActiveBoundary: false,
                  didStyleAllBoundaries: false,
                  didStyleAnyBoundary: false,
                };
              }

              const styledLayers = [];
              let didStyleActiveBoundary = false;

              selectedBoundaryEntries.forEach(([featureType, placeIds]) => {
                const featureLayer = googleMapRef.current.getFeatureLayer(featureType);
                if (!featureLayer?.isAvailable) return;

                featureLayer.style = (params) => {
                  const feature = params.feature;

                  return placeIds.has(feature?.placeId) ? boundaryFeatureStyles : null;
                };
                styledLayers.push(featureLayer);
                if (featureType === boundaryFeatureType && placeIds.has(nextPlaceId)) {
                  didStyleActiveBoundary = true;
                }
              });

              boundaryFeatureLayerRefs.current = styledLayers;
              setIsGoogleBoundaryVisible(styledLayers.length > 0);

              return {
                didStyleActiveBoundary,
                didStyleAllBoundaries: styledLayers.length === selectedBoundaryEntries.length,
                didStyleAnyBoundary: styledLayers.length > 0,
              };
            };

            const boundaryStyleResult = applyGoogleBoundaryStyles();
            didStyleGoogleBoundary = boundaryStyleResult.didStyleActiveBoundary;

            if (!boundaryStyleResult.didStyleAllBoundaries) {
              const retryBoundaryStyle = () => {
                if (isCancelled) return;
                const retryResult = applyGoogleBoundaryStyles();
                didStyleGoogleBoundary = retryResult.didStyleActiveBoundary;
                if (!retryResult.didStyleAllBoundaries) return;

                boundaryFeatureRetryRef.current.listener?.remove?.();
                boundaryFeatureRetryRef.current.timeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
                boundaryFeatureRetryRef.current = { listener: null, timeouts: [] };
              };
              const logUnavailableBoundaryLayer = () => {
                if (isCancelled || boundaryFeatureLayerRefs.current.length > 0) return;

                console.warn(
                  'Google Maps boundary layer is not available for the selected location.',
                  getBoundaryLayerDiagnostics()
                );
              };

              if (!boundaryStyleResult.didStyleAnyBoundary) {
                boundaryFeatureRetryRef.current.listener =
                  googleMapRef.current.addListener?.('mapcapabilities_changed', retryBoundaryStyle) || null;
              }
              boundaryFeatureRetryRef.current.timeouts = [
                window.setTimeout(retryBoundaryStyle, 250),
                window.setTimeout(retryBoundaryStyle, 750),
                window.setTimeout(logUnavailableBoundaryLayer, 1250),
              ];
            }
          }

          if (!isManualAreaMode && !didStyleGoogleBoundary && Circle && isNearbyRadiusMode) {
            radiusCircleRef.current = new Circle({
              center: {
                lat: nextCoordinates.latitude,
                lng: nextCoordinates.longitude,
              },
              clickable: false,
              fillColor: '#ed5c2f',
              fillOpacity: 0.14,
              map: googleMapRef.current,
              radius: radiusMiles * 1609.34,
              strokeColor: '#ed5c2f',
              strokeOpacity: 0.95,
              strokeWeight: 3,
            });
            setIsGoogleBoundaryVisible(false);
          } else if (!didStyleGoogleBoundary) {
            setIsGoogleBoundaryVisible(false);
          }

          if (isManualAreaMode) {
            addressBoundaryRef.current = null;
          }

          const fitMapToSelectedBounds = (bounds, padding = 72) => {
            const applyFit = () => {
              if (isCancelled || !googleMapRef.current) return;

              maps?.event?.trigger?.(googleMapRef.current, 'resize');
              googleMapRef.current.fitBounds(bounds, padding);
            };

            applyFit();
            window.requestAnimationFrame(applyFit);
            window.setTimeout(applyFit, 120);
            window.setTimeout(applyFit, 320);
          };

          if (
            nextAddressBounds &&
            (
              (!isManualAreaMode && (didStyleGoogleBoundary || isFindHomesAroundLocationsMode)) ||
              (isManualAreaMode && isFindHomesAroundLocationsMode && isNewSelectedMapAddress)
            )
          ) {
            const bounds = new maps.LatLngBounds(
              new maps.LatLng(nextAddressBounds.south, nextAddressBounds.west),
              new maps.LatLng(nextAddressBounds.north, nextAddressBounds.east)
            );
            fitMapToSelectedBounds(bounds);
          } else if (!isManualAreaMode && radiusCircleRef.current) {
            fitMapToSelectedBounds(radiusCircleRef.current.getBounds());
          } else if (shouldCenterOnStartingAddress) {
            centerMapOnStartingAddress();
            window.requestAnimationFrame(centerMapOnStartingAddress);
          }

          lastMapRenderKeyRef.current = mapRenderKey;
          setMapStatus('ready');
          setCoordinates((currentCoordinates) =>
            areCoordinatesEqual(currentCoordinates, nextCoordinates) ? currentCoordinates : nextCoordinates
          );
          setAddressBounds((currentBounds) => {
            const nextBounds = normalizeAddressBounds(nextAddressBounds);
            return JSON.stringify(currentBounds) === JSON.stringify(nextBounds) ? currentBounds : nextBounds;
          });
          setAddressViewportBounds((currentBounds) => {
            const nextBounds = normalizeAddressBounds(nextAddressViewportBounds);
            return JSON.stringify(currentBounds) === JSON.stringify(nextBounds) ? currentBounds : nextBounds;
          });
          setPlaceId((currentPlaceId) => (currentPlaceId === nextPlaceId ? currentPlaceId : nextPlaceId || ''));
          setPlaceTypes((currentTypes) =>
            JSON.stringify(currentTypes) === JSON.stringify(nextPlaceTypes) ? currentTypes : nextPlaceTypes
          );
          setLocationFilter((currentFilter) =>
            JSON.stringify(currentFilter) === JSON.stringify(nextLocationFilter)
              ? currentFilter
              : nextLocationFilter || null
          );
        };

        const geocoder = new Geocoder();
        const pendingResolvedAddress = pendingResolvedAddressRef.current?.key === mapRenderKey
          ? pendingResolvedAddressRef.current
          : null;
        const savedCoordinates = normalizeCoordinates(pendingResolvedAddress?.coordinates || coordinates);
        const savedAddressBounds = pendingResolvedAddress?.rawBounds || pendingResolvedAddress?.addressBounds || addressBounds;
        const savedAddressViewportBounds =
          pendingResolvedAddress?.rawViewportBounds ||
          pendingResolvedAddress?.addressViewportBounds ||
          addressViewportBounds;
        const savedPlaceId = pendingResolvedAddress?.placeId || placeId;
        const savedPlaceTypes = pendingResolvedAddress?.placeTypes || placeTypes;
        const renderSavedCoordinates = (shouldClearPending = false) => {
          if (!savedCoordinates) return false;

          renderStartingAddress(
            maps.LatLng
              ? new maps.LatLng(savedCoordinates.latitude, savedCoordinates.longitude)
              : { lat: savedCoordinates.latitude, lng: savedCoordinates.longitude },
            savedCoordinates,
            {
              addressViewportBounds: savedAddressViewportBounds,
              bounds: savedAddressBounds,
              locationFilter: pendingResolvedAddress?.locationFilter || locationFilter,
              placeId: savedPlaceId,
              types: savedPlaceTypes,
              viewport: savedAddressViewportBounds,
            }
          );
          if (shouldClearPending && pendingResolvedAddressRef.current?.key === mapRenderKey) {
            pendingResolvedAddressRef.current = null;
          }
          return true;
        };

        if (pendingResolvedAddress || savedCoordinates) {
          renderSavedCoordinates(Boolean(pendingResolvedAddress));
          return;
        }

        geocoder.geocode({
          address: mapAddress,
          bounds: addressAutocompleteBoundsRef.current || usaSearchBounds,
          componentRestrictions: { country: 'US' },
        }, (results, status) => {
          if (isCancelled) return;

          if (status !== 'OK' || !results?.[0]) {
            if (renderSavedCoordinates()) return;
            setMapStatus('error');
            setMapError('Could not find that address on Google Maps.');
            setCoordinates(null);
            return;
          }

          const restrictionMessage = getPlaceSearchRestrictionMessage({
            addressComponents: results[0].address_components,
            onlyLocations: isFindHomesAroundLocationsMode,
            types: results[0].types,
          });

          if (restrictionMessage) {
            setMapStatus('error');
            setMapError(restrictionMessage);
            setCoordinates(null);
            return;
          }

          const location = results[0].geometry.location;
          renderStartingAddress(location, {
            latitude: location.lat(),
            longitude: location.lng(),
          }, {
            addressComponents: results[0].address_components,
            bounds: results[0].geometry.bounds,
            locationFilter: getPlaceLocationFilter({
              addressComponents: results[0].address_components,
              types: results[0].types,
            }),
            placeId: results[0].place_id,
            types: results[0].types,
            viewport: results[0].geometry.viewport,
          });
        });
      })
      .catch((error) => {
        if (isCancelled) return;
        setMapStatus('error');
        setMapError(getGoogleMapsErrorMessage(error));
        setCoordinates(null);
      });

    return () => {
      isCancelled = true;
    };
  }, [
    addressBounds,
    addressConfigs,
    addressViewportBounds,
    clearBoundaryFeatureLayer,
    coordinates,
    isFindHomesAroundLocationsMode,
    isManualAreaMode,
    isNearbyRadiusMode,
    localMapCenter,
    locationFilter,
    mapAddress,
    placeId,
    placeTypes,
    radiusMiles,
    startingAddresses,
  ]);

  useEffect(() => {
    let isCancelled = false;
    let isLocationRequestInFlight = false;
    let hasResolvedBrowserLocation = false;
    let permissionStatus = null;
    let permissionRetryTimerId = null;
    let permissionRetryCount = 0;
    const maxPermissionRetryCount = 60;
    let grantedLocationRefreshTimerId = null;
    let grantedLocationRefreshCount = 0;
    const maxGrantedLocationRefreshCount = 5;
    let hasCompletedGrantedLocationRefresh = false;
    let locationWatchId = null;

    const updateLocationFromPosition = (position) => {
      if (isCancelled) return;

      const location = normalizeCoordinates({
        latitude: position?.coords?.latitude,
        longitude: position?.coords?.longitude,
      });
      if (!location) return;

      const bounds = getAddressSearchBoundsFromLocation(location);
      const mapCenter = getLocalMapCenterFromLocation(location);

      if (!bounds && !mapCenter) return;

      hasResolvedBrowserLocation = true;
      if (bounds) {
        setAddressSearchBounds((currentBounds) =>
          JSON.stringify(currentBounds) === JSON.stringify(bounds) ? currentBounds : bounds
        );
      }
      if (mapCenter && shouldApplyBrowserMapCenterRef.current) {
        setLocalMapCenter((currentCenter) =>
          areMapCentersEqual(currentCenter, mapCenter) ? currentCenter : mapCenter
        );
      }
    };

    const stopLocationWatch = () => {
      if (locationWatchId === null) return;

      navigator.geolocation?.clearWatch?.(locationWatchId);
      locationWatchId = null;
    };

    const startLocationWatch = () => {
      if (isCancelled || locationWatchId !== null || !navigator.geolocation?.watchPosition) return;

      try {
        locationWatchId = navigator.geolocation.watchPosition(
          updateLocationFromPosition,
          () => {},
          browserGeolocationOptions
        );
      } catch {
        locationWatchId = null;
      }
    };

    const requestBrowserLocation = () => {
      if (isCancelled || isLocationRequestInFlight || !navigator.geolocation) return;

      isLocationRequestInFlight = true;
      try {
        navigator.geolocation?.getCurrentPosition(
          (position) => {
            isLocationRequestInFlight = false;
            updateLocationFromPosition(position);
          },
          () => {
            isLocationRequestInFlight = false;
          },
          browserGeolocationOptions
        );
      } catch {
        isLocationRequestInFlight = false;
      }
    };

    const stopPermissionRetry = () => {
      if (!permissionRetryTimerId) return;

      window.clearInterval(permissionRetryTimerId);
      permissionRetryTimerId = null;
    };

    const stopGrantedLocationRefresh = () => {
      if (!grantedLocationRefreshTimerId) return;

      window.clearInterval(grantedLocationRefreshTimerId);
      grantedLocationRefreshTimerId = null;
    };

    const startGrantedLocationRefresh = () => {
      if (isCancelled || hasCompletedGrantedLocationRefresh || grantedLocationRefreshTimerId) return;

      startLocationWatch();
      requestBrowserLocation();
      grantedLocationRefreshCount = 0;
      stopGrantedLocationRefresh();
      grantedLocationRefreshTimerId = window.setInterval(() => {
        if (isCancelled) {
          stopGrantedLocationRefresh();
          return;
        }

        grantedLocationRefreshCount += 1;
        if (grantedLocationRefreshCount > maxGrantedLocationRefreshCount) {
          hasCompletedGrantedLocationRefresh = true;
          stopGrantedLocationRefresh();
          return;
        }

        requestBrowserLocation();
      }, 1000);
    };

    const handlePermissionChange = () => {
      if (permissionStatus?.state === 'granted') {
        startLocationWatch();
        startGrantedLocationRefresh();
      }
      if (permissionStatus?.state === 'denied') {
        stopPermissionRetry();
        stopGrantedLocationRefresh();
        stopLocationWatch();
        hasCompletedGrantedLocationRefresh = true;
      }
    };

    const setPermissionStatus = (status) => {
      if (!status || permissionStatus === status) return;

      permissionStatus?.removeEventListener?.('change', handlePermissionChange);
      permissionStatus = status;
      permissionStatus.addEventListener?.('change', handlePermissionChange);
      permissionStatus.onchange = handlePermissionChange;
    };

    const checkPermissionAndRequestLocation = () => {
      if (isCancelled) return;
      if (hasResolvedBrowserLocation && permissionStatus?.state === 'granted') {
        startLocationWatch();
        stopPermissionRetry();
        return;
      }

      permissionRetryCount += 1;
      if (permissionRetryCount > maxPermissionRetryCount && permissionRetryTimerId) {
        stopPermissionRetry();
        return;
      }

      let permissionQuery = null;

      try {
        permissionQuery = navigator.permissions?.query?.({ name: 'geolocation' });
      } catch {
        requestBrowserLocation();
        return;
      }

      if (!permissionQuery?.then) {
        requestBrowserLocation();
        return;
      }

      permissionQuery
        .then((status) => {
          if (isCancelled) return;

          setPermissionStatus(status);
          if (status.state === 'granted') {
            startLocationWatch();
            startGrantedLocationRefresh();
            stopPermissionRetry();
          }
          if (status.state === 'denied') {
            stopPermissionRetry();
          }
        })
        .catch(() => {});
    };

    const handlePageRecheck = () => {
      if (document.visibilityState === 'hidden') return;
      if (permissionStatus?.state === 'granted') {
        startLocationWatch();
        startGrantedLocationRefresh();
        return;
      }

      checkPermissionAndRequestLocation();
    };

    requestBrowserLocation();
    checkPermissionAndRequestLocation();

    permissionRetryTimerId = window.setInterval(checkPermissionAndRequestLocation, 1000);
    window.addEventListener('focus', handlePageRecheck);
    document.addEventListener('visibilitychange', handlePageRecheck);

    return () => {
      isCancelled = true;
      stopPermissionRetry();
      stopGrantedLocationRefresh();
      stopLocationWatch();
      window.removeEventListener('focus', handlePageRecheck);
      document.removeEventListener('visibilitychange', handlePageRecheck);
      permissionStatus?.removeEventListener?.('change', handlePermissionChange);
      if (permissionStatus) permissionStatus.onchange = null;
    };
  }, []);

  useEffect(() => {
    addressAutocompleteBoundsRef.current = addressSearchBounds;
    autocompleteRef.current?.setBounds?.(addressSearchBounds);
  }, [addressSearchBounds]);

  useEffect(() => {
    isFindHomesAroundLocationsModeRef.current = isFindHomesAroundLocationsMode;
    if (!autocompleteRef.current) return;

    const nextAutocompleteTypes = getAddressAutocompleteTypes(isFindHomesAroundLocationsMode);
    autocompleteRef.current.setTypes?.(nextAutocompleteTypes);
    autocompleteRef.current.setOptions?.(getAddressAutocompleteOptions({
      bounds: addressAutocompleteBoundsRef.current,
      onlyLocations: isFindHomesAroundLocationsMode,
    }));
  }, [isFindHomesAroundLocationsMode]);

  useEffect(() => {
    if (!addressInputRef.current || !googleMapsApiKey) return;

    let isCancelled = false;

    loadGoogleMapsLibraries().then(({ Autocomplete }) => {
      if (isCancelled || !addressInputRef.current || !Autocomplete) return;

      if (autocompleteRef.current && autocompleteInputElementRef.current === addressInputRef.current) return;

      autocompletePlaceChangedListenerRef.current?.remove?.();
      autocompletePlaceChangedListenerRef.current = null;
      autocompleteRef.current = null;
      autocompleteInputElementRef.current = addressInputRef.current;

      const autocompleteOptions = getAddressAutocompleteOptions({
        bounds: addressAutocompleteBoundsRef.current,
        fields: ['address_components', 'formatted_address', 'geometry', 'name', 'place_id', 'types'],
        onlyLocations: isFindHomesAroundLocationsModeRef.current,
      });

      autocompleteRef.current = new Autocomplete(addressInputRef.current, autocompleteOptions);

      autocompletePlaceChangedListenerRef.current = autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace();
        const onlyLocations = isFindHomesAroundLocationsModeRef.current;
        const restrictionMessage = getPlaceSearchRestrictionMessage({
          addressComponents: place?.address_components,
          onlyLocations,
          types: place?.types,
        });

        if (restrictionMessage) {
          setMapStatus('error');
          setMapError(restrictionMessage);
          return;
        }

        setMapError('');
        const placeLocation = place?.geometry?.location;
        const placeCoordinates = placeLocation
          ? {
              latitude: typeof placeLocation.lat === 'function' ? placeLocation.lat() : placeLocation.lat,
              longitude: typeof placeLocation.lng === 'function' ? placeLocation.lng() : placeLocation.lng,
            }
          : null;
        const placeBounds =
          normalizeAddressBounds(place?.geometry?.bounds) ||
          normalizeAddressBounds(place?.geometry?.viewport);
        const placeViewportBounds = normalizeAddressBounds(place?.geometry?.viewport);

        const placeAddress = place?.formatted_address || place?.name;

        if (placeAddress) {
          addStartingAddressValueRef.current?.(placeAddress, placeCoordinates, placeBounds, {
            addressComponents: place.address_components,
            locationFilter: getPlaceLocationFilter({
              addressComponents: place.address_components,
              types: place.types,
            }),
            placeId: place.place_id,
            placeTypes: place.types,
            viewportBounds: placeViewportBounds,
          });
        }
      });
    }).catch(() => {});

    return () => {
      isCancelled = true;
      autocompletePlaceChangedListenerRef.current?.remove?.();
      autocompletePlaceChangedListenerRef.current = null;
      autocompleteRef.current = null;
      autocompleteInputElementRef.current = null;
    };
  }, [addressEntrySource, isFindHomesAroundAddressesMode, isFindHomesAroundLocationsMode]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof MutationObserver === 'undefined') return undefined;

    const cleanAutocompleteDropdown = () => {
      document.querySelectorAll('.pac-container').forEach((container) => {
        const cleanNode = (node) => {
          if (node.nodeType === Node.TEXT_NODE) {
            const nextText = getAutocompleteDisplayText(node.textContent);
            if (nextText !== node.textContent) node.textContent = nextText;
            return;
          }

          node.childNodes?.forEach(cleanNode);
        };

        cleanNode(container);
      });
    };

    const observer = new MutationObserver(cleanAutocompleteDropdown);
    observer.observe(document.body, {
      characterData: true,
      childList: true,
      subtree: true,
    });
    cleanAutocompleteDropdown();

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isContactTypeFilterSyncPending) {
      radiusEstimateRunRef.current += 1;
      return undefined;
    }

    if (!isNearbyRadiusMode) {
      radiusEstimateRunRef.current += 1;
      return undefined;
    }

    if (usesSharedAddressSettings && startingAddressCount > 1) {
      radiusEstimateRunRef.current += 1;
      return undefined;
    }

    if (mapStatus !== 'ready' || !mapAddress || !coordinates || !radiusMiles) {
      radiusEstimateRunRef.current += 1;
      setEstimatedFilterKey('');
      setEstimatedHomes(null);
      setEstimatedRadiusMiles(null);
      setEstimateStatus('idle');
      return undefined;
    }

    const normalizedSelectedRadius = normalizeRadiusMiles(radiusMiles);
    const currentRadiusEstimate = currentRadiusEstimateRef.current;
    const hasReusableCurrentRadiusEstimate =
      currentRadiusEstimate.estimatedHomes !== null &&
      Number(currentRadiusEstimate.estimatedRadiusMiles) === normalizedSelectedRadius &&
      currentRadiusEstimate.estimatedFilterKey === selectedEstimateKey;

    if (hasReusableCurrentRadiusEstimate) {
      radiusEstimateRunRef.current += 1;
      if (currentRadiusEstimate.estimateStatus !== 'ready') setEstimateStatus('ready');
      setEstimateError('');
      return undefined;
    }

    let isCancelled = false;
    const estimateRunId = radiusEstimateRunRef.current + 1;
    radiusEstimateRunRef.current = estimateRunId;
    const isActiveEstimate = () => !isCancelled && radiusEstimateRunRef.current === estimateRunId;

    setEstimateStatus('loading');
    setEstimateError('');
    if (usesSharedAddressSettings && mapAddress) {
      const activeAddressKey = getAddressKey(mapAddress);
      setAddressEstimateStatusByKey((currentStatuses) => ({
        ...currentStatuses,
        [activeAddressKey]: 'loading',
      }));
    }

    const controller = new AbortController();
    radiusEstimateAbortControllerRef.current?.abort?.();
    radiusEstimateAbortControllerRef.current = controller;
    if (radiusEstimateDebounceRef.current) window.clearTimeout(radiusEstimateDebounceRef.current);

    radiusEstimateDebounceRef.current = window.setTimeout(() => {
      if (!isActiveEstimate() || controller.signal.aborted) return;

      requestRadiusEstimate(radiusMiles, controller.signal)
        .then((estimate) => {
          if (!isActiveEstimate()) return;

          setEstimatedHomes(estimate.estimatedHomes);
          setEstimatedFilterKey(selectedEstimateKey);
          setEstimatedRadiusMiles(estimate.radiusMiles);
          setIsDataAxleConfigured(estimate.dataAxleConfigured !== false);
          setEstimateStatus('ready');
          setEstimateError('');

          if (usesSharedAddressSettings) {
            const activeAddressKey = mapAddress ? getAddressKey(mapAddress) : '';

            if (activeAddressKey) {
              setAddressEstimateStatusByKey((currentStatuses) => {
                if (!currentStatuses[activeAddressKey]) return currentStatuses;

                const nextStatuses = { ...currentStatuses };
                delete nextStatuses[activeAddressKey];
                return nextStatuses;
              });
              setAddressEstimateResultByKey((currentResults) => {
                const nextResult = {
                  estimatedFilterKey: selectedEstimateKey,
                  estimatedHomes: estimate.estimatedHomes,
                  estimateStatus: 'ready',
                  estimatedRadiusMiles: estimate.radiusMiles,
                  isDataAxleConfigured: estimate.dataAxleConfigured !== false,
                };
                const currentResult = currentResults[activeAddressKey];
                const isSameEstimate =
                  currentResult?.estimatedFilterKey === nextResult.estimatedFilterKey &&
                  currentResult?.estimatedHomes === nextResult.estimatedHomes &&
                  currentResult?.estimateStatus === nextResult.estimateStatus &&
                  Number(currentResult?.estimatedRadiusMiles) === Number(nextResult.estimatedRadiusMiles) &&
                  currentResult?.isDataAxleConfigured === nextResult.isDataAxleConfigured;

                if (isSameEstimate) return currentResults;

                return {
                  ...currentResults,
                  [activeAddressKey]: nextResult,
                };
              });
            }
          }
        })
        .catch((error) => {
          if (!isActiveEstimate() || isCanceledRequest(error)) return;

          logNeighborhoodEstimateError('Active address estimate request failed.', {
            address: mapAddress,
            error: error?.message,
            responseData: error?.response?.data,
            status: error?.response?.status,
          });
          setEstimatedHomes(null);
          setEstimatedFilterKey('');
          setEstimatedRadiusMiles(null);
          setEstimateStatus('error');
          setEstimateError('Could not load contact estimate.');
          notifyEstimateRequestFailure();

          if (usesSharedAddressSettings) {
            const activeAddressKey = mapAddress ? getAddressKey(mapAddress) : '';

            if (activeAddressKey) {
              setAddressEstimateStatusByKey((currentStatuses) => ({
                ...currentStatuses,
                [activeAddressKey]: 'error',
              }));
              setAddressEstimateResultByKey((currentResults) => {
                if (!currentResults[activeAddressKey]) return currentResults;

                const nextResults = { ...currentResults };
                delete nextResults[activeAddressKey];
                return nextResults;
              });
            }
          }
        });
    }, estimateRequestDebounceMs);

    return () => {
      isCancelled = true;
      if (radiusEstimateDebounceRef.current) {
        window.clearTimeout(radiusEstimateDebounceRef.current);
        radiusEstimateDebounceRef.current = null;
      }
      if (radiusEstimateAbortControllerRef.current === controller) {
        radiusEstimateAbortControllerRef.current = null;
      }
      controller.abort();
      if (radiusEstimateRunRef.current === estimateRunId) {
        radiusEstimateRunRef.current += 1;
      }
    };
  }, [
    coordinates,
    isContactTypeFilterSyncPending,
    isNearbyRadiusMode,
    mapAddress,
    mapStatus,
    notifyEstimateRequestFailure,
    radiusMiles,
    requestRadiusEstimate,
    selectedEstimateKey,
    selectedFilterPayloadKey,
    startingAddressCount,
    usesSharedAddressSettings,
  ]);

  useEffect(() => {
    if (!multipleAddressRadiusEstimateKey || !multipleAddressRadiusEstimateConfigs.length) return undefined;

    const runId = multipleAddressEstimateRunRef.current + 1;
    const controller = new AbortController();
    const batchConfigs = multipleAddressRadiusEstimateConfigs;
    const batchInputKey = multipleAddressEstimateInputKey;
    multipleAddressEstimateRunRef.current = runId;
    multipleAddressEstimateAbortControllerRef.current?.abort?.();
    multipleAddressEstimateAbortControllerRef.current = controller;
    if (multipleAddressEstimateDebounceRef.current) {
      window.clearTimeout(multipleAddressEstimateDebounceRef.current);
    }
    let cancelBulkEstimateRequest = () => {};
    let removeBulkEstimateListener = () => {};
    let clearPendingBulkEstimateEvents = () => {};

    const applyAddressEstimateFailure = (config, error) => {
      if (
        multipleAddressEstimateRunRef.current !== runId ||
        pendingAddressEstimateRequestKeysRef.current.get(config.addressKey) !== config.estimateRequestKey
      ) return;

      pendingAddressEstimateRequestKeysRef.current.delete(config.addressKey);

      if (error && !isCanceledRequest(error)) {
        logNeighborhoodEstimateError('Batch address estimate request failed.', {
          address: config.address,
          error: error?.message,
          responseData: error?.response?.data,
          status: error?.response?.status,
        });
      }

      setAddressEstimateStatusByKey((currentStatuses) => ({
        ...currentStatuses,
        [config.addressKey]: 'error',
      }));

      setAddressEstimateResultByKey((currentResults) => {
        if (!currentResults[config.addressKey]) return currentResults;

        const nextResults = { ...currentResults };
        delete nextResults[config.addressKey];
        return nextResults;
      });

      if (config.addressKey === getAddressKey(mapAddress)) {
        setEstimatedHomes(null);
        setEstimatedFilterKey('');
        setEstimatedRadiusMiles(null);
        setEstimateStatus('error');
        setEstimateError('Could not load contact estimate.');
      }
    };

    const applyAddressEstimateSuccess = (config, estimateData) => {
      if (
        multipleAddressEstimateRunRef.current !== runId ||
        pendingAddressEstimateRequestKeysRef.current.get(config.addressKey) !== config.estimateRequestKey
      ) return;

      const estimatedCount = parseNullableNumber(estimateData?.estimatedHomes);
      if (estimatedCount === null) {
        logNeighborhoodEstimateError('Batch address estimate response did not include estimatedHomes.', {
          address: config.address,
          responseData: estimateData,
        });
        applyAddressEstimateFailure(config);
        return false;
      }

      pendingAddressEstimateRequestKeysRef.current.delete(config.addressKey);

      setAddressEstimateStatusByKey((currentStatuses) => {
        if (!currentStatuses[config.addressKey]) return currentStatuses;

        const nextStatuses = { ...currentStatuses };
        delete nextStatuses[config.addressKey];
        return nextStatuses;
      });

      setAddressEstimateResultByKey((currentResults) => {
        const estimatedRadiusMiles = normalizeRadiusMiles(radiusMiles);
        const nextResult = {
          estimatedFilterKey: selectedEstimateKey,
          estimatedHomes: estimatedCount,
          estimateStatus: 'ready',
          estimatedRadiusMiles,
          isDataAxleConfigured: estimateData?.dataAxleConfigured !== false,
        };
        const currentResult = currentResults[config.addressKey];
        const isSameEstimate =
          currentResult?.estimatedFilterKey === nextResult.estimatedFilterKey &&
          currentResult?.estimatedHomes === nextResult.estimatedHomes &&
          currentResult?.estimateStatus === nextResult.estimateStatus &&
          Number(currentResult?.estimatedRadiusMiles) === estimatedRadiusMiles &&
          currentResult?.isDataAxleConfigured === nextResult.isDataAxleConfigured;

        if (isSameEstimate) return currentResults;

        return {
          ...currentResults,
          [config.addressKey]: nextResult,
        };
      });

      if (config.addressKey === getAddressKey(mapAddress)) {
        setEstimatedHomes(estimatedCount);
        setEstimatedFilterKey(selectedEstimateKey);
        setEstimatedRadiusMiles(normalizeRadiusMiles(radiusMiles));
        setIsDataAxleConfigured(estimateData?.dataAxleConfigured !== false);
        setEstimateStatus('ready');
        setEstimateError('');
      }

      return true;
    };

    setIsWaitingForFirstBulkAddressEstimate(true);

    multipleAddressEstimateDebounceRef.current = window.setTimeout(() => {
      if (
        multipleAddressEstimateRunRef.current !== runId ||
        controller.signal.aborted
      ) return;

      batchConfigs.forEach((config) => {
        pendingAddressEstimateRequestKeysRef.current.set(config.addressKey, config.estimateRequestKey);
      });

      setAddressEstimateStatusByKey((currentStatuses) => {
        const nextStatuses = { ...currentStatuses };
        batchConfigs.forEach((config) => {
          nextStatuses[config.addressKey] = 'loading';
        });
        return nextStatuses;
      });
      setAddressEstimateResultByKey((currentResults) => {
        let didUpdate = false;
        const nextResults = { ...currentResults };

        batchConfigs.forEach((config) => {
          if (!nextResults[config.addressKey]) return;

          didUpdate = true;
          delete nextResults[config.addressKey];
        });

        return didUpdate ? nextResults : currentResults;
      });

      if (batchConfigs.some((config) => config.addressKey === getAddressKey(mapAddress))) {
        setEstimatedHomes(null);
        setEstimatedFilterKey('');
        setEstimatedRadiusMiles(null);
        setEstimateStatus('loading');
        setEstimateError('');
      }
      const requestId = `address-estimates-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const eventName = `${bulkAddressEstimateEventPrefix}-${requestId}`;
      const socketConnection = findNewContactsSocketRef.current;
      const configByRequestKey = new Map(
        batchConfigs.map((config) => [config.estimateRequestKey, config])
      );
      const completedAddressKeys = new Set();
      let completedEstimateCount = 0;
      let failedEstimateCount = 0;
      let removedBulkEstimateListener = false;
      let didCancelBulkEstimateRequest = false;
      let handleBulkEstimateEvent = () => {};
      let pendingBulkEstimateEvents = [];
      let pendingBulkEstimateCompleteEvent = null;
      let isBulkEstimateFlushScheduled = false;
      let bulkEstimateFlushFrame = null;

      cancelBulkEstimateRequest = () => {
        if (didCancelBulkEstimateRequest) return;
        didCancelBulkEstimateRequest = true;
        if (activeBulkAddressEstimateCancelRef.current === cancelBulkEstimateRequest) {
          activeBulkAddressEstimateCancelRef.current = null;
        }
        request.post(`find-new-contacts/neighborhood-estimates/${encodeURIComponent(requestId)}/cancel`)
          .catch(() => {});
      };
      activeBulkAddressEstimateCancelRef.current?.();
      activeBulkAddressEstimateCancelRef.current = cancelBulkEstimateRequest;

      removeBulkEstimateListener = () => {
        if (removedBulkEstimateListener) return;
        removedBulkEstimateListener = true;
        socketConnection?.off?.(eventName, handleBulkEstimateEvent);
      };

      const finishBulkEstimateBatch = () => {
        if (
          multipleAddressEstimateRunRef.current !== runId ||
          controller.signal.aborted
        ) return;

        removeBulkEstimateListener();
        if (activeBulkAddressEstimateCancelRef.current === cancelBulkEstimateRequest) {
          activeBulkAddressEstimateCancelRef.current = null;
        }
        cancelBulkEstimateRequest = () => {};
        setIsWaitingForFirstBulkAddressEstimate(false);

        if (failedEstimateCount > 0) {
          notifyEstimateRequestFailure();
        }
      };

      const applyBulkEstimateResults = (results) => {
        if (!results.length) return;

        let activeResult = null;
        const activeAddressKey = mapAddress ? getAddressKey(mapAddress) : '';

        setIsWaitingForFirstBulkAddressEstimate(false);
        setAddressEstimateStatusByKey((currentStatuses) => {
          let didUpdate = false;
          const nextStatuses = { ...currentStatuses };

          results.forEach((result) => {
            if (result.status === 'ready') {
              if (nextStatuses[result.config.addressKey]) didUpdate = true;
              delete nextStatuses[result.config.addressKey];
              return;
            }

            if (nextStatuses[result.config.addressKey] !== 'error') didUpdate = true;
            nextStatuses[result.config.addressKey] = 'error';
          });

          return didUpdate ? nextStatuses : currentStatuses;
        });

        setAddressEstimateResultByKey((currentResults) => {
          let didUpdate = false;
          const nextResults = { ...currentResults };

          results.forEach((result) => {
            const { config } = result;

            if (result.status === 'ready') {
              const estimatedRadiusMiles = normalizeRadiusMiles(radiusMiles);
              const nextResult = {
                estimatedFilterKey: selectedEstimateKey,
                estimatedHomes: result.estimatedHomes,
                estimateStatus: 'ready',
                estimatedRadiusMiles,
                isDataAxleConfigured: result.dataAxleConfigured,
              };
              const currentResult = currentResults[config.addressKey];
              const isSameEstimate =
                currentResult?.estimatedFilterKey === nextResult.estimatedFilterKey &&
                currentResult?.estimatedHomes === nextResult.estimatedHomes &&
                currentResult?.estimateStatus === nextResult.estimateStatus &&
                Number(currentResult?.estimatedRadiusMiles) === estimatedRadiusMiles &&
                currentResult?.isDataAxleConfigured === nextResult.isDataAxleConfigured;

              if (isSameEstimate) return;

              didUpdate = true;
              nextResults[config.addressKey] = nextResult;
              return;
            }

            if (!nextResults[config.addressKey]) return;

            didUpdate = true;
            delete nextResults[config.addressKey];
          });

          return didUpdate ? nextResults : currentResults;
        });

        if (activeAddressKey) {
          activeResult = results.find((result) => result.config.addressKey === activeAddressKey) || null;
        }

        if (!activeResult) return;

        if (activeResult.status === 'ready') {
          setEstimatedHomes(activeResult.estimatedHomes);
          setEstimatedFilterKey(selectedEstimateKey);
          setEstimatedRadiusMiles(normalizeRadiusMiles(radiusMiles));
          setIsDataAxleConfigured(activeResult.dataAxleConfigured);
          setEstimateStatus('ready');
          setEstimateError('');
          return;
        }

        setEstimatedHomes(null);
        setEstimatedFilterKey('');
        setEstimatedRadiusMiles(null);
        setEstimateStatus('error');
        setEstimateError('Could not load contact estimate.');
      };

      const processBulkEstimateEvent = (event = {}) => {
        if (
          multipleAddressEstimateRunRef.current !== runId ||
          controller.signal.aborted
        ) return null;

        const config = configByRequestKey.get(event.requestKey) || batchConfigs.find(
          (item) => item.addressKey === event.addressKey
        );
        if (!config || completedAddressKeys.has(config.addressKey)) return null;

        const isExpectedRequest =
          pendingAddressEstimateRequestKeysRef.current.get(config.addressKey) === config.estimateRequestKey;
        if (!isExpectedRequest) return null;

        completedAddressKeys.add(config.addressKey);
        completedEstimateCount += 1;
        pendingAddressEstimateRequestKeysRef.current.delete(config.addressKey);

        const estimatedCount = parseNullableNumber(event.estimatedHomes);
        if (event.status === 'ready' && estimatedCount !== null) {
          return {
            config,
            dataAxleConfigured: event.dataAxleConfigured !== false,
            estimatedHomes: estimatedCount,
            status: 'ready',
          };
        }

        failedEstimateCount += 1;
        if (event.error) {
          logNeighborhoodEstimateError('Batch address estimate request failed.', {
            address: config.address,
            error: event.error,
            responseData: event,
          });
        } else if (event.status === 'ready') {
          logNeighborhoodEstimateError('Batch address estimate response did not include estimatedHomes.', {
            address: config.address,
            responseData: event,
          });
        }

        return {
          config,
          status: 'error',
        };
      };

      const flushBulkEstimateEvents = () => {
        bulkEstimateFlushFrame = null;
        const estimateEvents = pendingBulkEstimateEvents.splice(0, bulkEstimateResultChunkSize);
        const results = estimateEvents
          .map(processBulkEstimateEvent)
          .filter(Boolean);

        applyBulkEstimateResults(results);

        if (completedEstimateCount >= batchConfigs.length) {
          isBulkEstimateFlushScheduled = false;
          finishBulkEstimateBatch();
          return;
        }

        if (pendingBulkEstimateEvents.length) {
          bulkEstimateFlushFrame = window.setTimeout(flushBulkEstimateEvents, 0);
          return;
        }

        isBulkEstimateFlushScheduled = false;
        if (pendingBulkEstimateCompleteEvent) {
          const completeEvent = pendingBulkEstimateCompleteEvent;
          pendingBulkEstimateCompleteEvent = null;
          handleBulkEstimateEvent(completeEvent);
        }
      };

      const scheduleBulkEstimateEvent = (event = {}) => {
        pendingBulkEstimateEvents.push(event);
        if (isBulkEstimateFlushScheduled) return;

        isBulkEstimateFlushScheduled = true;
        bulkEstimateFlushFrame = window.requestAnimationFrame(flushBulkEstimateEvents);
      };

      clearPendingBulkEstimateEvents = () => {
        pendingBulkEstimateEvents = [];
        pendingBulkEstimateCompleteEvent = null;
        if (!bulkEstimateFlushFrame) return;

        window.cancelAnimationFrame(bulkEstimateFlushFrame);
        window.clearTimeout(bulkEstimateFlushFrame);
        isBulkEstimateFlushScheduled = false;
        bulkEstimateFlushFrame = null;
      };

      handleBulkEstimateEvent = (event = {}) => {
        if (event.type === 'estimate') {
          scheduleBulkEstimateEvent(event);
          return;
        }

        if (event.type === 'complete') {
          if (pendingBulkEstimateEvents.length || isBulkEstimateFlushScheduled) {
            pendingBulkEstimateCompleteEvent = event;
            if (!isBulkEstimateFlushScheduled) {
              isBulkEstimateFlushScheduled = true;
              bulkEstimateFlushFrame = window.setTimeout(flushBulkEstimateEvents, 0);
            }
            return;
          }

          const incompleteResults = [];
          batchConfigs.forEach((config) => {
            if (completedAddressKeys.has(config.addressKey)) return;
            if (pendingAddressEstimateRequestKeysRef.current.get(config.addressKey) !== config.estimateRequestKey) return;

            completedAddressKeys.add(config.addressKey);
            completedEstimateCount += 1;
            pendingAddressEstimateRequestKeysRef.current.delete(config.addressKey);
            failedEstimateCount += 1;
            incompleteResults.push({
              config,
              status: 'error',
            });
          });

          applyBulkEstimateResults(incompleteResults);
          finishBulkEstimateBatch();
        }
      };

      socketConnection?.off?.(eventName, handleBulkEstimateEvent);
      socketConnection?.on?.(eventName, handleBulkEstimateEvent);

      waitForSocketConnection(socketConnection)
        .then(() => withTimeout(
          request.post('find-new-contacts/neighborhood-estimates', {
            requestId,
            requests: batchConfigs.map((config) => ({
              addressKey: config.addressKey,
              requestKey: config.estimateRequestKey,
              address: config.address,
              addressConfigs: [config],
              contactType: activeContactType,
              latitude: config.coordinates.latitude,
              longitude: config.coordinates.longitude,
              radiusMiles,
              filters: selectedFilterPayload,
              ...estimateCleanupPayload,
            })),
          }, { signal: controller.signal }),
          estimateRequestTimeoutMs,
          'Estimate request timed out.'
        ))
        .catch((error) => {
          if (
            multipleAddressEstimateRunRef.current !== runId ||
            controller.signal.aborted ||
            isCanceledRequest(error)
          ) return;

          failedEstimateCount = batchConfigs.length;
          const failedResults = [];
          batchConfigs.forEach((config) => {
            if (pendingAddressEstimateRequestKeysRef.current.get(config.addressKey) === config.estimateRequestKey) {
              pendingAddressEstimateRequestKeysRef.current.delete(config.addressKey);
              failedResults.push({
                config,
                status: 'error',
              });
            }
          });
          logNeighborhoodEstimateError('Batch address estimate request failed.', {
            error: error?.message,
            responseData: error?.response?.data,
            status: error?.response?.status,
          });
          applyBulkEstimateResults(failedResults);
          finishBulkEstimateBatch();
        });
    }, estimateRequestDebounceMs);

    return () => {
      const isSameInputBatch =
        multipleAddressEstimateRunRef.current === runId &&
        multipleAddressEstimateInputKeyRef.current === batchInputKey &&
        !controller.signal.aborted;
      if (isSameInputBatch) return;

      if (multipleAddressEstimateDebounceRef.current) {
        window.clearTimeout(multipleAddressEstimateDebounceRef.current);
        multipleAddressEstimateDebounceRef.current = null;
      }
      batchConfigs.forEach((config) => {
        if (pendingAddressEstimateRequestKeysRef.current.get(config.addressKey) === config.estimateRequestKey) {
          pendingAddressEstimateRequestKeysRef.current.delete(config.addressKey);
        }
      });
      clearPendingBulkEstimateEvents();
      removeBulkEstimateListener();
      cancelBulkEstimateRequest();
      setIsWaitingForFirstBulkAddressEstimate(false);
      if (multipleAddressEstimateAbortControllerRef.current === controller) {
        multipleAddressEstimateAbortControllerRef.current = null;
      }
      controller.abort();
    };
  }, [
    multipleAddressRadiusEstimateKey,
    multipleAddressEstimateInputKey,
    activeContactType,
    estimateCleanupPayload,
    mapAddress,
    notifyEstimateRequestFailure,
    radiusMiles,
    selectedFilterPayload,
    selectedEstimateKey,
    selectedFilterPayloadKey,
  ]);

  useEffect(() => {
    if (isContactTypeFilterSyncPending || !addressAggregateEstimateKey) {
      addressAggregateEstimateRunRef.current += 1;
      setAddressAggregateEstimatedHomes(null);
      setAddressAggregateEstimateStatus('idle');
      return undefined;
    }

    const aggregateRunId = addressAggregateEstimateRunRef.current + 1;
    const controller = new AbortController();
    addressAggregateEstimateRunRef.current = aggregateRunId;
    addressAggregateEstimateAbortControllerRef.current?.abort?.();
    addressAggregateEstimateAbortControllerRef.current = controller;
    if (addressAggregateEstimateDebounceRef.current) {
      window.clearTimeout(addressAggregateEstimateDebounceRef.current);
    }
    setAddressAggregateEstimatedHomes(null);
    setAddressAggregateEstimateStatus('loading');

    addressAggregateEstimateDebounceRef.current = window.setTimeout(() => {
      if (
        addressAggregateEstimateRunRef.current !== aggregateRunId ||
        controller.signal.aborted
      ) return;

      withTimeout(
        request.post('find-new-contacts/neighborhood-estimate', {
          address: 'Selected addresses',
          addresses: listAddressConfigs.map((config) => config.address).filter(Boolean),
          addressConfigs: listAddressConfigs,
          areas: addressAggregateRequestAreas,
          contactType: activeContactType,
          filters: selectedEstimateFilterPayload,
          ...estimateCleanupPayload,
        }, { signal: controller.signal }),
        estimateRequestTimeoutMs,
        'Estimate request timed out.'
      )
        .then((response) => {
          if (addressAggregateEstimateRunRef.current !== aggregateRunId) return;

          const estimatedCount = parseNullableNumber(response.data?.data?.estimatedHomes);
          if (estimatedCount === null) {
            logNeighborhoodEstimateError('Address aggregate estimate response did not include estimatedHomes.', {
              responseData: response.data,
              status: response.status,
            });
            setAddressAggregateEstimateStatus('error');
            notifyEstimateRequestFailure();
            return;
          }

          setAddressAggregateEstimatedHomes(estimatedCount);
          setAddressAggregateEstimateStatus('ready');
          setIsDataAxleConfigured(response.data?.data?.dataAxleConfigured !== false);
        })
        .catch((error) => {
          if (addressAggregateEstimateRunRef.current !== aggregateRunId || isCanceledRequest(error)) return;

          logNeighborhoodEstimateError('Address aggregate estimate request failed.', {
            error: error?.message,
            responseData: error?.response?.data,
            status: error?.response?.status,
          });
          setAddressAggregateEstimatedHomes(null);
          setAddressAggregateEstimateStatus('error');
          notifyEstimateRequestFailure();
        });
    }, estimateRequestDebounceMs);

    return () => {
      if (addressAggregateEstimateDebounceRef.current) {
        window.clearTimeout(addressAggregateEstimateDebounceRef.current);
        addressAggregateEstimateDebounceRef.current = null;
      }
      if (addressAggregateEstimateAbortControllerRef.current === controller) {
        addressAggregateEstimateAbortControllerRef.current = null;
      }
      controller.abort();
      if (addressAggregateEstimateRunRef.current === aggregateRunId) {
        addressAggregateEstimateRunRef.current += 1;
      }
    };
  }, [
    activeContactType,
    addressAggregateEstimateKey,
    addressAggregateRequestAreas,
    estimateCleanupPayload,
    isContactTypeFilterSyncPending,
    listAddressConfigs,
    notifyEstimateRequestFailure,
    selectedEstimateFilterPayload,
  ]);

  useEffect(() => {
    manualAreaEstimateByKeyRef.current = manualAreaEstimateByKey;
  }, [manualAreaEstimateByKey]);

  useEffect(() => {
    if (isContactTypeFilterSyncPending || !isManualAreaMode) return undefined;

    const estimateAddress = mapAddress || (isFindHomesAroundLocationsMode ? 'Selected map area' : '');

    if (mapStatus !== 'ready' || !estimateAddress) {
      setEstimatedHomes(null);
      setEstimatedFilterKey('');
      setEstimatedRadiusMiles(null);
      setEstimateStatus('idle');
      setManualAreaEstimateByKey({});
      return undefined;
    }

    let isCancelled = false;

    setEstimateError('');

    if (manualEstimateAreas.length === 0) {
      setEstimatedHomes(null);
      setEstimatedFilterKey('');
      setEstimatedRadiusMiles(null);
      setEstimateStatus('idle');
      setManualAreaEstimateByKey({});
      return;
    }

    const getReusableAreaEstimate = (area, estimates = manualAreaEstimateByKeyRef.current) => {
      const estimate = estimates[area.key];

      if (!estimate || estimate.filterKey !== selectedEstimateKey) return null;

      if (estimate.status === 'loading' && retryingManualAreaEstimateKeysRef.current.has(area.key)) {
        return {
          ...estimate,
          dataAxleConfigured: estimate.dataAxleConfigured !== false,
          estimatedHomes: null,
          filterKey: selectedEstimateKey,
          status: 'loading',
        };
      }

      if (estimate.status === 'error') {
        return {
          ...estimate,
          dataAxleConfigured: estimate.dataAxleConfigured !== false,
          estimatedHomes: null,
          filterKey: selectedEstimateKey,
          status: 'error',
        };
      }

      const estimatedCount = parseNullableNumber(estimate.estimatedHomes);

      if (estimate.status !== 'ready' || estimatedCount === null) return null;

      return {
        ...estimate,
        dataAxleConfigured: estimate.dataAxleConfigured !== false,
        estimatedHomes: estimatedCount,
        filterKey: selectedEstimateKey,
        status: 'ready',
      };
    };
    const areasToEstimate = manualEstimateAreas.filter((area) => !getReusableAreaEstimate(area));

    if (areasToEstimate.length === 0) {
      const reusableEstimatesByKey = Object.fromEntries(
        manualEstimateAreas.map((area) => [area.key, getReusableAreaEstimate(area)])
      );

      setManualAreaEstimateByKey(reusableEstimatesByKey);
      refreshManualAggregateEstimate(reusableEstimatesByKey);

      return () => {
        isCancelled = true;
      };
    }

    const loadingEstimatesByKey = Object.fromEntries(
      manualEstimateAreas.map((area) => [
        area.key,
        getReusableAreaEstimate(area) || {
          dataAxleConfigured: true,
          estimatedHomes: null,
          filterKey: selectedEstimateKey,
          status: 'loading',
        },
      ])
    );

    setManualAreaEstimateByKey(loadingEstimatesByKey);
    manualAreaEstimateByKeyRef.current = loadingEstimatesByKey;
    setEstimatedHomes(null);
    setEstimatedFilterKey('');
    setEstimatedRadiusMiles(null);
    setEstimateStatus('loading');
    refreshManualAggregateEstimate(loadingEstimatesByKey);

    const getManualAreaEstimatePayload = (area) => {
      const basePayload = {
        address: area.address || estimateAddress,
        contactType: activeContactType,
        filters: selectedFilterPayload,
        ...estimateCleanupPayload,
      };
      const requestArea = getManualAreaRequestArea(area);

      if (requestArea.type === 'location' && requestArea.locationFilter) {
        return {
          ...basePayload,
          locationFilter: requestArea.locationFilter,
        };
      }

      if (requestArea.type === 'polygon') {
        return {
          ...basePayload,
          area: requestArea,
        };
      }

      return {
        ...basePayload,
        area: requestArea,
        latitude: requestArea.latitude,
        longitude: requestArea.longitude,
        radiusMiles: requestArea.radiusMiles,
      };
    };

    const runId = manualAreaEstimateRunRef.current + 1;
    const controller = new AbortController();
    const areaRequestConfigs = areasToEstimate.map((area) => {
      const payload = getManualAreaEstimatePayload(area);

      return {
        area,
        estimateRequestKey: stableStringify({
          areaKey: area.key,
          contactType: activeContactType,
          filters: selectedFilterPayloadKey,
          payload,
        }),
        payload,
      };
    });
    manualAreaEstimateRunRef.current = runId;
    manualAreaEstimateAbortControllerRef.current?.abort?.();
    manualAreaEstimateAbortControllerRef.current = controller;
    if (manualAreaEstimateDebounceRef.current) {
      window.clearTimeout(manualAreaEstimateDebounceRef.current);
    }

    const applyManualAreaEstimateResult = (area, estimate) => {
      if (isCancelled || manualAreaEstimateRunRef.current !== runId || controller.signal.aborted) return;

      const nextEstimates = {
        ...manualAreaEstimateByKeyRef.current,
        [area.key]: estimate,
      };

      manualAreaEstimateByKeyRef.current = nextEstimates;
      setManualAreaEstimateByKey(nextEstimates);
      if (manualEstimateAreas.length === 1) {
        refreshManualAggregateEstimate(nextEstimates);
      }
    };

    const finishAreaEstimateBatch = () => {
      if (isCancelled || manualAreaEstimateRunRef.current !== runId || controller.signal.aborted) return;
      if (hasEstimateFailure) notifyEstimateRequestFailure();
      if (manualEstimateAreas.length > 1) {
        refreshManualAggregateEstimate(manualAreaEstimateByKeyRef.current);
      }
    };

    let completedAreaRequestCount = 0;
    let hasEstimateFailure = false;
    let cancelBulkEstimateRequest = () => {};
    let removeBulkEstimateListener = () => {};
    let clearPendingBulkEstimateEvents = () => {};

    manualAreaEstimateDebounceRef.current = window.setTimeout(() => {
      if (isCancelled || manualAreaEstimateRunRef.current !== runId || controller.signal.aborted) return;

      areaRequestConfigs.forEach((config) => {
        pendingManualAreaEstimateRequestKeysRef.current.set(config.area.key, config.estimateRequestKey);
      });

      const requestId = `area-estimates-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const eventName = `${bulkAddressEstimateEventPrefix}-${requestId}`;
      const socketConnection = findNewContactsSocketRef.current;
      const configByRequestKey = new Map(
        areaRequestConfigs.map((config) => [config.estimateRequestKey, config])
      );
      const completedAreaKeys = new Set();
      let removedBulkEstimateListener = false;
      let didCancelBulkEstimateRequest = false;
      let handleBulkEstimateEvent = () => {};
      let pendingBulkEstimateEvents = [];
      let pendingBulkEstimateCompleteEvent = null;
      let isBulkEstimateFlushScheduled = false;
      let bulkEstimateFlushFrame = null;

      cancelBulkEstimateRequest = () => {
        if (didCancelBulkEstimateRequest) return;
        didCancelBulkEstimateRequest = true;
        if (activeBulkManualAreaEstimateCancelRef.current === cancelBulkEstimateRequest) {
          activeBulkManualAreaEstimateCancelRef.current = null;
        }
        request.post(`find-new-contacts/neighborhood-estimates/${encodeURIComponent(requestId)}/cancel`)
          .catch(() => {});
      };
      activeBulkManualAreaEstimateCancelRef.current?.();
      activeBulkManualAreaEstimateCancelRef.current = cancelBulkEstimateRequest;

      removeBulkEstimateListener = () => {
        if (removedBulkEstimateListener) return;
        removedBulkEstimateListener = true;
        socketConnection?.off?.(eventName, handleBulkEstimateEvent);
      };

      const finishBulkAreaEstimateBatch = () => {
        if (isCancelled || manualAreaEstimateRunRef.current !== runId || controller.signal.aborted) return;

        removeBulkEstimateListener();
        if (activeBulkManualAreaEstimateCancelRef.current === cancelBulkEstimateRequest) {
          activeBulkManualAreaEstimateCancelRef.current = null;
        }
        cancelBulkEstimateRequest = () => {};
        finishAreaEstimateBatch();
      };

      const applyBulkAreaEstimateResults = (results) => {
        if (!results.length) return;

        results.forEach((result) => {
          applyManualAreaEstimateResult(result.config.area, {
            dataAxleConfigured: result.dataAxleConfigured !== false,
            estimatedHomes: result.status === 'ready' ? result.estimatedHomes : null,
            filterKey: selectedEstimateKey,
            status: result.status,
          });
        });
      };

      const processBulkAreaEstimateEvent = (event = {}) => {
        if (isCancelled || manualAreaEstimateRunRef.current !== runId || controller.signal.aborted) return null;

        const config = configByRequestKey.get(event.requestKey) || areaRequestConfigs.find(
          (item) => item.area.key === event.addressKey
        );
        if (!config || completedAreaKeys.has(config.area.key)) return null;

        const isExpectedRequest =
          pendingManualAreaEstimateRequestKeysRef.current.get(config.area.key) === config.estimateRequestKey;
        if (!isExpectedRequest) return null;

        completedAreaKeys.add(config.area.key);
        completedAreaRequestCount += 1;
        pendingManualAreaEstimateRequestKeysRef.current.delete(config.area.key);

        const estimatedCount = parseNullableNumber(event.estimatedHomes);
        if (event.status === 'ready' && estimatedCount !== null) {
          return {
            config,
            dataAxleConfigured: event.dataAxleConfigured !== false,
            estimatedHomes: estimatedCount,
            status: 'ready',
          };
        }

        hasEstimateFailure = true;
        if (event.error) {
          logNeighborhoodEstimateError('Manual area estimate request failed.', {
            address: config.area?.address,
            areaKey: config.area?.key,
            error: event.error,
            responseData: event,
          });
        } else if (event.status === 'ready') {
          logNeighborhoodEstimateError('Manual area estimate response did not include estimatedHomes.', {
            address: config.area?.address,
            areaKey: config.area?.key,
            responseData: event,
          });
        }

        return {
          config,
          dataAxleConfigured: event.dataAxleConfigured !== false,
          status: 'error',
        };
      };

      const flushBulkAreaEstimateEvents = () => {
        bulkEstimateFlushFrame = null;
        const estimateEvents = pendingBulkEstimateEvents.splice(0, bulkEstimateResultChunkSize);
        const results = estimateEvents
          .map(processBulkAreaEstimateEvent)
          .filter(Boolean);

        applyBulkAreaEstimateResults(results);

        if (completedAreaRequestCount >= areaRequestConfigs.length) {
          isBulkEstimateFlushScheduled = false;
          finishBulkAreaEstimateBatch();
          return;
        }

        if (pendingBulkEstimateEvents.length) {
          bulkEstimateFlushFrame = window.setTimeout(flushBulkAreaEstimateEvents, 0);
          return;
        }

        isBulkEstimateFlushScheduled = false;
        if (pendingBulkEstimateCompleteEvent) {
          const completeEvent = pendingBulkEstimateCompleteEvent;
          pendingBulkEstimateCompleteEvent = null;
          handleBulkEstimateEvent(completeEvent);
        }
      };

      const scheduleBulkAreaEstimateEvent = (event = {}) => {
        pendingBulkEstimateEvents.push(event);
        if (isBulkEstimateFlushScheduled) return;

        isBulkEstimateFlushScheduled = true;
        bulkEstimateFlushFrame = window.requestAnimationFrame(flushBulkAreaEstimateEvents);
      };

      clearPendingBulkEstimateEvents = () => {
        pendingBulkEstimateEvents = [];
        pendingBulkEstimateCompleteEvent = null;
        if (!bulkEstimateFlushFrame) return;

        window.cancelAnimationFrame(bulkEstimateFlushFrame);
        window.clearTimeout(bulkEstimateFlushFrame);
        isBulkEstimateFlushScheduled = false;
        bulkEstimateFlushFrame = null;
      };

      handleBulkEstimateEvent = (event = {}) => {
        if (event.type === 'estimate') {
          scheduleBulkAreaEstimateEvent(event);
          return;
        }

        if (event.type === 'complete') {
          if (pendingBulkEstimateEvents.length || isBulkEstimateFlushScheduled) {
            pendingBulkEstimateCompleteEvent = event;
            if (!isBulkEstimateFlushScheduled) {
              isBulkEstimateFlushScheduled = true;
              bulkEstimateFlushFrame = window.setTimeout(flushBulkAreaEstimateEvents, 0);
            }
            return;
          }

          const incompleteResults = [];
          areaRequestConfigs.forEach((config) => {
            if (completedAreaKeys.has(config.area.key)) return;
            if (pendingManualAreaEstimateRequestKeysRef.current.get(config.area.key) !== config.estimateRequestKey) return;

            completedAreaKeys.add(config.area.key);
            completedAreaRequestCount += 1;
            pendingManualAreaEstimateRequestKeysRef.current.delete(config.area.key);
            hasEstimateFailure = true;
            incompleteResults.push({
              config,
              dataAxleConfigured: true,
              status: 'error',
            });
          });

          applyBulkAreaEstimateResults(incompleteResults);
          finishBulkAreaEstimateBatch();
        }
      };

      socketConnection?.off?.(eventName, handleBulkEstimateEvent);
      socketConnection?.on?.(eventName, handleBulkEstimateEvent);

      waitForSocketConnection(socketConnection)
        .then(() => withTimeout(
          request.post('find-new-contacts/neighborhood-estimates', {
            requestId,
            requests: areaRequestConfigs.map((config) => ({
              ...config.payload,
              addressKey: config.area.key,
              requestKey: config.estimateRequestKey,
            })),
          }, { signal: controller.signal }),
          estimateRequestTimeoutMs,
          'Estimate request timed out.'
        ))
        .catch((error) => {
          if (
            isCancelled ||
            manualAreaEstimateRunRef.current !== runId ||
            controller.signal.aborted ||
            isCanceledRequest(error)
          ) return;

          const failedResults = [];
          areaRequestConfigs.forEach((config) => {
            if (pendingManualAreaEstimateRequestKeysRef.current.get(config.area.key) === config.estimateRequestKey) {
              pendingManualAreaEstimateRequestKeysRef.current.delete(config.area.key);
              failedResults.push({
                config,
                dataAxleConfigured: true,
                status: 'error',
              });
            }
          });
          hasEstimateFailure = failedResults.length > 0;
          logNeighborhoodEstimateError('Manual area estimate request failed.', {
            error: error?.message,
            responseData: error?.response?.data,
            status: error?.response?.status,
          });
          const validationMessage = getNeighborhoodEstimateValidationMessage(error);
          if (validationMessage) dispatch(failure(validationMessage));
          applyBulkAreaEstimateResults(failedResults);
          completedAreaRequestCount = areaRequestConfigs.length;
          finishBulkAreaEstimateBatch();
        });
    }, estimateRequestDebounceMs);

    return () => {
      isCancelled = true;
      if (manualAreaEstimateDebounceRef.current) {
        window.clearTimeout(manualAreaEstimateDebounceRef.current);
        manualAreaEstimateDebounceRef.current = null;
      }
      areaRequestConfigs.forEach((config) => {
        if (pendingManualAreaEstimateRequestKeysRef.current.get(config.area.key) === config.estimateRequestKey) {
          pendingManualAreaEstimateRequestKeysRef.current.delete(config.area.key);
        }
      });
      clearPendingBulkEstimateEvents();
      removeBulkEstimateListener();
      cancelBulkEstimateRequest();
      if (manualAreaEstimateAbortControllerRef.current === controller) {
        manualAreaEstimateAbortControllerRef.current = null;
      }
      controller.abort();
      if (manualAreaEstimateRunRef.current === runId) {
        manualAreaEstimateRunRef.current += 1;
      }
    };
  }, [
    activeContactType,
    estimateCleanupPayload,
    isContactTypeFilterSyncPending,
    isFindHomesAroundLocationsMode,
    isManualAreaMode,
    mapAddress,
    mapStatus,
    manualEstimateAreas,
    refreshManualAggregateEstimate,
    notifyEstimateRequestFailure,
    selectedFilterPayload,
    selectedEstimateKey,
    selectedFilterPayloadKey,
  ]);

  useEffect(() => {
    if (
      mapStatus !== 'ready' ||
      !isManualAreaMode ||
      !googleMapRef.current
    ) return;

    let isCancelled = false;

    loadGoogleMapsLibraries()
      .then(({ Polygon, maps }) => {
        if (isCancelled || !Polygon) return;

        customPolygonRefs.current.forEach((polygon) => polygon.setMap(null));
        customPolygonRefs.current = customShapes.map((shape) => {
          const paths = shape.map((point) => new maps.LatLng(point.latitude, point.longitude));

          return new Polygon({
            clickable: false,
            fillColor: drawAreaStrokeColor,
            fillOpacity: 0.16,
            map: googleMapRef.current,
            paths,
            strokeColor: drawAreaStrokeColor,
            strokeOpacity: 0.95,
            strokeWeight: 3,
          });
        });
      })
      .catch(() => {});

    return () => {
      isCancelled = true;
    };
  }, [customShapes, isManualAreaMode, mapStatus]);

  useEffect(() => {
    if (
      mapStatus !== 'ready' ||
      !isManualAreaMode ||
      !googleMapRef.current
    ) return;

    let isCancelled = false;

    loadGoogleMapsLibraries()
      .then(({ Polyline, maps }) => {
        if (isCancelled || !Polyline || !maps) return;

        const path = customShapePoints.map((point) => new maps.LatLng(point.latitude, point.longitude));

        if (!customPolylineRef.current) {
          customPolylineRef.current = new Polyline({
            clickable: false,
            geodesic: true,
            map: googleMapRef.current,
            path,
            strokeColor: drawAreaStrokeColor,
            strokeOpacity: 0.95,
            strokeWeight: 3,
          });
        } else {
          customPolylineRef.current.setPath(path);
        }

        customPolylineRef.current.setMap(customShapePoints.length > 0 ? googleMapRef.current : null);

        if (customShapePoints.length > 1) {
          const bounds = new maps.LatLngBounds();
          extendBoundsForShape(bounds, maps, customShapePoints);
          fitGoogleMapToBounds(googleMapRef.current, maps, bounds, 88, { onlyIfNeeded: true });
        }
      })
      .catch(() => {});

    return () => {
      isCancelled = true;
    };
  }, [customShapePoints, isManualAreaMode, mapStatus]);

  useEffect(() => {
    if (
      mapStatus !== 'ready' ||
      !isManualAreaMode ||
      !googleMapRef.current
    ) return;

    let isCancelled = false;

    loadGoogleMapsLibraries()
      .then(({ Circle }) => {
        if (isCancelled || !Circle) return;

        drawRadiusCircleRefs.current.forEach((circle) => circle.setMap(null));

        drawRadiusCircleRefs.current = drawRadiusAreas.map((area) =>
          new Circle({
            center: {
              lat: area.center.latitude,
              lng: area.center.longitude,
            },
            fillColor: drawAreaStrokeColor,
            fillOpacity: 0.16,
            map: googleMapRef.current,
            radius: area.radiusMiles * 1609.34,
            strokeColor: drawAreaStrokeColor,
            strokeOpacity: 0.95,
            strokeWeight: 3,
          })
        );
      })
      .catch(() => {});

    return () => {
      isCancelled = true;
    };
  }, [drawRadiusAreas, isManualAreaMode, mapStatus]);

  useEffect(() => {
    if (
      mapStatus !== 'ready' ||
      !isManualAreaMode ||
      !googleMapRef.current
    ) return;

    let isCancelled = false;

    loadGoogleMapsLibraries()
      .then(({ Circle, maps }) => {
        if (isCancelled || !Circle || !maps) return;

        if (drawRadiusPoints.length !== 2) {
          if (drawRadiusPreviewCircleRef.current) {
            drawRadiusPreviewCircleRef.current.setMap(null);
            drawRadiusPreviewCircleRef.current = null;
          }
          return;
        }

        const center = {
          lat: drawRadiusPoints[0].latitude,
          lng: drawRadiusPoints[0].longitude,
        };
        const radius = getDistanceMiles(drawRadiusPoints[0], drawRadiusPoints[1]) * 1609.34;

        if (!drawRadiusPreviewCircleRef.current) {
          drawRadiusPreviewCircleRef.current = new Circle({
            center,
            fillColor: drawAreaStrokeColor,
            fillOpacity: 0.16,
            map: googleMapRef.current,
            radius,
            strokeColor: drawAreaStrokeColor,
            strokeOpacity: 0.95,
            strokeWeight: 3,
          });
        } else {
          drawRadiusPreviewCircleRef.current.setCenter(center);
          drawRadiusPreviewCircleRef.current.setRadius(radius);
          drawRadiusPreviewCircleRef.current.setMap(googleMapRef.current);
        }

        fitGoogleMapToBounds(
          googleMapRef.current,
          maps,
          drawRadiusPreviewCircleRef.current.getBounds?.(),
          88,
          { onlyIfNeeded: true }
        );
      })
      .catch(() => {});

    return () => {
      isCancelled = true;
    };
  }, [drawRadiusPoints, isManualAreaMode, mapStatus]);

  useEffect(() => {
    manualAreaDeleteMarkerRefs.current.forEach(clearMapMarker);
    manualAreaDeleteMarkerRefs.current = [];

    if (
      mapStatus !== 'ready' ||
      !isManualAreaMode ||
      !googleMapRef.current ||
      manualEstimateAreas.length === 0
    ) return undefined;

    let isCancelled = false;

    loadGoogleMapsLibraries()
      .then(({ AdvancedMarkerElement, Marker, maps }) => {
        if (isCancelled) return;

        manualAreaDeleteMarkerRefs.current.forEach(clearMapMarker);

        manualAreaDeleteMarkerRefs.current = manualEstimateAreas
          .map((area, index) => {
            const storedEstimate = manualAreaEstimateByKey[area.key];
            const estimate = storedEstimate?.filterKey === selectedEstimateKey
              ? storedEstimate
              : { estimatedHomes: null, status: 'loading' };
            const estimateLabel = getManualAreaEstimateLabel(estimate);
            const areaTypeLabel = area.sourceType === 'location'
              ? 'location'
              : area.sourceType === 'circle'
                ? 'circle area'
                : 'freeform area';

            return createMapAreaDeleteMarker({
              AdvancedMarkerElement,
              Marker,
              label: estimateLabel,
              map: googleMapRef.current,
              maps,
              numberLabel: String(index + 1),
              onClick: () => removeManualArea(area),
              position: {
                lat: (area.deletePosition || area.labelPosition).latitude,
                lng: (area.deletePosition || area.labelPosition).longitude,
              },
              status: estimate.status,
              title: `Location ${index + 1}. Estimated ${contactTypeCopy.plural}: ${estimateLabel}. ${area.sourceType === 'location' ? 'Remove' : 'Delete'} ${areaTypeLabel}`,
              transform: 'translateY(42px)',
            });
          })
          .filter(Boolean);
      })
      .catch(() => {});

    return () => {
      isCancelled = true;
      manualAreaDeleteMarkerRefs.current.forEach(clearMapMarker);
      manualAreaDeleteMarkerRefs.current = [];
    };
  }, [
    isFindHomesAroundLocationsMode,
    isManualAreaMode,
    manualAreaEstimateByKey,
    manualEstimateAreas,
    mapStatus,
    removeManualArea,
    selectedFilterPayloadKey,
    contactTypeCopy,
  ]);

  useEffect(() => {
    clearMapMarker(selectedManualAreaMarkerRef.current);
    selectedManualAreaMarkerRef.current = null;

    const pinPosition = selectedManualArea?.labelPosition ||
      selectedManualArea?.deletePosition ||
      (selectedManualArea
        ? { latitude: selectedManualArea.latitude, longitude: selectedManualArea.longitude }
        : null);
    const latitude = Number(pinPosition?.latitude);
    const longitude = Number(pinPosition?.longitude);

    if (
      mapStatus !== 'ready' ||
      !isManualAreaMode ||
      !googleMapRef.current ||
      !Number.isFinite(latitude) ||
      !Number.isFinite(longitude)
    ) return undefined;

    let isCancelled = false;

    loadGoogleMapsLibraries()
      .then(({ AdvancedMarkerElement, Marker, maps }) => {
        if (isCancelled) return;

        clearMapMarker(selectedManualAreaMarkerRef.current);
        selectedManualAreaMarkerRef.current = createMapMarker({
          AdvancedMarkerElement,
          Marker,
          map: googleMapRef.current,
          maps,
          position: {
            lat: latitude,
            lng: longitude,
          },
          title: selectedManualArea?.sourceType === 'location'
            ? getDisplayAddress(selectedManualArea.address) || 'Selected location'
            : 'Selected target area',
          useAdvancedMarker: isFindHomesAroundLocationsMode,
          zIndex: 80,
        });
      })
      .catch(() => {});

    return () => {
      isCancelled = true;
      clearMapMarker(selectedManualAreaMarkerRef.current);
      selectedManualAreaMarkerRef.current = null;
    };
  }, [
    isFindHomesAroundLocationsMode,
    isManualAreaMode,
    mapStatus,
    selectedManualArea,
  ]);

  useEffect(() => {
    if (
      mapStatus !== 'ready' ||
      !isManualAreaMode ||
      isDrawingShape ||
      !googleMapRef.current ||
      !window.google?.maps
    ) return;

    const hasDrawnAreas = drawRadiusAreas.length > 0 || customShapes.length > 0;
    if (!hasDrawnAreas) return;
    if (suppressManualAreaAutoFitRef.current) {
      suppressManualAreaAutoFitRef.current = false;
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    drawRadiusAreas.forEach((area) => extendBoundsForRadiusArea(bounds, window.google.maps, area));
    customShapes.forEach((shape) => extendBoundsForShape(bounds, window.google.maps, shape));

    if (bounds.isEmpty()) return;

    window.requestAnimationFrame(() => {
      googleMapRef.current?.fitBounds(bounds, 88);
    });
  }, [customShapes, drawRadiusAreas, isDrawingShape, isManualAreaMode, mapStatus]);

  useEffect(() => {
    const restoreMapInteraction = () => {
      if (!googleMapRef.current) return;

      googleMapRef.current.setOptions({
        draggable: true,
        draggableCursor: null,
        draggingCursor: null,
        gestureHandling: 'greedy',
        scrollwheel: true,
      });
    };

    const cleanupDrawingMode = () => {
      mapDrawingListenersRef.current.forEach((listener) => listener.remove());
      mapDrawingListenersRef.current = [];
      drawRadiusCenterRef.current = null;
      drawRadiusEdgeRef.current = null;
      isDrawingRadiusRef.current = false;
      restoreMapInteraction();
    };

    cleanupDrawingMode();

    if (!isDrawingShape || !googleMapRef.current || !window.google?.maps) return;
    if (areaMode !== 'custom' && areaMode !== 'draw-radius') return;

    googleMapRef.current.setOptions({
      draggable: false,
      draggableCursor: 'crosshair',
      draggingCursor: 'crosshair',
      gestureHandling: 'greedy',
      scrollwheel: true,
    });

    const isGoogleMapControlEvent = (event) => {
      if (!(event.target instanceof Element)) return false;

      return Boolean(event.target.closest(
        'button, a, [role="button"], .gmnoprint, .gm-control-active, .gm-style-cc'
      ));
    };

    const getPointerPoint = (event) => {
      const projection = mapProjectionOverlayRef.current?.getProjection?.();
      const MapPoint = window.google?.maps?.Point;
      if (!projection || !MapPoint || !mapRef.current) return null;

      const rect = mapRef.current.getBoundingClientRect();
      const latLng = projection.fromContainerPixelToLatLng(
        new MapPoint(event.clientX - rect.left, event.clientY - rect.top)
      );

      if (!latLng) return null;

      return {
        latitude: latLng.lat(),
        longitude: latLng.lng(),
      };
    };

    const isSamePoint = (firstPoint, secondPoint) =>
      firstPoint &&
      secondPoint &&
      firstPoint.latitude === secondPoint.latitude &&
      firstPoint.longitude === secondPoint.longitude;
    const finishDrawingWithoutArea = () => {
      setDrawRadiusPoints([]);
      setCustomShapePoints([]);
      drawRadiusCenterRef.current = null;
      drawRadiusEdgeRef.current = null;
      isDrawingRadiusRef.current = false;
      setIsDrawingShape(false);
    };

    const stopRadiusDrawing = (edgePoint = drawRadiusEdgeRef.current) => {
      if (!isDrawingRadiusRef.current || !drawRadiusCenterRef.current || !edgePoint) return;
      if (isFindHomesAroundLocationsMode && selectedMapLocationCount >= maxSelectedMapLocations) {
        finishDrawingWithoutArea();
        return;
      }

      const centerPoint = drawRadiusCenterRef.current;
      const nextRadiusMiles = Number(getDistanceMiles(centerPoint, edgePoint).toFixed(3));
      if (Number.isFinite(nextRadiusMiles) && nextRadiusMiles > maximumFreeformRadiusMiles) {
        finishDrawingWithoutArea();
        dispatch(
          failure(
            `Circle radius cannot exceed ${maximumFreeformRadiusMiles} miles. Draw a smaller circle or pick a closer center.`
          )
        );
        return;
      }
      const safeRadiusMiles = Math.max(nextRadiusMiles, minimumRadiusMiles);
      const nextArea = {
        center: centerPoint,
        edge: edgePoint,
        radiusMiles: safeRadiusMiles,
      };

      suppressManualAreaAutoFitRef.current = true;
      setDrawRadiusAreas((currentAreas) => [...currentAreas, nextArea]);
      setManualAreaOrder((currentOrder) =>
        appendManualAreaOrderEntry(currentOrder, 'circle', getDrawRadiusAreaOrderKey(nextArea))
      );
      setDrawRadiusPoints([]);
      setRadiusMiles(safeRadiusMiles);
      setCustomRadiusMiles(safeRadiusMiles);
      drawRadiusCenterRef.current = null;
      drawRadiusEdgeRef.current = null;
      isDrawingRadiusRef.current = false;
      setIsDrawingShape(false);
    };

    if (areaMode === 'draw-radius') {
      const handlePointerDown = (event) => {
        if (event.button !== 0) return;
        if (isGoogleMapControlEvent(event)) return;

        const nextPoint = getPointerPoint(event);
        if (!nextPoint) return;

        event.preventDefault();
        event.stopPropagation();
        mapRef.current?.setPointerCapture?.(event.pointerId);
        drawRadiusCenterRef.current = nextPoint;
        drawRadiusEdgeRef.current = nextPoint;
        isDrawingRadiusRef.current = true;
        setDrawRadiusPoints([nextPoint, nextPoint]);
      };

      const handlePointerMove = (event) => {
        if (!isDrawingRadiusRef.current || !drawRadiusCenterRef.current) return;

        const edgePoint = getPointerPoint(event);
        if (!edgePoint) return;

        event.preventDefault();
        event.stopPropagation();
        drawRadiusEdgeRef.current = edgePoint;

        if (event.buttons === 0) {
          stopRadiusDrawing(edgePoint);
          return;
        }

        setDrawRadiusPoints([drawRadiusCenterRef.current, edgePoint]);
      };

      const handlePointerUp = (event) => {
        if (!isDrawingRadiusRef.current) return;

        const edgePoint = getPointerPoint(event) || drawRadiusEdgeRef.current;
        event.preventDefault();
        event.stopPropagation();
        mapRef.current?.releasePointerCapture?.(event.pointerId);
        stopRadiusDrawing(edgePoint);
      };

      const fallbackEndDrawing = () => stopRadiusDrawing();
      mapRef.current.addEventListener('pointerdown', handlePointerDown, true);
      mapRef.current.addEventListener('pointermove', handlePointerMove, true);
      mapRef.current.addEventListener('pointerup', handlePointerUp, true);
      mapRef.current.addEventListener('pointercancel', handlePointerUp, true);
      mapRef.current.addEventListener('lostpointercapture', fallbackEndDrawing, true);
      window.addEventListener('pointerup', fallbackEndDrawing);
      window.addEventListener('blur', fallbackEndDrawing);

      return () => {
        mapRef.current?.removeEventListener('pointerdown', handlePointerDown, true);
        mapRef.current?.removeEventListener('pointermove', handlePointerMove, true);
        mapRef.current?.removeEventListener('pointerup', handlePointerUp, true);
        mapRef.current?.removeEventListener('pointercancel', handlePointerUp, true);
        mapRef.current?.removeEventListener('lostpointercapture', fallbackEndDrawing, true);
        window.removeEventListener('pointerup', fallbackEndDrawing);
        window.removeEventListener('blur', fallbackEndDrawing);
        cleanupDrawingMode();
      };
    }

    let isDrawingFreeform = false;
    let freeformDraftPoints = [];

    const updateFreeformPreview = () => {
      const previewPoints = simplifyShapePoints(freeformDraftPoints);
      setCustomShapePoints(previewPoints);
    };

    const appendFreeformPoint = (point, force = false) => {
      if (!point) return;

      const lastPoint = freeformDraftPoints[freeformDraftPoints.length - 1];
      if (!force && lastPoint && getDistanceMiles(lastPoint, point) < freeformPointMinDistanceMiles) return;

      if (force && isSamePoint(lastPoint, point)) return;

      freeformDraftPoints = [...freeformDraftPoints, point];
      updateFreeformPreview();
    };

    const stopFreeformDrawing = (point = null) => {
      if (!isDrawingFreeform) return;

      appendFreeformPoint(point, Boolean(point));
      const completedShape = simplifyShapePoints(freeformDraftPoints);

      if (
        completedShape.length >= 3 &&
        (!isFindHomesAroundLocationsMode || selectedMapLocationCount < maxSelectedMapLocations)
      ) {
        suppressManualAreaAutoFitRef.current = true;
        setCustomShapes((currentShapes) => [...currentShapes, completedShape]);
        setManualAreaOrder((currentOrder) =>
          appendManualAreaOrderEntry(currentOrder, 'custom', getCustomShapeOrderKey(completedShape))
        );
      }

      freeformDraftPoints = [];
      isDrawingFreeform = false;
      setCustomShapePoints([]);
      setIsDrawingShape(false);
    };

    const handleFreeformPointerDown = (event) => {
      if (event.button !== 0) return;
      if (isGoogleMapControlEvent(event)) return;

      const nextPoint = getPointerPoint(event);
      if (!nextPoint) return;

      event.preventDefault();
      event.stopPropagation();
      mapRef.current?.setPointerCapture?.(event.pointerId);
      isDrawingFreeform = true;
      freeformDraftPoints = [nextPoint];
      updateFreeformPreview();
    };

    const handleFreeformPointerMove = (event) => {
      if (!isDrawingFreeform) return;

      const nextPoint = getPointerPoint(event);
      if (!nextPoint) return;

      event.preventDefault();
      event.stopPropagation();
      appendFreeformPoint(nextPoint);
    };

    const handleFreeformPointerUp = (event) => {
      if (!isDrawingFreeform) return;

      const nextPoint = getPointerPoint(event);
      event.preventDefault();
      event.stopPropagation();
      mapRef.current?.releasePointerCapture?.(event.pointerId);
      stopFreeformDrawing(nextPoint);
    };

    const fallbackEndFreeformDrawing = () => stopFreeformDrawing();
    mapRef.current.addEventListener('pointerdown', handleFreeformPointerDown, true);
    mapRef.current.addEventListener('pointermove', handleFreeformPointerMove, true);
    mapRef.current.addEventListener('pointerup', handleFreeformPointerUp, true);
    mapRef.current.addEventListener('pointercancel', handleFreeformPointerUp, true);
    window.addEventListener('pointermove', handleFreeformPointerMove, true);
    window.addEventListener('pointercancel', fallbackEndFreeformDrawing, true);
    window.addEventListener('pointerup', fallbackEndFreeformDrawing);
    window.addEventListener('blur', fallbackEndFreeformDrawing);

    return () => {
      mapRef.current?.removeEventListener('pointerdown', handleFreeformPointerDown, true);
      mapRef.current?.removeEventListener('pointermove', handleFreeformPointerMove, true);
      mapRef.current?.removeEventListener('pointerup', handleFreeformPointerUp, true);
      mapRef.current?.removeEventListener('pointercancel', handleFreeformPointerUp, true);
      window.removeEventListener('pointermove', handleFreeformPointerMove, true);
      window.removeEventListener('pointercancel', fallbackEndFreeformDrawing, true);
      window.removeEventListener('pointerup', fallbackEndFreeformDrawing);
      window.removeEventListener('blur', fallbackEndFreeformDrawing);
      cleanupDrawingMode();
    };
  }, [areaMode, isDrawingShape, isFindHomesAroundLocationsMode, mapStatus, selectedMapLocationCount]);

  const applyFilterDraftToSelected = useCallback(() => {
    if (hasInvalidRangeFilters(filterDraft, activeContactType)) return;
    if (filterDraftPayloadKey === selectedFilterPayloadKey) return;

    setSelectedFilters(rememberFiltersForContactType(activeContactType, filterDraft));
  }, [
    activeContactType,
    filterDraft,
    filterDraftPayloadKey,
    rememberFiltersForContactType,
    selectedFilterPayloadKey,
  ]);

  const cancelPendingFilters = useCallback(() => {
    const emptyFilters = createEmptyFilterState(activeContactType);

    setFilterDraft(emptyFilters);
    setSelectedFilters(rememberFiltersForContactType(activeContactType, emptyFilters));
  }, [activeContactType, rememberFiltersForContactType]);

  const closeFiltersPanel = useCallback(() => {
    if (hasInvalidRangeFilters(filterDraft, activeContactType)) return;

    applyFilterDraftToSelected();
    setShowFilters(false);
  }, [activeContactType, applyFilterDraftToSelected, filterDraft]);

  const toggleFiltersPanel = () => {
    if (showFilters) {
      closeFiltersPanel();
      return;
    }

    setFilterDraft(normalizeFindContactFilters(selectedFilters, activeContactType));
    setShowFilters(true);
  };

  const selectFilter = () => {
    if (!showFilters) {
      setFilterDraft(normalizeFindContactFilters(selectedFilters, activeContactType));
      setShowFilters(true);
    }
  };

  const setFilterRangeValue = (key, field, value) => {
    const definition = activeDataAxleFilterByKey[key];
    if (!definition || !['min', 'max'].includes(field)) return;

    setFilterDraft((currentFilters) => {
      const nextFilters = normalizeFindContactFilters(currentFilters, activeContactType);
      nextFilters[key] = {
        ...nextFilters[key],
        [field]: value,
      };
      return nextFilters;
    });
  };

  const setFilterBooleanValue = (key, enabled) => {
    if (!activeDataAxleFilterByKey[key]) return;

    setFilterDraft((currentFilters) => {
      const nextFilters = normalizeFindContactFilters(currentFilters, activeContactType);
      nextFilters[key] = { enabled };
      return nextFilters;
    });
  };

  const setFilterSingleValue = (key, value) => {
    const definition = activeDataAxleFilterByKey[key];
    if (!definition) return;

    setFilterDraft((currentFilters) => {
      const nextFilters = normalizeFindContactFilters(currentFilters, activeContactType);
      nextFilters[key] = { value: normalizeFilterOptionValue(definition, value) };
      return nextFilters;
    });
  };

  const setFilterMultiValues = (key, values) => {
    const definition = activeDataAxleFilterByKey[key];
    if (!definition) return;

    setFilterDraft((currentFilters) => {
      const nextFilters = normalizeFindContactFilters(currentFilters, activeContactType);
      nextFilters[key] = {
        values: normalizeFilterMultiValues(definition, values),
      };
      return nextFilters;
    });
  };

  const clearAllAddressEstimateState = useCallback(() => {
    radiusEstimateRunRef.current += 1;
    multipleAddressEstimateRunRef.current += 1;
    addressAggregateEstimateRunRef.current += 1;
    manualAreaEstimateRunRef.current += 1;
    cancelActiveBulkAddressEstimate();
    cancelActiveBulkManualAreaEstimate();
    if (radiusEstimateDebounceRef.current) {
      window.clearTimeout(radiusEstimateDebounceRef.current);
      radiusEstimateDebounceRef.current = null;
    }
    if (customRadiusCommitDebounceRef.current) {
      window.clearTimeout(customRadiusCommitDebounceRef.current);
      customRadiusCommitDebounceRef.current = null;
    }
    radiusEstimateAbortControllerRef.current?.abort?.();
    radiusEstimateAbortControllerRef.current = null;
    if (multipleAddressEstimateDebounceRef.current) {
      window.clearTimeout(multipleAddressEstimateDebounceRef.current);
      multipleAddressEstimateDebounceRef.current = null;
    }
    multipleAddressEstimateAbortControllerRef.current?.abort?.();
    multipleAddressEstimateAbortControllerRef.current = null;
    if (addressAggregateEstimateDebounceRef.current) {
      window.clearTimeout(addressAggregateEstimateDebounceRef.current);
      addressAggregateEstimateDebounceRef.current = null;
    }
    addressAggregateEstimateAbortControllerRef.current?.abort?.();
    addressAggregateEstimateAbortControllerRef.current = null;
    if (manualAreaEstimateDebounceRef.current) {
      window.clearTimeout(manualAreaEstimateDebounceRef.current);
      manualAreaEstimateDebounceRef.current = null;
    }
    manualAreaEstimateAbortControllerRef.current?.abort?.();
    manualAreaEstimateAbortControllerRef.current = null;
    pendingAddressEstimateRequestKeysRef.current.clear();
    pendingManualAreaEstimateRequestKeysRef.current.clear();
    retryingAddressEstimateKeysRef.current.clear();
    setEstimatedFilterKey('');
    setEstimatedHomes(null);
    setEstimatedRadiusMiles(null);
    setIsDataAxleConfigured(true);
    setEstimateStatus('idle');
    setEstimateError('');
    setAddressEstimateStatusByKey({});
    setAddressEstimateResultByKey({});
    setAddressConfigs((currentConfigs) => {
      let didUpdate = false;
      const nextConfigs = Object.entries(currentConfigs).reduce((configs, [addressKey, config]) => {
        const clearedConfig = clearAddressEstimateState(config);
        if (!areAddressConfigsEqual(config, clearedConfig)) didUpdate = true;

        return {
          ...configs,
          [addressKey]: clearedConfig,
        };
      }, {});

      return didUpdate ? nextConfigs : currentConfigs;
    });
  }, [cancelActiveBulkAddressEstimate, cancelActiveBulkManualAreaEstimate]);

  const clearFilterValue = (key, updateAppliedFilters = false) => {
    if (!activeDataAxleFilterByKey[key]) return;

    const clearFromFilters = (currentFilters) => {
      const nextFilters = normalizeFindContactFilters(currentFilters, activeContactType);
      nextFilters[key] = createEmptyFilterValue(activeDataAxleFilterByKey[key]);
      return nextFilters;
    };

    setFilterDraft(clearFromFilters);
    if (updateAppliedFilters) {
      setSelectedFilters((currentFilters) =>
        rememberFiltersForContactType(activeContactType, clearFromFilters(currentFilters))
      );
    }
  };

  const handleSavedFilterError = useCallback((message) => {
    dispatch(failure(message));
  }, [dispatch]);

  const handleSelectSavedFilter = useCallback((savedFilter) => {
    if (!savedFilter?.filters) return;

    const nextFilters = normalizeFindContactFilters(savedFilter.filters, activeContactType);

    setFilterDraft(nextFilters);
    setSelectedFilters(rememberFiltersForContactType(activeContactType, nextFilters));
    setActiveSavedFilter({ id: savedFilter.id, name: savedFilter.name });
    setShowFilters(true);
  }, [activeContactType, rememberFiltersForContactType]);

  const handleSavedFilterDeleted = useCallback((savedFilter) => {
    if (activeSavedFilter?.id === savedFilter?.id) {
      setActiveSavedFilter(null);
    }
    dispatch(success('Filter Deleted Successfully'));
  }, [activeSavedFilter?.id, dispatch]);

  const handleClearAllFilterDraft = useCallback(() => {
    setFilterDraft(createEmptyFilterState(activeContactType));
    setActiveSavedFilter(null);
  }, [activeContactType]);

  const handleOpenSaveFilterModal = useCallback(() => {
    if (hasInvalidRangeFilters(filterDraft, activeContactType)) return;
    setSaveFilterModalOpen(true);
  }, [activeContactType, filterDraft]);

  const handleCloseSaveFilterModal = useCallback(() => {
    if (saveFilterLoading) return;
    setSaveFilterModalOpen(false);
  }, [saveFilterLoading]);

  const handleSaveFilterSubmit = useCallback(async ({ name }) => {
    const trimmedName = String(name || '').trim();
    if (!trimmedName) return;

    setSaveFilterLoading(true);
    try {
      const payload = {
        module: SAVED_FILTER_MODULES.FIND_NEW_CONTACTS,
        subModule: activeContactType,
        name: trimmedName,
        filters: normalizeFindContactFilters(filterDraft, activeContactType),
      };

      const response = await createSavedFilter(payload);

      const savedFilter = response?.data?.data;
      setActiveSavedFilter({
        id: savedFilter?.id || null,
        name: savedFilter?.name || trimmedName,
      });
      setSaveFilterModalOpen(false);
      dispatch(success(response?.data?.message || 'Filter Saved Successfully'));
    } catch (error) {
      dispatch(failure(error?.response?.data?.message || 'Could not save filter.'));
    } finally {
      setSaveFilterLoading(false);
    }
  }, [activeContactType, dispatch, filterDraft]);

  const handleNearbyTargetModeSelect = (nextMode, allowDeselect = false) => {
    if (!canUseTargetingMethods) return;

    const normalizedNextMode = nextMode === 'radius' ? 'radius' : 'homes';
    const isDeselectingMode = allowDeselect && areaMode === 'radius' && nearbyTargetMode === normalizedNextMode;
    const nextNearbyTargetMode = isDeselectingMode ? defaultNearbyTargetMode : normalizedNextMode;
    if (nextNearbyTargetMode !== 'radius' && customRadiusCommitDebounceRef.current) {
      window.clearTimeout(customRadiusCommitDebounceRef.current);
      customRadiusCommitDebounceRef.current = null;
    }
    const nextModeSettings = updateNearbyModeSettings(nearbyModeSettings, {
      customRadiusMiles,
      customTargetCount,
      nearbyTargetMode,
      radiusMiles,
      targetCount,
    });

    setAreaMode('radius');
    setNearbyModeSettings(nextModeSettings);
    setNearbyTargetMode(nextNearbyTargetMode);
    if (nextNearbyTargetMode === 'homes' && isFindHomesAroundLocationsMode) {
      clearAreaOverlays();
    }
    if (nearbyTargetMode !== nextNearbyTargetMode || isDeselectingMode) {
      clearAllAddressEstimateState();
      radiusEstimateRunRef.current += 1;
      multipleAddressEstimateRunRef.current += 1;
      if (nextNearbyTargetMode === 'homes') {
        setTargetCount('');
        setCustomTargetCount('');
        setRadiusMiles('');
        setCustomRadiusMiles('');
      }
      if (nextNearbyTargetMode === 'radius') {
        setRadiusMiles(nextModeSettings.radius.radiusMiles || '');
        setCustomRadiusMiles(nextModeSettings.radius.customRadiusMiles || '');
        setTargetCount('');
        setCustomTargetCount('');
      }
      if (!nextNearbyTargetMode) {
        setRadiusMiles('');
        setCustomRadiusMiles('');
        setTargetCount('');
        setCustomTargetCount('');
      }
      setEstimatedHomes(null);
      setEstimatedFilterKey('');
      setEstimatedRadiusMiles(null);
      setEstimateStatus('idle');
    }
    setIsDrawingShape(false);
    setDrawRadiusPoints([]);
    setCustomShapePoints([]);
    drawRadiusCircleRefs.current.forEach((circle) => circle.setMap(null));
    drawRadiusCircleRefs.current = [];
    if (drawRadiusPreviewCircleRef.current) {
      drawRadiusPreviewCircleRef.current.setMap(null);
      drawRadiusPreviewCircleRef.current = null;
    }
    setManualAreaEstimateByKey({});
    customPolygonRefs.current.forEach((polygon) => polygon.setMap(null));
    customPolygonRefs.current = [];
    if (customPolylineRef.current) customPolylineRef.current.setMap(null);
    customPointMarkerRefs.current.forEach(clearMapMarker);
    radiusPointMarkerRefs.current.forEach(clearMapMarker);
    customPointMarkerRefs.current = [];
    radiusPointMarkerRefs.current = [];
  };

  const cancelDrawAreaMode = useCallback(() => {
    suppressManualAreaAutoFitRef.current = true;
    setIsDrawingShape(false);
    setDrawRadiusPoints([]);
    setCustomShapePoints([]);
    drawRadiusCenterRef.current = null;
    drawRadiusEdgeRef.current = null;
    isDrawingRadiusRef.current = false;
    if (customPolylineRef.current) customPolylineRef.current.setMap(null);
  }, []);

  const handleDrawAreaModeSelect = () => {
    if (!isFindHomesAroundLocationsMode || !canUseTargetingMethods) return;

    if (customRadiusCommitDebounceRef.current) {
      window.clearTimeout(customRadiusCommitDebounceRef.current);
      customRadiusCommitDebounceRef.current = null;
    }

    if (isManualAreaMode) {
      setAreaMode('radius');
      setNearbyTargetMode(defaultNearbyTargetMode);
      setIsDrawingShape(false);
      return;
    }

    setAreaMode('draw-area');
    setNearbyTargetMode(defaultNearbyTargetMode);
    setIsDrawingShape(false);
  };

  const handlePresetRadius = (radius) => {
    if (!canUseTargetingMethods) return;

    handleNearbyTargetModeSelect('homes');
    setRadiusMiles(radius);
    setCustomRadiusMiles('');
  };

  const handleNearbyRadiusSelect = (radius) => {
    if (!canUseTargetingMethods) return;

    if (customRadiusCommitDebounceRef.current) {
      window.clearTimeout(customRadiusCommitDebounceRef.current);
      customRadiusCommitDebounceRef.current = null;
    }
    handleNearbyTargetModeSelect('radius');
    const isSelectedPreset = customRadiusMiles === '' && Number(radiusMiles) === radius;

    if (isSelectedPreset) {
      clearAllAddressEstimateState();
      setRadiusMiles('');
      setCustomRadiusMiles('');
      setNearbyModeSettings((currentSettings) =>
        updateNearbyModeSettings(currentSettings, {
          customRadiusMiles: '',
          nearbyTargetMode: 'radius',
          radiusMiles: '',
        })
      );
      setEstimatedHomes(null);
      setEstimatedRadiusMiles(null);
      setEstimateStatus('idle');
      return;
    }

    if (normalizeRadiusMiles(radiusMiles) !== normalizeRadiusMiles(radius)) {
      clearAllAddressEstimateState();
      radiusEstimateRunRef.current += 1;
      multipleAddressEstimateRunRef.current += 1;
    }
    setRadiusMiles(radius);
    setCustomRadiusMiles('');
    setNearbyModeSettings((currentSettings) =>
      updateNearbyModeSettings(currentSettings, {
        customRadiusMiles: '',
        nearbyTargetMode: 'radius',
        radiusMiles: radius,
      })
    );
    setEstimatedHomes(null);
    setEstimatedRadiusMiles(null);
    setEstimateStatus('idle');
  };

  const clearPendingCustomRadiusCommit = () => {
    if (!customRadiusCommitDebounceRef.current) return;
    window.clearTimeout(customRadiusCommitDebounceRef.current);
    customRadiusCommitDebounceRef.current = null;
  };

  const applyCustomRadiusValue = (inputValue) => {
    if (!canUseTargetingMethods) return;

    const nextRadius = Number(inputValue);
    if (!Number.isFinite(nextRadius) || nextRadius <= 0) {
      clearAllAddressEstimateState();
      setCustomRadiusMiles('');
      setRadiusMiles('');
      setNearbyModeSettings((currentSettings) =>
        updateNearbyModeSettings(currentSettings, {
          customRadiusMiles: '',
          nearbyTargetMode: 'radius',
          radiusMiles: '',
        })
      );
      setEstimatedHomes(null);
      setEstimatedRadiusMiles(null);
      setEstimateStatus('idle');
      return;
    }

    const safeRadius = normalizeRadiusMiles(nextRadius);
    if (normalizeRadiusMiles(radiusMiles) !== safeRadius) {
      clearAllAddressEstimateState();
      radiusEstimateRunRef.current += 1;
      multipleAddressEstimateRunRef.current += 1;
    }
    setCustomRadiusMiles(nextRadius > maximumRadiusMiles ? String(safeRadius) : inputValue);
    setRadiusMiles(safeRadius);
    setNearbyModeSettings((currentSettings) =>
      updateNearbyModeSettings(currentSettings, {
        customRadiusMiles: nextRadius > maximumRadiusMiles ? String(safeRadius) : inputValue,
        nearbyTargetMode: 'radius',
        radiusMiles: safeRadius,
      })
    );
  };

  const handleCustomRadiusChange = (event) => {
    if (!canUseTargetingMethods) return;

    const inputValue = event.target.value;
    clearPendingCustomRadiusCommit();
    handleNearbyTargetModeSelect('radius');

    if (inputValue === '') {
      setCustomRadiusMiles('');
      applyCustomRadiusValue(inputValue);
      return;
    }

    setCustomRadiusMiles(inputValue);

    const nextRadius = Number(inputValue);
    if (!Number.isFinite(nextRadius) || nextRadius <= 0) return;

    customRadiusCommitDebounceRef.current = window.setTimeout(() => {
      customRadiusCommitDebounceRef.current = null;
      applyCustomRadiusValue(inputValue);
    }, customRadiusCommitDebounceMs);
  };

  const commitCustomRadius = () => {
    clearPendingCustomRadiusCommit();
    applyCustomRadiusValue(customRadiusMiles);
  };

  const handleCustomRadiusKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    commitCustomRadius();
  };

  const selectDrawAreaType = (nextType) => {
    if (nextType === 'freeform') {
      setAreaMode('custom');
      setDrawRadiusPoints([]);
      setIsDrawingShape(false);
      isDrawingRadiusRef.current = false;
      return;
    }

    setAreaMode('draw-radius');
    setCustomShapePoints([]);
    setIsDrawingShape(false);
  };

  const startDrawRadius = () => {
    setAreaMode('draw-radius');
    setIsDrawingShape(true);
    setDrawRadiusPoints([]);
    if (!isFindHomesAroundLocationsMode) {
      clearBoundaryFeatureLayer();
      if (addressBoundaryRef.current) {
        addressBoundaryRef.current.setMap(null);
        addressBoundaryRef.current = null;
      }
    }
    if (radiusCircleRef.current) {
      radiusCircleRef.current.setMap(null);
      radiusCircleRef.current = null;
    }
    if (customPolylineRef.current) customPolylineRef.current.setMap(null);
  };

  const startCustomShape = () => {
    setAreaMode('custom');
    setIsDrawingShape(true);
    setCustomShapePoints([]);
    setDrawRadiusPoints([]);
    if (!isFindHomesAroundLocationsMode) {
      clearBoundaryFeatureLayer();
      if (addressBoundaryRef.current) {
        addressBoundaryRef.current.setMap(null);
        addressBoundaryRef.current = null;
      }
    }
    if (radiusCircleRef.current) {
      radiusCircleRef.current.setMap(null);
      radiusCircleRef.current = null;
    }
    radiusPointMarkerRefs.current.forEach(clearMapMarker);
    radiusPointMarkerRefs.current = [];
  };

  const handleDrawToolSelect = (nextDrawAreaType) => {
    if (!isFindHomesAroundLocationsMode) return;

    const isSameDrawTool =
      (nextDrawAreaType === 'freeform' && areaMode === 'custom') ||
      (nextDrawAreaType === 'radius' && areaMode === 'draw-radius');

    if (isSameDrawTool && isDrawingShape) {
      cancelDrawAreaMode();
      return;
    }

    selectDrawAreaType(nextDrawAreaType);
    if (nextDrawAreaType === 'freeform') {
      startCustomShape();
      return;
    }

    startDrawRadius();
  };

  const handleMapTypeSelect = (nextMapTypeId) => {
    if (!['roadmap', 'satellite'].includes(nextMapTypeId)) return;

    mapTypeIdRef.current = nextMapTypeId;
    setMapTypeId(nextMapTypeId);
    googleMapRef.current?.setMapTypeId(nextMapTypeId);
  };

  useEffect(() => {
    if (!isManualAreaMode || !isDrawingShape) return undefined;

    const handleEscapeDrawMode = (event) => {
      if (event.key === 'Escape') cancelDrawAreaMode();
    };

    const handleOutsideMapClick = (event) => {
      if (!mapPreviewRef.current || mapPreviewRef.current.contains(event.target)) return;
      cancelDrawAreaMode();
    };

    document.addEventListener('keydown', handleEscapeDrawMode);
    document.addEventListener('pointerdown', handleOutsideMapClick, true);

    return () => {
      document.removeEventListener('keydown', handleEscapeDrawMode);
      document.removeEventListener('pointerdown', handleOutsideMapClick, true);
    };
  }, [cancelDrawAreaMode, isDrawingShape, isManualAreaMode]);

  const isPresetNearbyRadius = nearbyRadiusOptions.includes(Number(radiusMiles));
  const customRadiusInputValue = customRadiusMiles !== ''
    ? customRadiusMiles
    : isPresetNearbyRadius
      ? ''
      : radiusMiles;
  const isCustomTargetCountSelected = customTargetCount !== '';
  const isCustomRadiusSelected = customRadiusInputValue !== '';
  const canUseDrawAreaModes = isFindHomesAroundLocationsMode;
  const displayedAreaMode = !canUseDrawAreaModes ? 'radius' : areaMode;
  const isDrawRadiusToolActive = isDrawingShape && areaMode === 'draw-radius';
  const isFreeformToolActive = isDrawingShape && areaMode === 'custom';
  const displayedNearbyTargetMode = displayedAreaMode === 'radius'
    ? nearbyTargetMode
    : defaultNearbyTargetMode;
  const shouldShowListCleanupOptions = isFindHomesAroundAddressesMode || startingAddressMode === 'multiple';
  const handleExcludeTargetAddressesChange = (event) => {
    const excludeTargetAddressesFromMailingList = event.target.checked;

    clearAllAddressEstimateState();
    manualAggregateEstimateRunRef.current += 1;
    if (manualAggregateEstimateRequestRef.current.timeoutId) {
      window.clearTimeout(manualAggregateEstimateRequestRef.current.timeoutId);
    }
    manualAggregateEstimateRequestRef.current = { key: '', promise: null, timeoutId: null };
    manualAggregateEstimateResolvedKeyRef.current = '';
    retryingManualAreaEstimateKeysRef.current.clear();
    setAddressAggregateEstimatedHomes(null);
    setAddressAggregateEstimateStatus('idle');
    setManualAreaEstimateByKey({});
    manualAreaEstimateByKeyRef.current = {};
    setListCleanupOptions((currentOptions) => ({
      ...normalizeListCleanupOptions(currentOptions),
      excludeTargetAddressesFromMailingList,
    }));
  };
  const listCleanupOptionsPanel = shouldShowListCleanupOptions ? (
    <Box className="listCleanupOptions">
      {isFindHomesAroundAddressesMode && (
      <label className="listCleanupOption">
        <Box className="checkboxRow listCleanupCheckboxRow">
          <Checkbox
            checked={listCleanupOptions.excludeTargetAddressesFromMailingList}
            checkedIcon={<DoneIcon />}
            className="bulkCheck newbulkCheck checkBoxSm listCleanupMuiCheckbox"
            disableRipple
            icon={<Box className="bulkNotchecked" />}
            onChange={handleExcludeTargetAddressesChange}
          />
        </Box>
        <span className="listCleanupText">Exclude the addresses above from the mailing list</span>
      </label>
      )}
    </Box>
  ) : null;
  const mapTargetPanel = (
    <MapTargetPanel
      canUseDrawAreaModes={canUseDrawAreaModes}
      canUseTargetingMethods={canUseTargetingMethods}
      contactTypeCopy={contactTypeCopy}
      commitCustomRadius={commitCustomRadius}
      commitCustomTargetCount={commitCustomTargetCount}
      customRadiusInputValue={customRadiusInputValue}
      customTargetCount={customTargetCount}
      displayedAreaMode={displayedAreaMode}
      displayedNearbyTargetMode={displayedNearbyTargetMode}
      handleCustomRadiusChange={handleCustomRadiusChange}
      handleCustomRadiusKeyDown={handleCustomRadiusKeyDown}
      handleCustomTargetCountChange={handleCustomTargetCountChange}
      handleCustomTargetCountKeyDown={handleCustomTargetCountKeyDown}
      handleDrawAreaModeSelect={handleDrawAreaModeSelect}
      handleNearbyRadiusSelect={handleNearbyRadiusSelect}
      handleNearbyTargetModeSelect={handleNearbyTargetModeSelect}
      handleTargetCountSelect={handleTargetCountSelect}
      isCustomRadiusSelected={isCustomRadiusSelected}
      isCustomTargetCountSelected={isCustomTargetCountSelected}
      isManualAreaModeValue={isManualAreaModeValue}
      isRadiusEstimatePending={isRadiusEstimatePending}
      maximumRadiusMiles={maximumRadiusMiles}
      maximumTargetCount={maximumTargetCount}
      minimumRadiusMiles={minimumRadiusMiles}
      minimumTargetCount={minimumTargetCount}
      nearbyRadiusOptions={nearbyRadiusOptions}
      radiusMiles={radiusMiles}
      targetCount={targetCount}
      targetCountOptions={targetCountOptions}
    />
  );

  const purchaseModalQuantity = getPurchasableHomesCount();
  const purchaseModalTotalCost = purchaseModalQuantity * costPerRecord;
  const canPurchaseList =
    canContinueWithAddressEstimates &&
    purchaseModalQuantity > 0 &&
    !hasUnappliedFilterChanges;
  const purchaseConfirmModalConfig = {
    ...purchaseConfirmModal,
    message: purchaseConfirmModal.open ? (
      <Box className="purchaseConfirmContent">
        <Typography>
          You selected <strong>{formatNumber(purchaseModalQuantity)} records</strong> for{' '}
          <strong>{formatCurrency(purchaseModalTotalCost)}</strong>. We'll deduct the cost from your Wallet balance. If your balance is insufficient, the remaining amount will be charged to your saved payment method.
        </Typography>
        <Box className="purchaseListNameField">
          <InputLabel className="purchaseListNameLabel" htmlFor="purchase-list-name">
            Mailing list name
          </InputLabel>
          <Input
            autoFocus
            className="purchaseListNameInput"
            fullWidth
            id="purchase-list-name"
            inputProps={{ maxLength: 35 }}
            onChange={(event) => setPurchaseListName(event.target.value)}
            value={purchaseListName}
          />
          <Typography className="purchaseListNameHelper" component="span">
            This name is also applied as a tag to your contacts.
          </Typography>
        </Box>
        <Box className="purchaseTermsAcknowledgement">
          <Checkbox
            checked={purchaseTermsAccepted}
            checkedIcon={<DoneIcon />}
            className="purchaseTermsCheckbox"
            disableRipple
            icon={<Box className="bulkNotchecked" />}
            inputProps={{ 'aria-label': 'Agree to the End User Agreement' }}
            onChange={(event) => setPurchaseTermsAccepted(event.target.checked)}
          />
          <Typography className="purchaseTermsText" component="span">
            I have read and agree to the{' '}
            {endUserAgreementUrl ? (
              <a
                href={endUserAgreementUrl}
                onClick={(event) => event.stopPropagation()}
                rel="noreferrer"
                target="_blank"
              >
                End User Agreement
              </a>
            ) : (
              <span className="purchaseTermsLink">End User Agreement</span>
            )}
            , and I will use this list only for the permitted purposes described in it.
          </Typography>
        </Box>
      </Box>
    ) : purchaseConfirmModal.message,
    handleOk: () => confirmPurchase(pendingPurchaseAction),
    successButtonDisabled: purchaseConfirmModal.open && (!purchaseListName.trim() || !purchaseTermsAccepted),
  };

  return (
    <Box className={isFindHomesAroundLocationsMode ? 'findNewContactsScreen locationMode' : 'findNewContactsScreen'}>
      <Box className="findNewContactsCard">
        <Box className="findNewContactsHeader">
          <Box className="findNewContactsHeadingCopy">
            <Typography className="pageTitle">{pageHeaderTitle}</Typography>
            <Typography className="pageDescription">{pageHeaderDescription}</Typography>
          </Box>
          <Box className="contactTypeActions" aria-label="Contact type">
            <button
              aria-pressed={activeContactType === 'consumers'}
              className={activeContactType === 'consumers' ? 'contactTypeAction selected' : 'contactTypeAction'}
              onClick={() => setActiveContactType('consumers')}
              type="button"
            >
              <HomeOutlinedIcon />
              <span>Consumers</span>
            </button>
            <button
              aria-pressed={activeContactType === 'businesses'}
              className={activeContactType === 'businesses' ? 'contactTypeAction selected' : 'contactTypeAction'}
              onClick={() => setActiveContactType('businesses')}
              type="button"
            >
              <BusinessOutlinedIcon />
              <span>Businesses</span>
            </button>
          </Box>
        </Box>
        <Box className="findNewContactsGrid">
          <Box className="mailingListMain">
            <Box className="setupCard mailingBuilderCard">
              <AddressEntryPanel
                address={address}
                addressContactsPanelMessage={addressContactsPanelMessage}
                addressEntrySource={addressEntrySource}
                addressInputLabel={addressInputLabel}
                addressInputPlaceholder={addressInputPlaceholder}
                addressInputRef={addressInputRef}
                addressUploadDropzone={addressUploadDropzone}
                addressUploadMessage={addressUploadMessage}
                contactOptions={contactOptions}
                contactSearchFocusRequest={contactSearchFocusRequest}
                contactsLoading={contactsLoading}
                downloadSampleAddressUploadCsv={downloadSampleAddressUploadCsv}
                formatNumber={formatNumber}
                handleAddContactAddresses={handleAddContactAddresses}
                handleAddressKeyDown={handleAddressKeyDown}
                handleAddressEntrySourceSelect={handleAddressEntrySourceSelect}
                hasContactSourceSelection={hasContactSourceSelection}
                hasReachedMaxAddressEntries={hasReachedMaxAddressEntries}
                hasReachedMaxSelectedAddresses={hasReachedMaxSelectedAddresses}
                isAddingContactAddresses={isAddingContactAddresses}
                isAddressInputSpotlighted={isAddressInputSpotlighted}
                isFindHomesAroundAddressesMode={isFindHomesAroundAddressesMode}
                isProcessingAddressUpload={isProcessingAddressUpload}
                maxSelectedItemsPlaceholder={addressEntryLimitMessage}
                selectedContactIds={selectedContactIds}
                selectedTagContactsCount={selectedTagContactsCount}
                selectedTagIds={selectedTagIds}
                setAddress={setAddress}
                setContactAddressMessage={setContactAddressMessage}
                setContactSearch={setContactSearch}
                setSelectedContactIds={setSelectedContactIds}
                setSelectedTagIds={setSelectedTagIds}
                setTagSearch={setTagSearch}
                tagOptions={tagOptions}
                tagsLoading={tagsLoading}
                uploadedAddressCount={uploadedAddressCount}
                uploadedAddressFileName={uploadedAddressFileName}
              />
              <Box className="addressTargetDivider" />

            {!isFindHomesAroundLocationsMode && mapTargetPanel}

            {!isFindHomesAroundLocationsMode && <Box className="addressTargetDivider refineTargetDivider" />}

            <FiltersPanel
              activeSavedFilterId={activeSavedFilter?.id || null}
              clearFilterValue={clearFilterValue}
              contactTypeCopy={contactTypeCopy}
              filterDefinitions={activeFilterDefinitions}
              filterDraftCount={filterDraftCount}
              filterDraftPayload={filterDraftPayload}
              hasPendingFilterDraftChanges={hasPendingFilterDraftChanges}
              isFilterDraftInvalid={hasInvalidFilterDraft}
              invalidFilterDraftKeys={invalidFilterDraftKeys}
              lookupLoadingByKey={lookupLoadingByKey}
              lookupOptionLabelsByKey={lookupOptionLabelsByKey}
              lookupOptionsByKey={lookupDropdownOptionsByKey}
              normalizedFilterDraft={normalizedFilterDraft}
              onApplyFilters={applyFilterDraftToSelected}
              onClearAllFilters={handleClearAllFilterDraft}
              onLookupSearch={handleLookupSearch}
              onOpenSaveFilterModal={handleOpenSaveFilterModal}
              onSavedFilterDeleted={handleSavedFilterDeleted}
              onSavedFilterError={handleSavedFilterError}
              onSelectSavedFilter={handleSelectSavedFilter}
              refinePanelRef={refinePanelRef}
              refineRowRef={refineRowRef}
              savedFiltersModule={SAVED_FILTER_MODULES.FIND_NEW_CONTACTS}
              savedFiltersSubModule={activeContactType}
              selectedFilterItems={selectedFilterItems}
              selectedFilterTooltipByKey={selectedFilterTooltipByKey}
              selectFilter={selectFilter}
              setFilterBooleanValue={setFilterBooleanValue}
              setFilterMultiValues={setFilterMultiValues}
              setFilterRangeValue={setFilterRangeValue}
              setFilterSingleValue={setFilterSingleValue}
              showFilters={showFilters}
              toggleFiltersPanel={toggleFiltersPanel}
            />

            <MapPreviewPanel
              areaMode={areaMode}
              canUseAreaControls={canUseAreaControls}
              canUseDrawAreaModes={canUseDrawAreaModes}
              contactTypeCopy={contactTypeCopy}
              handleDrawToolSelect={handleDrawToolSelect}
              handleMapTypeSelect={handleMapTypeSelect}
              isDrawingShape={isDrawingShape}
              isDrawRadiusToolActive={isDrawRadiusToolActive}
              isFindHomesAroundLocationsMode={isFindHomesAroundLocationsMode}
              isFreeformToolActive={isFreeformToolActive}
              isManualAreaMode={isManualAreaMode}
              mapAddress={mapAddress}
              mapError={mapError}
              mapPreviewRef={mapPreviewRef}
              mapRef={mapRef}
              mapStatus={mapStatus}
              mapTypeId={mapTypeId}
              shouldBlockMapForManualEstimate={shouldBlockMapForManualEstimate}
            />
          </Box>
        </Box>

        <SummaryPanel
          activeAddressIndex={activeAddressIndex}
          canClearSelection={canClearSelection}
          canPurchaseList={canPurchaseList}
          contactTypeCopy={contactTypeCopy}
          costPerRecordLabel={costPerRecordLabel}
          filterDraftCount={filterDraftCount}
          focusAddressInput={handleSummaryAddAddress}
          focusManualAreaOnMap={focusManualAreaOnMap}
          getAddressSummaryRecordMeta={getAddressSummaryRecordMeta}
          getDisplayAddress={getDisplayAddress}
          handleBackToCreateNewOrder={handleBackToCreateNewOrder}
          handleSelectStartingAddress={handleSelectStartingAddress}
          inputEntityLabel={inputEntityLabel}
          isFindHomesAroundLocationsMode={isFindHomesAroundLocationsMode}
          isFilterDraftInvalid={hasInvalidFilterDraft}
          isManualAreaMode={isManualAreaMode}
          isSummaryHomesLoading={isSummaryHomesLoading}
          listAddressConfigs={listAddressConfigs}
          listCleanupOptionsPanel={listCleanupOptionsPanel}
          hasPendingFilterDraftChanges={hasUnappliedFilterChanges}
          onApplyFilters={applyFilterDraftToSelected}
          onCancelFilters={cancelPendingFilters}
          openClearListConfirm={openClearListConfirm}
          openPurchaseConfirm={openPurchaseConfirm}
          pendingFilterChangeCount={pendingFilterChangeCount}
          retryManualAreaEstimate={retryManualAreaEstimate}
          retryStartingAddressEstimate={retryStartingAddressEstimate}
          removeManualArea={removeManualArea}
          removeStartingAddress={removeStartingAddress}
          selectedManualAreaKey={selectedManualAreaKey}
          summaryEmptyAddressActionLabel={summaryEmptyAddressActionLabel}
          startingAddressCount={startingAddressCount}
          startingAddressSummaryTitle={startingAddressSummaryTitle}
          summaryAddressListRef={summaryAddressListRef}
          summaryDuplicatesText={summaryDuplicatesText}
          summaryHomesCount={summaryHomesCount}
          summaryLocationItems={summaryLocationItems}
          summaryPrimaryLabel={summaryPrimaryLabel}
          summaryPrimaryValue={summaryPrimaryValue}
          summaryRows={summaryRows}
          summaryStatValueStyle={summaryStatValueStyle}
          summaryTotalCostValue={summaryTotalCostValue}
        />
        </Box>
      </Box>
      <ConfirmModal openConfirmModal={clearConfirmModal} />
      <ConfirmModal openConfirmModal={purchaseConfirmModalConfig} />
      <SaveFilterModal
        open={saveFilterModalOpen}
        onClose={handleCloseSaveFilterModal}
        isLoading={saveFilterLoading}
        onSubmit={handleSaveFilterSubmit}
      />
    </Box>
  );
};

export default FindNewContacts;
